// Control UI English locale strings are the i18n source bundle.
import type { TranslationMap } from "../lib/types.ts";
import * as agentEn from "./en-agents.ts";

export const en: TranslationMap = {
  common: {
    health: "Health",
    ok: "OK",
    yes: "Yes",
    no: "No",
    assistant: "Assistant",
    active: "Active",
    loading: "Loading…",
    refreshing: "Refreshing…",
    offline: "Offline",
    connect: "Connect",
    connected: "Connected",
    refresh: "Refresh",
    retry: "Retry",
    staleData: "Showing stale data.",
    reload: "Reload",
    reset: "Reset",
    restore: "Restore",
    unarchive: "Unarchive",
    undo: "Undo",
    probe: "Probe",
    call: "Call",
    confirm: "Confirm",
    cancel: "Cancel",
    close: "Close",
    previous: "Previous",
    next: "Next",
    continue: "Continue",
    back: "Back",
    create: "Create",
    copy: "Copy",
    copied: "Copied!",
    copyFailed: "Copy failed",
    copyCode: "Copy code",
    resizeSplitView: "Resize split view",
    delete: "Delete",
    remove: "Remove",
    dismiss: "Dismiss",
    unselect: "Unselect",
    enabled: "Enabled",
    disabled: "Disabled",
    failed: "Failed",
    none: "none",
    na: "n/a",
    never: "never",
    justNow: "just now",
    now: "now",
    unknown: "Unknown",
    configured: "Configured",
    running: "Running",
    linked: "Linked",
    mode: "Mode",
    system: "System",
    light: "Light",
    dark: "Dark",
    baseUrl: "Base URL",
    lastStart: "Last start",
    lastProbe: "Last probe",
    lastConnect: "Last connect",
    lastMessage: "Last message",
    authAge: "Auth age",
    credential: "Credential",
    audience: "Audience",
    publicKey: "Public Key",
    probeOk: "Probe ok",
    probeFailed: "Probe failed",
    reloadConfig: "Reload Config",
    loadConfig: "Load config",
    loadApprovals: "Load approvals",
    settingsSections: "Settings sections",
    version: "Version",
    docs: "Docs",
    learnMore: "Learn more",
    colorMode: "Color mode",
    colorModeOption: "Color mode: {mode}",
    search: "Search",
    save: "Save",
    saving: "Saving…",
    saveAndPublish: "Save & Publish",
    importing: "Importing…",
    importFromRelays: "Import from Relays",
    showAdvanced: "Show Advanced",
    hideAdvanced: "Hide Advanced",
    unsavedChanges: "You have unsaved changes",
    working: "Working…",
    showQr: "Show QR",
    relink: "Relink",
    waitForScan: "Wait for scan",
    logout: "Logout",
    skipToMainContent: "Skip to main content",
  },
  optionCard: {
    recommended: "Recommended",
    skip: "Skip for now",
  },
  nativeLinkMenu: {
    label: "Link actions",
    openInline: "Open in Sidebar",
    openExternal: "Open in Default Browser",
    copy: "Copy Link",
  },
  filePreview: {
    label: "Support files",
    listLabel: "Files",
    searchPlaceholder: "Search files…",
    readOnly: "read-only",
    emptyTitle: "No files match",
    emptySubtitle: "Try another file name or content search.",
    copyFile: "Copy file",
    fileCount: "{count} files",
    filteredFileCount: "{count}/{total} files",
    noMatches: "No files match.",
    navigate: "navigate",
    kind: {
      text: "Text",
      shell: "Shell",
      file: "File",
    },
  },
  mcpApp: {
    title: "MCP App",
    unavailable: "MCP App unavailable: {error}",
    errors: {
      gatewayUnavailable: "MCP App gateway unavailable",
      mountUnavailable: "MCP App mount unavailable",
      sandboxTimedOut: "MCP App sandbox timed out",
      sandboxUnavailable: "MCP App sandbox unavailable",
      initializationTimedOut: "MCP App initialization timed out",
      requestFailed: "Request failed",
      invalidSandboxUrl: "MCP App sandbox URL is invalid",
    },
  },
  githubPreview: {
    loading: "Loading GitHub details…",
    unavailable: "GitHub preview unavailable",
    states: {
      merged: "Merged",
      draft: "Draft",
      open: "Open",
      closed: "Closed",
      notPlanned: "Not planned",
    },
    file: "{count} file",
    files: "{count} files",
    comment: "{count} comment",
    comments: "{count} comments",
    pullRequest: "pull request",
    issue: "issue",
    ariaLabel: "{state} {kind} {repo} #{number}: {title}, by {author}",
  },
  channels: {
    lastError: "Last error",
    refreshingStaleSnapshot:
      "Refreshing channel status in the background; showing the last successful snapshot.",
    hub: {
      connectedTitle: "Your channels",
      addTitle: "Add a channel",
      addSubtitle: "Pick a service and follow the guided setup.",
      updatedAgo: "Updated {ago}",
      noneConnected: "No channels connected yet. Pick one below to get started.",
      partialSnapshot: "Some channel checks did not finish before the UI budget.",
      stateRunning: "Running",
      stateConfigured: "Configured",
      stateAttention: "Needs attention",
      setUp: "Set up",
      guidedSetup: "Guided step-by-step setup",
      openDetails: "Open details",
      lastMessageAgo: "Last message {ago}",
      browseAllTitle: "More channels…",
      browseAllSubtitle: "Browse every available channel, including installable plugins.",
      runSetup: "Run setup",
      saveBeforeSetup:
        "You have unsaved channel config changes. Save or reload them before running guided setup.",
    },
    pairing: {
      title: "DM access requests",
      subtitle: "Review people waiting to send direct messages to pairing-protected channels.",
      channelFilter: "Channel",
      accountFilter: "Account",
      allChannels: "All channels",
      allAccounts: "All accounts",
      approve: "Approve",
      dismiss: "Dismiss",
      approveAria: "Approve {sender} for {channel}, account {account}",
      dismissAria: "Dismiss {sender} for {channel}, account {account}",
      requested: "Requested {ago}",
      expires: "Expires {ago}",
      senderDetails: "Sender details",
      missingPermission:
        "This connection does not have operator.pairing access, so DM requests cannot be reviewed.",
      noAccounts: "No configured channel accounts use DM sender pairing.",
      noRequests: "No pending DM access requests.",
      noFilteredRequests: "No pending requests match these filters.",
      limits:
        "Requests expire after {minutes} minutes. Each channel account can hold up to {count} pending requests.",
      detailTitle: "DM access",
      detailSubtitle: "People must be approved before their direct messages reach the agent.",
      pendingCount: "{count} pending",
      noPending: "No pending requests",
      review: "Review requests",
      approveDialogTitle: "Approve DM access",
      dismissDialogTitle: "Dismiss DM access request",
      approveExplanation:
        "This lets the sender talk to the agent in direct messages. It does not grant group access.",
      dismissExplanation:
        "This removes the current request but does not block the sender. They can request access again later.",
      notifyRequester: "Notify the requester after approval",
      makeCommandOwner: "Also make this sender the first command owner",
      commandOwnerHelp:
        "Command owners can run privileged commands and approve dangerous actions. This option is only available while no owner is configured.",
      commandOwnerNeedsAdmin:
        "No command owner is configured. This connection needs operator.admin to assign the first owner.",
      approvedNotice: "DM access approved.",
      approvedOwnerNotice: "DM access approved and the first command owner was configured.",
      approvedNotificationFailedNotice:
        "DM access approved, but the requester notification could not be delivered.",
      approvedOwnerFailedNotice:
        "DM access approved, but the first command owner could not be configured.",
      approvedFollowupsFailedNotice:
        "DM access approved, but requester notification and command-owner setup both failed.",
      dismissedNotice: "DM access request dismissed. The sender can request access again.",
    },
    setup: {
      dialogLabel: "Set up {channel}",
      title: "Set up {channel}",
      genericTitle: "a channel",
      subtitle: "A short guided setup — you can fine-tune everything later.",
      starting: "Starting setup…",
      working: "Working…",
      sessionExpired:
        "This setup session expired after the Gateway restarted. Close this dialog, then start channel setup again.",
      continue: "Continue",
      finish: "Finish",
      copyText: "Copy",
      openLink: "Open link",
      docs: "Docs",
      doneTitle: "Channel configured",
      doneBody:
        "Configuration saved. The gateway reloads the channel automatically; check its card for live status.",
      doneNoChangesTitle: "No changes made",
      doneNoChangesBody: "Setup finished without configuring a channel. Nothing was saved.",
      whatsappScanTitle: "Link WhatsApp by scanning the QR code",
      whatsappScanHelp:
        "On your phone, open WhatsApp → Settings → Linked devices → Link a device, then scan this code.",
      whatsappQrLoading: "Generating QR code…",
      whatsappQrHint: "Click Show QR to generate a pairing code.",
      whatsappQrAlt: "WhatsApp QR",
      whatsappLinked: "WhatsApp is linked and ready.",
      regenerateQr: "New QR code",
      linkLater: "Link later",
    },
    config: {
      schemaUnavailable: "Schema unavailable. Use Raw.",
      channelSchemaUnavailable: "Channel config schema unavailable.",
      loadingSchema: "Loading config schema…",
    },
    generic: {
      subtitle: "Channel status and configuration.",
    },
    discord: {
      title: "Discord",
      subtitle: "Bot status and channel configuration.",
    },
    googleChat: {
      title: "Google Chat",
      subtitle: "Chat API webhook status and channel configuration.",
    },
    imessage: {
      title: "iMessage",
      subtitle: "macOS bridge status and channel configuration.",
    },
    signal: {
      title: "Signal",
      subtitle: "signal-cli status and channel configuration.",
    },
    slack: {
      title: "Slack",
      subtitle: "Socket mode status and channel configuration.",
    },
    telegram: {
      title: "Telegram",
      subtitle: "Bot status and channel configuration.",
    },
    whatsapp: {
      title: "WhatsApp",
      subtitle: "Link WhatsApp Web and monitor connection health.",
      phoneNumber: "Phone number",
      loggedOut: "Logged out.",
      logoutNotCleared:
        "No stored WhatsApp session was cleared. It may already be absent, or its auth directory may require manual cleanup.",
    },
    gatewayUrlConfirmation: {
      title: "Change Gateway URL",
      subtitle: "This will reconnect to a different gateway server",
      warning: "Only confirm if you trust this URL. Malicious URLs can compromise your system.",
    },
    nostr: {
      title: "Nostr",
      subtitle: "Decentralized DMs via Nostr relays (NIP-04).",
      profile: "Profile",
      editProfile: "Edit Profile",
      profilePicture: "Profile picture",
      noProfile: "No profile set.",
      noProfileHint: 'Click "Edit Profile" to add your name, bio, and avatar.',
      name: "Name",
      displayName: "Display Name",
      about: "About",
      advanced: "Advanced",
      profilePicturePreview: "Profile picture preview",
      account: "Account",
      username: "Username",
      usernameHelp: "Short username (e.g., satoshi)",
      bio: "Bio",
      bioPlaceholder: "Tell people about yourself...",
      bioHelp: "A brief bio or description",
      displayNameHelp: "Your full display name",
      avatarUrl: "Avatar URL",
      avatarHelp: "HTTPS URL to your profile picture",
      bannerUrl: "Banner URL",
      bannerHelp: "HTTPS URL to a banner image",
      website: "Website",
      websiteHelp: "Your personal website",
      nip05Identifier: "NIP-05 Identifier",
      nip05Help: "Verifiable identifier (e.g., you@domain.com)",
      lightningAddress: "Lightning Address",
      lightningHelp: "Lightning address for tips (LUD-16)",
      placeholders: {
        username: "satoshi",
        displayName: "Satoshi Nakamoto",
        avatarUrl: "https://example.com/avatar.jpg",
        bannerUrl: "https://example.com/banner.jpg",
        website: "https://example.com",
        nip05: "you@example.com",
        lightningAddress: "you@getalby.com",
      },
      notices: {
        timeout:
          "Request timed out after 30 seconds; the server may still have applied the change — check the profile before retrying.",
        operationFailed: "{prefix}: {error}",
        updateFailed: "Profile update failed",
        updateFailedStatus: "Profile update failed ({status})",
        publishFailed: "Profile publish failed on all relays.",
        published: "Profile published to relays.",
        importFailed: "Profile import failed",
        importFailedStatus: "Profile import failed ({status})",
        importedFromRelays: "Profile imported from relays. Review and publish.",
        imported: "Profile imported. Review and publish.",
      },
    },
  },
  lazyView: {
    errorTitle: "Panel failed to load",
    genericSubtitle: "Something went wrong while loading this panel.",
    staleTitle: "A new version is available",
    staleSubtitle: "OpenClaw was updated in the background. Reload to get the latest panel.",
    reloading: "Reloading…",
    retry: "Retry",
    stylesFailed: "Styles failed to load, so the page may look broken.",
  },
  updates: {
    adminRequired: "Administrator access is required to change update settings or start an update.",
    campaign: {
      countdown: "Updating in {time}",
      waitingForIdle: "Waiting for active work · forced update in {time}",
      held: "Update held · resumes in {time}",
      applying: "Applying update…",
    },
    holdOneHour: "Hold 1 h",
    target: {
      version: "v{version}",
      commitBehind: "{count} commit behind",
      commitsBehind: "{count} commits behind",
    },
    sidebar: {
      campaignTarget: "{status} · {target}",
    },
    channel: {
      stable: "Stable",
      beta: "Beta",
      dev: "Dev",
      extendedStable: "Extended stable",
    },
    installKind: {
      package: "Package",
      git: "Git checkout",
      unknown: "Unknown",
    },
    page: {
      intro: "Manage the connected Gateway's release channel and update policy.",
      buildTitle: "Current build",
      gatewayVersion: "Gateway version",
      controlUiCommit: "Control UI commit",
      builtAt: "Built",
      installedAt: "Installed",
      installedAtUnknown: "Unknown · recorded after the next successful update",
      lastCommitAt: "Last commit",
      installKind: "Install type",
      policyTitle: "Update policy",
      channel: "Release channel",
      channelDescription: "Choose which OpenClaw release track this Gateway follows.",
      automaticUpdates: "Automatic updates",
      automaticUpdatesDescription:
        "Schedule available updates automatically. Dev auto-updates apply to git checkouts.",
      devPackageAutomaticHint:
        "Automatic dev updates require a source (git) install. This install is a package install — use stable or beta for automatic updates.",
      extendedStableAutomaticHint:
        "Extended stable reports available releases but never installs them automatically.",
      statusTitle: "Update status",
      scheduleStatus: "Status",
      commits: "Commits",
      available: "Update available {target}",
      upToDate: "Up to date",
      statusUnavailable: "Update status unavailable",
      gitCommitAhead: "{count} commit ahead of tracked upstream",
      gitCommitsAhead: "{count} commits ahead of tracked upstream",
      gitDiverged: "Diverged · {ahead} ahead, {behind} behind",
      gitFetchFailed: "Could not fetch the tracked upstream",
      gitNoUpstream: "No tracked upstream is configured",
      gitComparisonFailed: "Could not compare this checkout with its tracked upstream",
      updateNow: "Update now",
      updateNowDescription: "Install the available update and restart the Gateway.",
    },
    coalescedRestart:
      "Update installed. A gateway restart is already in progress; status will refresh after it reconnects.",
    error: "Update error: {error}",
    status: "Update {status}: {reason}. {guidance}",
    verificationFailed:
      "Update installed but running version did not change — restart may have been blocked.",
    verificationFailedWithVersions:
      "Update installed but running version did not change — restart may have been blocked. Expected v{expectedVersion}, running v{actualVersion}.",
    verificationFailedWithIdentity:
      "Update finished, but the running install does not match the expected revision. Expected {expected}, running {actual}.",
    handoffTimeout:
      "Update handoff started, but completion was not reported after reconnect. Run `openclaw update status` for the final result.",
    outcomeUnknown:
      "The update request may have been accepted, but the Gateway did not report a final result after reconnect. Run `openclaw update status` before retrying.",
    failureReasons: {
      dirty: "Commit or stash changes, then retry.",
      noUpstream: "Set an upstream branch, then retry.",
      notGitInstall:
        "Not a git checkout. Run `openclaw update` from the CLI for a global reinstall.",
      notOpenclawRoot:
        "Run the update from an OpenClaw checkout or use the CLI global reinstall path.",
      depsInstallFailed: "Dependency install failed. Fix the install error and retry.",
      buildFailed: "Build failed. Fix the build error and retry.",
      buildDirty:
        "The selected revision's build changed checkout files. Retry with a revision that includes its generated artifacts.",
      uiBuildFailed: "The control UI rebuild failed. Fix the UI build error and retry.",
      globalInstallFailed:
        "The global package install did not verify on disk. Retry or reinstall from the CLI.",
      restartDisabled:
        "The update was not applied because gateway restarts are disabled. Enable restarts in config, then retry.",
      restartUnavailable:
        "This global install cannot be safely replaced while restarts are disabled and no supervisor is present.",
      restartUnhealthy:
        "The replacement process never became healthy. The previous process stayed up so you can recover.",
      restartRevisionMismatch:
        "The restarted Gateway is running a different revision. Check the service install root and retry.",
      restartRevisionUnavailable:
        "The restarted Gateway could not report its revision. Check the service install root and logs before retrying.",
      alreadyCurrent: "This checkout is already at its tracked upstream revision.",
      managedServiceHandoffAlreadyRunning:
        "Another managed update is already running. Wait for it to complete, then refresh update status.",
      doctorFailed: "Doctor repair failed. Run `openclaw doctor --non-interactive` and retry.",
      default: "See the gateway logs for the exact failure and retry once the cause is fixed.",
    },
    postRestart: {
      restartUnhealthy:
        "The replacement process never became healthy and the previous process stayed up.",
      default: "Check the gateway logs for the replacement failure.",
    },
  },
  devices: {
    pairing: {
      button: "Pair mobile device",
      adminRequired: "Administrator access is required to create setup codes.",
      title: "OpenClaw mobile",
      subtitle: "Scan this QR code in the mobile app to connect a new phone.",
      noApp: "Don't have the app yet?",
      getApps: "Get the apps",
      generating: "Creating a secure setup code…",
      accessTitle: "Mobile access",
      fullAccess: "Full access (recommended)",
      fullAccessHint:
        "Device capabilities plus complete Gateway controls, including settings and upgrades.",
      limitedAccess: "Limited access",
      limitedAccessHint:
        "Device capabilities, chat, and approvals without administrative controls.",
      generateCode: "Create setup code",
      transportLimitedTitle: "Limited for network safety",
      transportLimitedHint:
        "This Gateway URL uses plaintext ws://. Use wss:// or Tailscale Serve, then create a new code for full access.",
      failed: "Could not create a setup code.",
      qrAlt: "OpenClaw mobile pairing QR code",
      qrUnavailable: "QR unavailable. Copy the setup code instead.",
      copySetupCode: "Copy setup code",
      newCode: "New code",
      showSetupCode: "Show setup code",
      pending: "Device requests waiting for review: {count}",
      review: "Review",
      waiting: "Official OpenClaw mobile apps connect automatically after scanning.",
      help: "Pairing help",
      manageDevices: "Manage devices",
    },
    binding: {
      loadConfigHint: "Load config to edit bindings.",
      formModeHint: "Switch the Config tab to Form mode to edit bindings here.",
      execNodeBinding: "Exec node binding",
      execNodeBindingSubtitle: "Pin agents to a specific node when using exec host=node.",
      defaultBinding: "Default binding",
      defaultBindingHint: "Used when agents do not override a node binding.",
      node: "Node",
      anyNode: "Any node",
      noNodes: "No nodes with system.run available.",
      noAgents: "No agents found.",
      defaultAgent: "default agent",
      agent: "agent",
      usesDefault: "uses default ({node})",
      override: "override: {node}",
      binding: "Binding",
      useDefault: "Use default",
      any: "any",
    },
    inventory: {
      title: "Paired devices",
      summaryConnected: "{connected} of {total} connected",
      summaryPending: "{count} awaiting approval",
      cleanupStale: "Clean up {count} stale",
      pendingApproval: "Pending approval",
      empty: "No paired devices.",
      connectedWithoutPairing: "Connected without pairing",
      olderPairing: "{count} older pairing of {name}",
      olderPairings: "{count} older pairings of {name}",
      autoPaired: "auto-paired",
      versionDrift: "version drift",
      versionDriftTitle:
        "Device {nodeVersion}; Gateway {gatewayVersion}. Update the older component to align the fleet.",
      manualWake: "manual wake required",
      manualWakeTitle:
        "The Gateway cannot wake an offline Windows device. Start the machine or restore its network connection.",
      approvalNeeded: "approval needed",
      inputAgo: "input {time} ago",
      seen: "seen {time}",
      approved: "approved {time}",
      details: "Details",
      deviceId: "Device ID: {id}",
      remoteIp: "Remote IP: {ip}",
      scopes: "scopes: {scopes}",
      tokens: "Tokens",
      capabilities: "Capabilities",
      commands: "Commands",
      connected: "connected",
      offline: "offline",
      approve: "Approve",
      reject: "Reject",
      remove: "Remove",
      removeName: "Remove {name}",
      removePromptTitle: "Remove {name}?",
      removePromptBody: "This device must pair again before it can reconnect.",
      removeStalePromptTitle: "Remove {count} stale pairings?",
      removeStalePromptTitleOne: "Remove 1 stale pairing?",
      removeStalePromptBody: "Affected clients re-pair silently on their next connection.",
      gateway: "gateway",
      unpaired: "unpaired",
      unknownClient: "unknown client",
      revoked: "revoked",
      active: "active",
      rotate: "Rotate",
      revoke: "Revoke",
      none: "none",
      rolesAndScopes: "roles: {roles} · scopes: {scopes}",
      scopeUpgrade: "scope upgrade requires approval",
      roleUpgrade: "role upgrade requires approval",
      reapproval: "reconnect details changed; approval required",
      newPairing: "new device pairing request",
      repair: "repair",
      requestedAt: "{note} · requested {time}",
      requestedAccess: "requested: {access}",
      approvedAccess: "approved now: {access}",
    },
    execApprovals: {
      title: "Exec approvals",
      subtitlePrefix: "Allowlist and approval policy for",
      loadHint: "Load exec approvals to edit allowlists.",
      hostNativePolicy: "Host-native policy",
      hostNativeHint: "Read-only here. Edit from the companion app or CLI.",
      native: "Native",
      defaultAction: "Default action",
      rule: "{count} rule",
      rules: "{count} rules",
      allShells: "all shells",
      off: "off",
      on: "on",
      target: "Target",
      targetHint: "Gateway edits local approvals; node edits the selected node.",
      host: "Host",
      gateway: "Gateway",
      node: "Node",
      selectNode: "Select node",
      noNodes: "No nodes advertise exec approvals yet.",
      scope: "Scope",
      defaults: "Defaults",
      security: "Security",
      defaultSecurity: "Default security mode.",
      defaultValue: "Default: {value}.",
      mode: "Mode",
      useDefaultValue: "Use default ({value})",
      ask: "Ask",
      defaultPrompt: "Default prompt policy.",
      askFallback: "Ask fallback",
      promptUnavailable: "Applied when the UI prompt is unavailable.",
      fallback: "Fallback",
      autoAllowSkills: "Auto-allow skill CLIs",
      autoAllowSkillsHint: "Allow skill executables listed by the Gateway.",
      usingDefault: "Using default ({value}).",
      override: "Override ({value}).",
      useDefault: "Use default",
      allowlist: "Allowlist",
      allowlistHint: "Case-insensitive glob patterns.",
      addPattern: "Add pattern",
      emptyAllowlist: "No allowlist entries yet.",
      newPattern: "New pattern",
      lastUsed: "Last used: {time}",
      pattern: "Pattern",
      remove: "Remove",
      options: {
        deny: "Deny",
        allowlist: "Allowlist",
        full: "Full",
        off: "Off",
        onMiss: "On miss",
        always: "Always",
      },
    },
  },
  worktrees: {
    newWorktree: "New worktree",
    ownerManual: "Manual",
    ownerSession: "Session",
    ownerWorkboard: "Workboard",
    title: "Managed Worktrees",
    subtitle: "Isolated repository checkouts owned by OpenClaw.",
    cleanNow: "Clean up now",
    name: "Name",
    repo: "Repository",
    empty: "No managed worktrees.",
    restorable: "Restorable",
    restore: "Restore",
    confirmDelete: "Snapshot and delete {name}?",
    confirmForceDelete: "Snapshot failed: {error}\n\nDelete without a snapshot?",
  },
  agentChip: agentEn.agentChip,
  agentScope: agentEn.agentScope,
  newSession: {
    title: "New session",
    hint: "Pick where this session works, then say what to do.",
    draftRow: "New session",
    agent: "Agent",
    where: "Where",
    gateway: "Gateway · local",
    gatewayNamed: "Gateway · {name}",
    cloudWorker: "Cloud · {profile}",
    cloudWorkerProvider: "Cloud worker provider: {provider}",
    cloudRequiresWorktree: "Cloud workers require a managed worktree",
    cloudRequiresOpenClawRuntime:
      "Cloud workers require the OpenClaw runtime; {runtime} is selected.",
    cloudSecureContextRequired:
      "Cloud workers need a secure browser context so recovery can protect your task.",
    cloudStartFailed: "The session was created locally, but cloud startup failed: {error}",
    cloudSyncsFolder: "Syncs {folder} to the cloud worker",
    folder: "Folder",
    folderPlaceholder: "Agent workspace",
    places: "Places",
    recentFolders: "Recent",
    runsOn: "Runs on {place}",
    browse: "Browse folders",
    browseRequiresAdmin: "Folder browsing needs an admin connection",
    browserUp: "Parent folder",
    browserUse: "Use this folder",
    browserEmpty: "No subfolders",
    browserLoadFailed: "Couldn't list that folder.",
    hiddenFolder: "Hidden folder",
    worktree: "Worktree",
    checkingGit: "Checking Git availability…",
    gitCheckUnavailable: "Couldn't verify Git for this folder. Choose it again to retry.",
    worktreeUnavailable: "Selected folder is not a Git checkout",
    baseBranch: "Base branch",
    worktreeName: "Worktree name",
    worktreeNamePlaceholder: "auto",
    worktreeNameInvalid: "Worktree names use lowercase letters, digits, and dashes.",
    incognito: "Incognito",
    incognitoDescription: "Keep this session only until the Gateway restarts",
    draft: "Draft",
    draftDescription: "Keep this session to yourself until you publish it",
    messagePlaceholder: "What should this session work on?",
    readingAttachment: "Reading attachment",
    start: "Start session",
    starting: "Starting…",
    createFailed: "Couldn't create the session.",
    createOutcomeUnknown:
      "The Gateway changed while this session was starting. Check recent sessions before starting this task again.",
    catalogUnavailable: "This session target is unavailable.",
  },
  dashboardsPage: {
    emptyTitle: "No dashboards yet",
    emptyDescription: "Open a session and switch to the Dashboard face to add it here.",
    loadError: "Could not load dashboards: {error}",
  },
  sessionsView: {
    subagentPrefix: "Subagent:",
    automationPrefix: "Automation:",
    actionRequiresConnection: "Connect to the Gateway to change sessions.",
    actionUnavailable: "This Gateway does not support this session action.",
    actionRequiresRead: "This action requires operator.read access.",
    actionRequiresWrite: "This action requires operator.write access.",
    actionRequiresAdmin: "This action requires operator.admin access.",
    deletePreservedWorktrees:
      "{count} session worktree(s) with uncommitted or unpushed work were kept ({branches}). Manage them under Settings -> Worktrees.",
    deletePreservedWorktreeConfirm:
      "The session's worktree has uncommitted or unpushed work, so it was kept ({branch}). Delete the checkout anyway?",
    title: "Sessions",
    subtitle: "Browse sessions and manage per-session overrides.",
    store: "Store: {path}",
    active: "Updated within",
    limit: "Limit",
    createdBy: "Created by {name}",
    archivedBy: "Archived by {name}",
    people: "People",
    allCreators: "All people",
    filterControls: "Session filters",
    sourceFilters: "Session source filters",
    global: "Global",
    unknown: "Unknown",
    sessionState: "Session state",
    all: "All",
    sessionArchived: "Session archived",
    sessionsArchived: "Archived {count} sessions",
    deleteAllArchived: "Delete all archived…",
    deleteAllArchivedConfirm: "Delete {count} archived sessions and their transcripts?",
    activeTooltip: "Loads sessions updated in the last {count} minutes.",
    limitTooltip: "Max sessions to load.",
    globalTooltip: "Include global sessions.",
    unknownTooltip: "Include unknown sessions.",
    archivedOnlyTooltip: "Show only archived sessions.",
    minutesPlaceholder: "min",
    searchPlaceholder: "Filter by key, agent, label, kind…",
    transcriptSearchTitle: "Search transcripts",
    transcriptSearchDescription:
      "Find exact words or phrases in user and assistant messages across the default agent's sessions.",
    transcriptSearchInputLabel: "Search session transcripts",
    transcriptSearchPlaceholder: "Search exact words or phrases…",
    transcriptSearchAction: "Search",
    transcriptSearchClear: "Clear",
    transcriptSearchRetry: "Retry",
    transcriptSearchSearching: "Searching transcripts…",
    transcriptSearchUnavailable: "Transcript search requires a newer Gateway.",
    transcriptSearchError: "Transcript search failed",
    transcriptSearchIndexing:
      "The transcript index is still updating. Retry to include recent messages.",
    transcriptSearchEmpty: "No transcript messages match that search.",
    transcriptSearchMatches: "Transcript matches: {count}",
    transcriptSearchTruncated: "Showing the first 25 matches.",
    user: "User",
    assistant: "Assistant",
    selected: "{count} selected",
    deleteSelected: "Delete",
    selectAllOnPage: "Select all on page",
    selectSession: "Select session",
    optionalPlaceholder: "(optional)",
    key: "Key",
    label: "Label",
    kind: "Kind",
    updated: "Updated",
    tokens: "Tokens",
    compaction: "Compaction",
    goal: "Goal",
    goalNote: "Goal note",
    thinking: "Thinking",
    fast: "Fast",
    verbose: "Verbose",
    reasoning: "Reasoning",
    actions: "Actions",
    addToWorkboard: "Add to Workboard",
    openWorkboardCard: "Open Workboard card",
    dashboardAvailable: "Dashboard available",
    approvalNeeded: "Approval needed",
    queuedMessage: "{count} message queued to send",
    queuedMessages: "{count} messages queued to send",
    noSessions: "No sessions found.",
    noActiveSessions: "No active sessions.",
    noArchivedSessions: "No archived sessions.",
    noSessionsMatchFilters: "No sessions match your filters.",
    pagination: "{start}-{end} of {total} rows",
    pageSize: "Rows per page",
    rowsPerPage: "{count} per page",
    showAll: "Show all",
    inherit: "inherit",
    offExplicit: "off (explicit)",
    auto: "auto",
    on: "on",
    off: "off",
    full: "full",
    manual: "manual",
    autoThreshold: "auto-threshold",
    overflowRetry: "overflow retry",
    timeoutRetry: "timeout retry",
    tokenRange: "{before} to {after} tokens",
    tokensBefore: "{count} tokens before",
    tokenDeltaUnavailable: "token delta unavailable",
    contextUsage: "{percent}% of context used ({used} / {context} tokens)",
    contextUsageApprox: "~{percent}% of context used ({used} / {context} tokens, approximate)",
    checkpoints: "{count} Checkpoints",
    checkpoint: "{count} Checkpoint",
    showSessionDetails: "Show session details for {count}",
    hideSessionDetails: "Hide session details for {count}",
    sessionDetails: "Session details",
    overrides: "Overrides",
    compactionHistory: "Compaction history",
    status: "Status",
    statusLive: "Live",
    statusIdle: "Idle",
    statusUnknown: "Unknown",
    statusRunning: "Running",
    statusDone: "Done",
    statusFailed: "Failed",
    statusKilled: "Killed",
    statusTimeout: "Timed out",
    waitingForAnswer: "Waiting for your answer",
    waitingForApproval: "Waiting for approval",
    runFailedReason: "Run failed: {reason}",
    runErrorTimedOut: "Timed out",
    runErrorUnknown: "Unknown error",
    attentionRequired: "Session needs attention",
    openSession: "Open session",
    model: "Model",
    provider: "Provider",
    runtime: "Runtime",
    runDuration: "Run duration",
    lastActive: "Last active {time}",
    surface: "Surface",
    subject: "Subject",
    room: "Room",
    space: "Space",
    sessionId: "Session ID",
    activeRun: "Active run",
    showChildSessions: "Show {count} child sessions for {session}",
    hideChildSessions: "Hide {count} child sessions for {session}",
    childSessions: "Child sessions",
    showMoreChildren: "Show {count} more",
    archived: "Archived",
    pinned: "Pinned",
    unread: "Unread",
    automationAttached: "Automation attached",
    incognito: "Incognito session",
    cloudWorkerPlacement: "Cloud worker: {state}",
    cloudWorkerPlacementConflict: "Cloud worker: {state} · 1 workspace conflict",
    cloudWorkerPlacementConflicts: "Cloud worker: {state} · {count} workspace conflicts",
    cloudWorkerDescendantConflict: "Cloud worker child: 1 workspace conflict",
    cloudWorkerDescendantConflicts: "Cloud worker children: {count} workspace conflicts",
    renameSession: "Rename session",
    renameSessionPrompt: "Rename session",
    renameSessionMenu: "Rename…",
    pinSession: "Pin session",
    unpinSession: "Unpin session",
    changeIcon: "Change icon",
    customEmoji: "Custom emoji",
    invalidEmojiIcon: "Enter exactly one emoji.",
    removeIcon: "Remove icon",
    markUnread: "Mark as unread",
    markRead: "Mark as read",
    markUnreadCount: "Mark {count} as unread",
    markReadCount: "Mark {count} as read",
    forkSession: "Fork",
    forkedSession: "Forked session",
    openChat: "Open chat",
    openPullRequest: "Open PR",
    openInEditorMenu: "Open in",
    archiveSession: "Archive session",
    archiveSessionCount: "Archive {count}",
    restoreSession: "Restore session",
    restoreSessionCount: "Restore {count}",
    stopCloudWorker: "Stop cloud worker…",
    stopCloudWorkerConfirm: 'Stop the cloud worker for "{session}"?',
    stopCloudWorkerConfirmAction: "Stop worker",
    deleteSessionMenu: "Delete…",
    deleteSessionCount: "Delete {count}…",
    deleteSessionConfirm: 'Delete "{session}" and its transcript?',
    deleteSessionsConfirm: "Delete {count} sessions and their transcripts?",
    deleteSelectedConfirmOne:
      "Delete 1 session?\n\nThis will delete the session entry and archive its transcript.",
    deleteSelectedConfirm:
      "Delete {count} sessions?\n\nThis will delete the session entries and archive their transcripts.",
    groupBy: "Group by",
    groupByNone: "None",
    groupByCategory: "Custom groups",
    showCronSessions: "Show automation sessions",
    groupByChannel: "Channel",
    groupByKind: "Kind",
    groupByAgent: "Agent",
    groupByDate: "Date",
    group: "Group",
    ungrouped: "Ungrouped",
    newGroup: "New group…",
    newGroupPrompt: "New group name",
    moveToGroup: "Move session to a group",
    moveToGroupMenu: "Move to group",
    moveToGroupMenuCount: "Move {count} to group",
    removeFromGroup: "Remove from group",
    groupMenu: "Group options for {group}",
    renameGroupMenu: "Rename group…",
    renameGroupPrompt: "Rename group",
    deleteGroupMenu: "Delete group…",
    deleteGroupConfirm: 'Delete group "{group}"? Its sessions move to Ungrouped.',
    dragSessionHint: "Drag session to move between groups",
    dateToday: "Today",
    dateYesterday: "Yesterday",
    dateThisWeek: "This week",
    dateOlder: "Older",
    dateNoActivity: "No activity",
    groupRowCount: "{count} sessions",
    groupRowCountOne: "{count} session",
    loadingCheckpoints: "Loading checkpoints…",
    noCheckpoints: "No compaction checkpoints recorded for this session.",
    noSummary: "No summary captured.",
    branchFromCheckpoint: "Branch from checkpoint",
    branchCheckpointConfirm: "Create a new child session from this compacted checkpoint?",
    restoreCheckpoint: "Restore checkpoint",
    restoreCheckpointConfirm:
      "Restore this session to the selected compacted checkpoint?\n\nThis replaces the current active transcript for the session key.",
  },
  agents: {
    noAgents: "No agents",
    defaults: {
      title: "Agent defaults",
      description: "Defaults every agent inherits unless overridden.",
    },
    copyId: "Copy ID",
    default: "Default",
    setDefault: "Set Default",
    ...agentEn.pinning,
    selectTitle: "Select an agent",
    selectSubtitle: "Pick an agent to inspect its workspace and tools.",
    tabs: {
      overview: "Overview",
      files: "Files",
      tools: "Tools",
      skills: "Skills",
      channels: "Channels",
      cronJobs: "Automations",
      memory: "Memory",
    },
    context: {
      title: "Agent Context",
      workspace: "Workspace",
      openFilesTab: "Open Files tab",
      primaryModel: "Primary Model",
      runtime: "Runtime",
      thinkingDefault: "Thinking Default",
      identityName: "Identity Name",
      identityAvatar: "Identity Avatar",
      skillsFilter: "Skills Filter",
      default: "Default",
      configurationSubtitle: "Workspace, identity, and model configuration.",
      schedulingSubtitle: "Workspace and scheduling targets.",
    },
    identity: agentEn.identity,
    overview: {
      title: "Overview",
      subtitle: "Workspace paths and identity metadata.",
      selectedSkills: "{count} selected",
      allSkills: "all skills",
      unsavedConfig: "You have unsaved config changes.",
      modelSelection: "Model Selection",
      primaryModel: "Primary model",
      primaryModelDefault: "Primary model (default)",
      notSet: "Not set",
      inheritDefault: "Inherit default",
      inheritDefaultModel: "Inherit default ({model})",
      fallbacks: "Fallbacks",
    },
    toolCatalog: {
      groups: {
        files: "Files",
        runtime: "Runtime",
        web: "Web",
        memory: "Memory",
        sessions: "Sessions",
        ui: "UI",
        messaging: "Messaging",
        automation: "Automation",
        nodes: "Nodes",
        agents: "Agents",
        media: "Media",
      },
      descriptions: {
        read: "Read file contents",
        write: "Create or overwrite files",
        edit: "Make precise edits",
        applyPatch: "Patch files (OpenAI)",
        exec: "Run shell commands",
        process: "Manage background processes",
        webSearch: "Search the web",
        webFetch: "Fetch web content",
        memorySearch: "Semantic search",
        memoryGet: "Read memory files",
        sessionsList: "List sessions",
        sessionsHistory: "Session history",
        sessionsSend: "Send to session",
        sessionsSpawn: "Spawn sub-agent",
        sessionStatus: "Session status",
        browser: "Control web browser",
        canvas: "Control canvases",
        message: "Send messages",
        cron: "Schedule tasks",
        gateway: "Gateway control",
        nodes: "Nodes + devices",
        agentsList: "List agents",
        image: "Image understanding",
      },
      profiles: {
        minimal: "Minimal",
        coding: "Coding",
        messaging: "Messaging",
        full: "Full",
      },
    },
    skillsPanel: {
      title: "Skills",
      subtitle: "Per-agent skill allowlist and workspace skills.",
      loadConfig: "Load the gateway config to set per-agent skills.",
      customAllowlist: "This agent uses a custom skill allowlist.",
      allEnabled: "All skills are enabled. Disabling any skill will create a per-agent allowlist.",
      loadAgent: "Load skills for this agent to view workspace-specific entries.",
      filter: "Filter",
      searchPlaceholder: "Search skills",
      shown: "{count} shown",
      empty: "No skills found.",
      missing: "Missing: {items}",
      reason: "Reason: {items}",
      updateError: "Could not update the agent skill allowlist.",
    },
    channels: {
      title: "Channels",
      subtitle: "Gateway-wide channel status snapshot.",
      lastRefresh: "Last refresh: {time}",
      loadHint: "Load channels to see live status.",
      empty: "No channels found.",
      connectedCount: "{connected}/{total} connected",
      noAccounts: "no accounts",
      configuredCount: "{count} configured",
      notConfigured: "not configured",
      enabledCount: "{count} enabled",
      setupGuide: "Setup guide",
    },
    cronPanel: {
      schedulerTitle: "Scheduler",
      schedulerSubtitle: "Gateway automations status.",
      jobs: "Jobs",
      nextWake: "Next wake",
      agentJobsTitle: "Agent Automations",
      agentJobsSubtitle: "Scheduled jobs targeting this agent.",
      noJobs: "No jobs assigned.",
      runNow: "Run Now",
    },
    files: {
      emptyDraft: "Empty draft",
      minRead: "{count} min read",
      markdownPreview: "Markdown Preview",
      extensionPreview: "{ext} Preview",
      preview: "Preview",
      expandPreview: "Expand preview",
      collapsePreview: "Collapse preview",
      editFile: "Edit file",
      closePreview: "Close preview",
      coreFilesTitle: "Core Files",
      coreFilesSubtitle: "Bootstrap persona, identity, and tool guidance.",
      workspace: "Workspace",
      loadHint: "Load the agent workspace files to edit core instructions.",
      empty: "No files found.",
      missing: "missing",
      selectFile: "Select a file to edit.",
      willCreateOnSave: "Will Create on Save",
      liveDraftPreview: "Live Draft Preview",
      savedPreview: "Saved Preview",
      updated: "Updated {time}",
      notCreatedYet: "Not Created Yet",
      updatedUnknown: "Updated Unknown",
      missingHint: "This file is missing. Saving will create it in the agent workspace.",
      addFile: "Add file…",
      createHint: "This file does not exist yet. Saving will create it in the agent workspace.",
      content: "Content",
      words: "{count} words",
      lines: "lines",
    },
  },
  debug: {
    snapshotsTitle: "Snapshots",
    snapshotsSubtitle: "Status, health, and heartbeat data.",
    status: "Status",
    health: "Health",
    lastHeartbeat: "Last heartbeat",
    security: {
      audit: "Security audit",
      critical: "{count} critical",
      warnings: "{count} warnings",
      noCriticalIssues: "No critical issues",
      info: "{count} info",
      runPrefix: "Run",
      runSuffix: "for details.",
    },
    manualRpcTitle: "Manual RPC",
    manualRpcSubtitle: "Send a raw gateway method with JSON params.",
    callFailed: "Call failed",
    method: "Method",
    selectMethod: "Select a method…",
    paramsJson: "Params (JSON)",
    modelsTitle: "Models",
    modelsSubtitle: "Catalog from models.list.",
    eventLogTitle: "Event Log",
    eventLogSubtitle: "Latest gateway events.",
    noEvents: "No events yet.",
  },
  configForm: {
    redactedPlaceholder: "[redacted - click reveal to view]",
    sectionHelp: "Help for {section}",
    readGuide: "Read the guide",
    showAdvanced: "Show advanced",
    advancedHidden: "{count} advanced setting hidden",
    advancedHiddenPlural: "{count} advanced settings hidden",
    advancedDivider: "Advanced",
    hideValue: "Hide value",
    revealValue: "Reveal value",
    disableStreamToReveal: "Disable stream mode to reveal value",
    unsupportedType: "Unsupported type: {type}. Use Raw mode.",
    structuredSecretRaw: "Structured value (SecretRef) - use Raw mode to edit",
    structuredSecretFile: "Structured value (SecretRef) - edit the config file directly",
    defaultValue: "Default: {value}",
    usingDefault: "Using default: {value}",
    resetToDefault: "Reset to default",
    select: "Select...",
    nullValue: "null",
    jsonValue: "JSON value",
    invalidJson: "Enter valid JSON before leaving this field.",
    invalidString: "Enter a value that matches this setting's constraints.",
    invalidNumber: "Enter a value within the allowed range and step.",
    draftRejected: "This setting could not be saved. Your draft is still here.",
    unsupportedArray: "Unsupported array schema. Use Raw mode.",
    itemCountOne: "{count} item",
    itemCount: "{count} items",
    add: "Add",
    noItems: 'No items yet. Click "Add" to create one.',
    removeItem: "Remove item",
    customEntries: "Custom entries",
    addEntry: "Add Entry",
    noCustomEntries: "No custom entries.",
    key: "Key",
    removeEntry: "Remove entry",
    schemaUnavailable: "Schema unavailable.",
    unsupportedSchema: "Unsupported schema. Use Raw.",
    unsupportedNode: "Unsupported schema node. Use Raw mode.",
    noSettingsMatch: 'No settings match "{query}"',
    noSettingsInSection: "No settings in this section",
    sections: {
      env: {
        label: "Environment Variables",
        description: "Environment variables passed to the gateway process",
      },
      update: {
        label: "Updates",
        description: "Auto-update settings and release channel",
      },
      agents: {
        label: "Agents",
        description: "Agent configurations, models, and identities",
      },
      auth: {
        label: "Authentication",
        description: "API keys and authentication profiles",
      },
      channels: {
        label: "Channels",
        description: "Messaging channels (Telegram, Discord, Slack, etc.)",
      },
      messages: {
        label: "Messages",
        description: "Message handling and routing settings",
      },
      commands: {
        label: "Commands",
        description: "Custom slash commands",
      },
      hooks: {
        label: "Hooks",
        description: "Webhooks and event hooks",
      },
      skills: {
        label: "Skills",
        description: "Skill packs and capabilities",
      },
      tools: {
        label: "Tools",
        description: "Tool configurations (browser, search, etc.)",
      },
      gateway: {
        label: "Gateway",
        description: "Gateway server settings (port, auth, binding)",
      },
      wizard: {
        label: "Setup Wizard",
        description: "Setup wizard state and history",
      },
      meta: {
        label: "Metadata",
        description: "Gateway metadata and version information",
      },
      logging: {
        label: "Logging",
        description: "Log levels and output configuration",
      },
      browser: {
        label: "Browser",
        description: "Browser automation settings",
      },
      ui: {
        label: "UI",
        description: "User interface preferences",
      },
      models: {
        label: "Models",
        description: "AI model configurations and providers",
      },
      bindings: {
        label: "Bindings",
        description: "Key bindings and shortcuts",
      },
      broadcast: {
        label: "Broadcast",
        description: "Broadcast and notification settings",
      },
      tts: {
        label: "Voice",
        description: "Text-to-speech output, voices, and personas",
      },
      session: {
        label: "Session",
        description: "Session management and persistence",
      },
      cron: {
        label: "Automations",
        description: "Scheduled tasks and automation",
      },
      discovery: {
        label: "Discovery",
        description: "Service discovery and networking",
      },
      talk: {
        label: "Talk",
        description: "Voice and speech settings",
      },
      plugins: {
        label: "Plugins",
        description: "Plugin management and extensions",
      },
      diagnostics: {
        label: "Diagnostics",
        description: "Instrumentation, OpenTelemetry, and cache-trace settings",
      },
      cli: {
        label: "CLI",
        description: "CLI banner and startup behavior",
      },
      secrets: {
        label: "Secrets",
        description: "Secret provider configuration",
      },
      acp: {
        label: "ACP",
        description: "Agent Communication Protocol runtime and streaming settings",
      },
      mcp: {
        label: "MCP",
        description: "Model Context Protocol server definitions",
      },
    },
  },
  // Historical namespace from the pre-restructure "quick settings" page; its
  // keys now serve Models, Privacy & Security, Appearance, and Profile.
  // Renaming would force retranslation of every key, so the name stays.
  quickSettings: {
    language: "Language",
    model: {
      title: "Model & Thinking",
      model: "Model",
      thinking: "Thinking",
      fastMode: "Fast mode",
      default: "Default",
      modelPolicy: "Model policy",
      thinkingLevels: {
        off: "Off",
        low: "Low",
        medium: "Medium",
        high: "High",
      },
      fastModes: {
        auto: "Auto",
        fast: "Fast",
        standard: "Standard",
      },
    },
    channels: {
      title: "Channels",
      connect: "Connect →",
    },
    appearance: {
      lobsterVisits: "Lobster visits",
      lobsterVisitsOn: "Drops by occasionally",
      lobsterVisitsOff: "Never visits",
      lobsterSounds: "Lobster sounds",
      lobsterSoundsOn: "Tiny blubs when touched",
      lobsterSoundsOff: "Silent",
      lobsterdex: "Lobsterdex",
      lobsterdexSeen: "{seen}/{total} visited",
      lobsterdexFirstVisited: "{name} · first visited {date}",
      lobsterdexCardFirstVisited: "First visited {date}",
      lobsterdexCardShinySeen: "✦ Shiny spotted {date}",
      lobsterdexCardCopyLink: "Copy link",
      lobsterdexOpen: "Open Lobsterdex",
    },
    security: {
      intro: "Review gateway access, tool policy, device authentication, and approvals.",
      title: "Security",
      gatewayAuth: "Gateway auth",
      execPolicy: "Exec policy",
      deviceAuth: "Device auth",
      browserEnabled: "Browser enabled",
      toolProfile: "Tool profile",
    },
    system: {
      gatewayHost: "Gateway Host",
      up: "Up {duration}",
      cpu: "CPU",
      memory: "Memory",
      disk: "Disk",
      usage: "{label} usage",
      loadAverage: "Load average: {values}",
      core: "{count} core",
      cores: "{count} cores",
      load: "load",
      used: "used",
      freeOf: "{free} free of {total}",
      runtime: "Node {version} · PID {pid}",
    },
    personal: {
      browserOnly: "Stored in this browser only.",
    },
  },
  configPage: {
    themeImported: "Imported {name}.",
    themeRemoved: "Custom theme removed.",
  },
  configView: {
    categories: {
      core: "Core",
      ai: "Agent Defaults",
      communication: "Communication",
      security: "Privacy & Security",
      automation: "Automation",
      infrastructure: "Infrastructure",
      appearance: "Appearance",
      other: "Other",
    },
    sections: {
      env: "Environment",
      auth: "Authentication",
      update: "Updates",
      meta: "Meta",
      logging: "Logging",
      diagnostics: "Diagnostics",
      cli: "CLI",
      secrets: "Secrets",
      agents: "Agents",
      models: "Models",
      skills: "Skills",
      tools: "Tools",
      memory: "Memory",
      session: "Session",
      channels: "Channels",
      messages: "Messages",
      broadcast: "Broadcast",
      notifications: "Notifications",
      talk: "Talk",
      tts: "Voice",
      commands: "Commands",
      hooks: "Hooks",
      bindings: "Bindings",
      cron: "Automations",
      approvals: "Approvals",
      security: "Security Policy",
      plugins: "Plugins",
      gateway: "Gateway",
      browser: "Browser",
      nodeHost: "Node Host",
      discovery: "Discovery",
      acp: "ACP",
      mcp: "MCP",
      theme: "Theme",
      ui: "UI",
      wizard: "Setup Wizard",
    },
    themes: {
      claw: {
        label: "Claw",
        description: "Chroma family",
      },
      knot: {
        label: "Knot",
        description: "Black & red",
      },
      dash: {
        label: "Dash",
        description: "Chocolate blueprint",
      },
    },
    textSizes: {
      small: "Small",
      default: "Default",
      large: "Large",
      xl: "XL",
      xxl: "XXL",
    },
    syncedHint: "Synced across your devices through the gateway.",
    syncPendingHint: "Waiting to sync through the gateway.",
    notifications: {
      title: "Push notifications",
      nativeTitle: "Notifications",
      hint: "Receive browser push notifications from your gateway.",
      nativeHint: "Notifications are shown natively by the OpenClaw app on this Mac.",
      unavailableHint: "Not available in this browser.",
      unavailable: "Unavailable",
      checking: "Checking...",
      granted: "Granted",
      denied: "Denied",
      notRequested: "Not requested",
      unsupported: "Unsupported",
      subscribed: "Subscribed",
      notSubscribed: "Not subscribed",
      blocked: "Blocked",
      ready: "Ready",
      browserSupport: "Browser support",
      available: "Available",
      notSupported: "Not supported",
      permission: "Permission",
      status: "Status",
      unsubscribe: "Unsubscribe",
      sendTest: "Send test",
      subscribing: "Subscribing...",
      enable: "Enable notifications",
      openSystemSettings: "Open System Settings",
      blockedHint:
        "Notifications are blocked. Update your browser site permissions to allow notifications.",
      nativeBlockedHint:
        "Notifications are disabled for OpenClaw in macOS. Allow them in System Settings > Notifications.",
    },
    appearance: {
      intro: "Theme, chat, and sidebar preferences for this Control UI client.",
      theme: "Theme",
      chooseTheme: "Choose a theme family.",
      importedTheme: "Imported theme",
      import: "Import",
      importedFrom: "Imported from tweakcn: {name}",
      importHint: "Import a tweakcn theme into this browser-local slot",
      importFromTweakcn: "Import from tweakcn",
      tweakcnInstructions:
        "Open tweakcn.com, choose or create a theme, click Share, then paste the copied theme link here. Share links, editor URLs, registry URLs, theme IDs, and default theme names like amethyst-haze are accepted.",
      browseTweakcn: "Browse tweakcn themes",
      themeLink: "Theme link or ID",
      replace: "Replace {name}",
      importTheme: "Import theme",
      clear: "Clear {name}",
      loaded: "Loaded",
      inlineHintBefore: "Click",
      inlineHintAfter:
        "to add one browser-local tweakcn theme. In tweakcn, use Share and paste the copied link here.",
      textSize: "Text size",
    },
    chatPrefs: {
      title: "Chat",
      messageWidth: "Message width",
      messageWidthHint:
        "Optional CSS width for the centered transcript, such as 960px, 82%, or min(1280px, 82%).",
      messageWidthInvalid:
        "Enter a CSS width such as 960px, 82%, min(1280px, 82%), or calc(100% - 2rem).",
    },
    sidebarPrefs: {
      title: "Sidebar",
      hint: "Choose what appears while sessions are running.",
      liveActivity: "Show live agent activity in sidebar",
      liveActivityHint: "Show the latest assistant or tool activity beneath running sessions.",
    },
    sessionObserver: {
      title: "Session observer",
      hint: "Summarize long-running sessions with a small utility model.",
      toggle: "Session observer",
      toggleHint: "Generate live status digests for subscribed Control UI sessions.",
      resolvedModel: "Resolved small model",
      modelAuto: "auto ({model})",
      modelConfigured: "configured ({model})",
      modelDisabled: "disabled",
      modelUnavailable: "unavailable (no small model)",
      modelPicker: "Small model",
      modelPickerHint: "Used for observer digests and other short utility tasks.",
      modelCatalogUnavailable: "Explicit model catalog unavailable",
      auto: "Auto (provider default)",
      disabled: "Disabled",
      toggleNote: "Update the Control UI session observer",
      modelNote: "Update the default utility model",
    },
    connection: {
      title: "Connection",
      gateway: "Gateway",
      status: "Status",
      assistant: "Assistant",
    },
    root: "<root>",
    itemCount: "[{count} item]",
    itemCountPlural: "[{count} items]",
    form: "Form",
    raw: "Raw",
    formUnsafeTitle: "Form view can't safely edit some fields",
    rawTitle: "Edit raw JSON/JSON5 config",
    rawUnavailableTitle: "Raw mode unavailable for this snapshot",
    open: "Open",
    applying: "Applying…",
    autoSaveSaving: "Saving…",
    autoSaveSaved: "Saved",
    autoSaveFailed: "Save failed",
    autoSaveConflict: "Settings changed elsewhere",
    retry: "Retry",
    applyChanges: "Apply changes",
    rawDiscard: "Discard",
    rawDraftBlocksApply:
      "Unsaved raw config edits — save or discard them in the Raw editor before restarting.",
    rawDraftPendingFormTitle:
      "Unsaved raw config edits — save or discard them before switching to Form.",
    rawDraftBlocksFormEdit:
      "Unsaved raw config edits could not be parsed; resolve them in the Raw editor before changing settings.",
    invalidConfig: "Your configuration is invalid. Some settings may not work as expected.",
    dismissWarning: "Don't remind again",
    viewPendingChangesRaw: "View pending changes",
    rawDiffUnavailable: "Changes detected (JSON diff not available)",
    hideEnvValues: "Hide env values",
    revealEnvValues: "Reveal env values",
    peek: "Peek",
    loadingSchema: "Loading schema…",
    formUnsafeCount: "1 setting in this config can only be edited as text: {paths}",
    formUnsafeCountPlural: "{count} settings in this config can only be edited as text: {paths}",
    formUnsafeMore: "+{count} more",
    openRawEditor: "Open Raw editor",
    rawConfig: "Raw config (JSON/JSON5)",
    secretCount: "{count} secret",
    secretCountPlural: "{count} secrets",
    redacted: "redacted",
    visible: "visible",
    revealSensitive: "Reveal sensitive values",
    hideSensitive: "Hide sensitive values",
    toggleRawRedaction: "Toggle raw config redaction",
    sensitiveHidden:
      "{count} sensitive value hidden. Use the reveal button above to edit the raw config.",
    sensitiveHiddenPlural:
      "{count} sensitive values hidden. Use the reveal button above to edit the raw config.",
  },
  execApproval: {
    expiresIn: "expires in {time}",
    expired: "expired",
    execApprovalNeeded: "Exec approval needed",
    pluginApprovalNeeded: "Plugin approval needed",
    pending: "{count} pending",
    otherPending: "Other pending requests",
    reviewRequest: "Review approval from {agent}: {command}",
    agentPendingOne: "{count} pending approval",
    agentPending: "{count} pending approvals",
    allowOnce: "Allow once",
    alwaysAllow: "Always allow",
    allowAlwaysUnavailable: "Allow Always is unavailable for this command.",
    deny: "Deny",
    labels: {
      host: "Host",
      agent: "Agent",
      session: "Session",
      cwd: "CWD",
      resolved: "Resolved",
      security: "Security",
      ask: "Ask",
      severity: "Severity",
      plugin: "Plugin",
    },
  },
  approvalPage: {
    brandName: "OpenClaw",
    eyebrow: "Operator approval",
    loadingTitle: "Loading approval",
    loadingDescription: "Checking the current approval state with the Gateway.",
    unavailableTitle: "Approval unavailable",
    unavailableDescription:
      "This approval could not be found or this device is not authorized to review it.",
    connectionErrorTitle: "Connection interrupted",
    connectionErrorDescription:
      "OpenClaw cannot confirm or record a decision while disconnected. Reconnect to check the current status.",
    retry: "Retry",
    execTitle: "Command approval",
    pending: "Waiting for your decision",
    pendingDescription: "Review the request carefully. The first answer from any surface wins.",
    approvedHere: "Approved here",
    deniedHere: "Denied here",
    resolvedElsewhere: "Resolved elsewhere",
    resolvedElsewhereDescription:
      "Another surface or an earlier attempt recorded the decision first.",
    approved: "Approved",
    denied: "Denied",
    expired: "Expired",
    cancelled: "Cancelled",
    allowedOnceDescription: "The operation was approved for this request only.",
    allowedAlwaysDescription: "The operation was approved with the always-allow decision.",
    deniedDescription: "The operation was denied and will not continue.",
    expiredDescription: "No decision arrived before the deadline, so the operation was denied.",
    cancelledDescription: "The requesting run ended before a decision could be used.",
    summaryLabel: "Summary",
    commandLabel: "Command",
    requestLabel: "Request details",
    nodeLabel: "Node",
    toolLabel: "Tool",
    expiresLabel: "Expires",
    resolvedLabel: "Resolved",
    actionsLabel: "Approval decisions",
    resolvingDecision: "Recording {decision}…",
    safeToClose: "The decision is recorded. You can close this page.",
    openControlUi: "Open Control UI",
  },
  agentTools: {
    connectedSource: "Connected: {id}",
    connected: "Connected",
    channelSource: "Channel: {id}",
    channel: "Channel",
    builtIn: "Built-In",
    plugin: "Plugin: {id}",
    optional: "Optional",
    liveNow: "Live Now",
    disabledByOverride: "Disabled by agent override.",
    enabledByProfile: "Enabled by the current profile.",
    enabledByOverride: "Enabled by agent override.",
    notIncluded: "Not included in the current profile.",
    overrideOff: "Override Off",
    enabled: "Enabled",
    overrideOn: "Override On",
    profileOff: "Profile Off",
    notLive: "Not Live",
    otherAgent: "Other Agent",
    title: "Tool Access",
    subtitle: "Profile + per-tool overrides for this agent.",
    enabledSummary: "{enabled}/{total} enabled.",
    enableAll: "Enable All",
    disableAll: "Disable All",
    loadConfig: "Load the gateway config to adjust tool profiles.",
    explicitAllowlist:
      "This agent is using an explicit allowlist in config. Tool overrides are managed in the Config tab.",
    globalAllowlist:
      "Global tools.allow is set. Agent overrides cannot enable tools that are globally blocked.",
    loadingCatalog: "Loading runtime tool catalog…",
    catalogFallback: "Could not load runtime tool catalog. Showing built-in fallback list instead.",
    availableNow: "Available Right Now",
    availableNowSubtitle: "What this agent can use in the current chat session.",
    noSession: "no session",
    switchAgent: "Switch chat to this agent to view its live runtime tools.",
    loadingAvailable: "Loading available tools…",
    availableError: "Could not load available tools for this session.",
    noAvailable: "No tools are available for this session right now.",
    moreLiveTitle: "{count} more live tools are available in the groups below.",
    moreLive: "+{count} more live tools",
    quickPresets: "Quick Presets",
    catalogTitle: "Tool Catalog",
    inherit: "Inherit",
    profile: "Profile",
    source: "Source",
    live: "Live",
    status: "Status",
    profileSourceAgent: "agent override",
    profileSourceGlobal: "global default",
    profileSourceDefault: "default",
    statusSaving: "saving…",
    statusUnsaved: "unsaved",
    statusSaved: "saved",
    toolPreview: "Tool preview",
    more: "+{count} more",
    toolsOne: "{count} Tool",
    tools: "{count} Tools",
    enabledToolsOne: "{count} Enabled Tool",
    enabledTools: "{count} Enabled Tools",
    liveToolsOne: "{count} Live Tool",
    liveTools: "{count} Live Tools",
    access: "Access",
    session: "Current Session",
    disableNamed: "Disable {name}",
    enableNamed: "Enable {name}",
    defaultPresets: "Default Presets",
    availableVia: "Available now via {source}.",
    unavailableSession: "Not available in this chat session right now.",
    inspectAgent: "Switch chat to this agent to inspect live availability.",
    linkTool: "Link to This Tool",
  },
  skillGroups: {
    workspace: "Workspace Skills",
    builtIn: "Built-in Skills",
    installed: "Installed Skills",
    extra: "Extra Skills",
    other: "Other Skills",
  },
  skillsPage: {
    title: "Skills",
    tabs: {
      all: "All",
      ready: "Ready",
      needsSetup: "Needs Setup",
      disabled: "Disabled",
    },
    defaultAgent: "{name} (default)",
    filterPlaceholder: "Filter installed skills",
    shown: "{count} shown",
    clawHub: "ClawHub",
    clawHubSubtitle: "Search and install skills from the registry",
    searchClawHub: "Search ClawHub skills…",
    searching: "Searching…",
    acknowledgeRisk: "Acknowledge risk and install",
    disconnected: "Not connected to gateway.",
    empty: "No skills found.",
    noClawHubResults: "No skills found on ClawHub.",
    install: "Install",
    installing: "Installing…",
    close: "Close",
    by: "By",
    latest: "Latest: v{version}",
    platforms: "Platforms: {platforms}",
    installNamed: "Install {name}",
    notFound: "Skill not found.",
    openDetails: "Open {name} details",
    enabledNamed: "{name} enabled",
    invalidLink: "ClawHub link invalid",
    overview: "Overview",
    skillCard: "Skill Card",
    missingRequirements: "Missing requirements",
    reason: "Reason: {reasons}",
    disabled: "Disabled",
    enabled: "Enabled",
    apiKey: "API key",
    getKey: "Get your key:",
    saveKey: "Save key",
    source: "Source:",
    refreshing: "Refreshing…",
    fullSecurityReport: "Full security report",
    loadingSkillCard: "Loading Skill Card…",
    skillCardNotLoaded: "Skill Card not loaded.",
    verdict: {
      unavailable: "Unavailable",
      clean: "Clean",
      pending: "Pending",
      blocked: "Blocked",
      review: "Review",
    },
  },
  skillStatus: {
    bundled: "bundled",
    eligible: "eligible",
    blocked: "blocked",
    disabled: "disabled",
    blockedAllowlist: "blocked by allowlist",
    blockedAgentFilter: "blocked by agent filter",
  },
  nav: {
    account: "Account",
    back: "Back",
    forward: "Forward",
    settings: "Settings",
    askOpenClaw: "Ask OpenClaw",
    settingsGroupConnections: "Connections",
    settingsGroupAgents: "Agents & Tools",
    settingsGroupSecurity: "Privacy & Security",
    settingsGroupSystem: "System",
    settingsSearchLabel: "Search settings",
    settingsSearchPlaceholder: "Search settings…",
    settingsSearchNoResults: "No matching settings.",
    settingsSearchClear: "Clear settings search",
    exitSettings: "Back to app",
    expand: "Expand sidebar",
    collapse: "Collapse sidebar",
    resize: "Resize sidebar",
    more: "More",
    home: "Home",
    pages: "Pages",
    customize: "Edit pinned items",
    customizeReset: "Reset pinned items",
    workboardGroup: "WorkBoard",
  },
  terminal: {
    title: "Terminal",
    toggle: "Toggle terminal",
    open: "Open terminal",
    hide: "Hide terminal",
    resize: "Resize terminal panel",
    newSession: "New terminal session",
    addFiles: "Add files to terminal",
    dropFiles: "Drop files to add them",
    uploadProgress: "Uploading {current} of {total}",
    uploadFailed: "Upload failed",
    retryUpload: "Retry",
    closeSession: "Close terminal session",
    sessions: "Terminal sessions",
    refreshSessions: "Refresh",
    loadingSessions: "Loading sessions…",
    noSessions: "No live terminal sessions",
    currentSession: "current",
    sessionAttached: "attached",
    attachSession: "Attach session",
    attachFailed: "Could not attach terminal session",
    connecting: "Connecting to session…",
    connectionTimedOut: "Session did not connect within 30 seconds.",
    tabLabel: "shell {n}",
    tabHint: "{agent} · {cwd}",
    agentOwnedBadge: "agent",
    exited: "exited",
    exitedCode: "exited ({code})",
    detached: "detached",
    dockBottom: "Dock to bottom",
    dockRight: "Dock to right",
    dockMain: "Fill main content area",
    dockMode: "Terminal panel position",
    unavailable: "The terminal is not available on this gateway.",
    uploadTooLarge: "File exceeds the 16 MiB terminal upload limit: {file}",
    uploadUnsafeCmdPath: "Cannot safely insert an uploaded path containing % or ! into cmd.exe",
    uploadUnsupportedShell: "Cannot safely insert an uploaded path into unsupported shell: {shell}",
  },
  browser: {
    title: "Browser",
    toggle: "Toggle browser panel",
    hide: "Hide browser panel",
    resize: "Resize browser panel",
    newTab: "New tab",
    closeTab: "Close tab",
    untitledTab: "New tab",
    back: "Back",
    forward: "Forward",
    reload: "Reload",
    openExternal: "Open in your browser",
    urlPlaceholder: "Enter a URL and press Enter",
    dockBottom: "Dock to bottom",
    dockRight: "Dock to right",
    annotate: "Annotate page",
    inspect: "Inspect element",
    annotateSend: "Send to chat",
    annotateUndo: "Undo",
    annotateClear: "Clear",
    annotateDone: "Exit annotate mode",
    annotateHint: "Draw on the page, then send the markup to your chat.",
    inspectName: "Name",
    inspectRole: "Role",
    inspectFocusable: "Focusable",
    loading: "Loading page…",
    notRunning: "The gateway browser is not running.",
    start: "Start browser",
    empty: "No open tabs. Enter a URL above to browse.",
    noChatTarget: "Open a chat session first so the annotation has somewhere to go.",
    inspectUnavailable: "Element inspection is disabled (browser.evaluateEnabled=false).",
    annotationSent: "Annotation added to the chat composer.",
    errors: {
      requestFailed: "Browser request failed: {error}",
      screenshotPathMissing: "Browser screenshot did not return a media path.",
      screenshotFetchTimedOut: "Screenshot fetch timed out.",
      screenshotFetchFailed: "Screenshot fetch failed ({status}).",
      screenshotReadFailed: "Screenshot read failed.",
      screenshotDecodeFailed: "Screenshot decode failed.",
      canvasUnavailable: "Canvas 2D context unavailable.",
    },
    annotatePrompt: {
      // introTitled/elementDetail (not intro/element): translated keys never
      // retranslate on source-wording changes, so the provenance-label rewrite
      // required fresh key names to propagate to all locales.
      introTitled:
        'I annotated the page at {url} (page-reported title: "{title}") — the attached screenshot shows my markup.',
      introUntitled: "I annotated the page at {url} — the attached screenshot shows my markup.",
      region:
        "Marked region {index}: centered around {x}% across / {y}% down, spanning about {width}% × {height}% of the view.",
      moreRegions: "…plus {count} more marked region(s), all visible in the screenshot.",
      elementDetail:
        "Marked element (page-reported): {descriptor} — {width}×{height}px at ({x}, {y}).",
      outro: "Please look at the marked area and tell me what you make of it.",
    },
  },
  routeTitles: {
    modelProviders: "Models",
    notifications: "Notifications",
    advanced: "Advanced",
  },
  tabs: {
    agents: "Agents",
    activity: "Activity",
    apps: "Apps",
    approvals: "Approvals",
    workboard: "Workboard",
    worktrees: "Worktrees",
    channels: "Channels",
    connection: "Gateway",
    sessions: "Sessions",
    usage: "Usage",
    cron: "Automations",
    tasks: "Tasks",
    skills: "Skills",
    plugins: "Plugins",
    skillWorkshop: "Skill Workshop",
    devices: "Devices",
    chat: "Chat",
    dashboards: "Dashboards",
    custodian: "OpenClaw",
    profile: "Profile",
    communications: "Communications",
    appearance: "Appearance",
    lobsterdex: "Lobsterdex",
    automation: "Automation",
    mcp: "MCP",
    memory: "Memory",
    talk: "Talk",
    infrastructure: "Infrastructure",
    labs: "Labs",
    updates: "Updates",
    about: "About",
    aiAgents: "Agent Defaults",
    modelSetup: "Model Setup",
    memoryImport: "Import Memory",
    security: "Privacy & Security",
    debug: "Debug",
    logs: "Logs",
    plugin: "Plugin",
  },
  subtitles: {
    agents: "Workspaces, tools, identities.",
    activity: "Browser-local tool activity summaries.",
    apps: "Companion apps for phone, watch, desktop, and browser.",
    approvals: "Recent exec, plugin, and system-agent approvals.",
    workboard: "Agent work queue and session handoff.",
    worktrees: "Isolated agent task checkouts and recovery snapshots.",
    channels: "Channels and settings.",
    connection: "Gateway endpoint, credentials, and handshake status.",
    sessions: "Active sessions and defaults.",
    usage: "API usage and costs.",
    cron: "Scheduled tasks and recurring agent runs.",
    tasks: "Background tasks: subagents, automation runs, CLI.",
    skills: "Skills and API keys.",
    plugins: "Install and manage optional capabilities.",
    skillWorkshop: "Review, refine, and apply proposals before they become live skills.",
    devices: "Paired devices, pairing approvals, and exec bindings.",
    chat: "Gateway chat for quick interventions.",
    dashboards: "Sessions that open on their dashboard face.",
    custodian: "System setup and care.",
    config: "Legacy settings route; opens Appearance.",
    profile: "Your display name, avatar, and identity on this gateway.",
    communications: "Messages and text-to-speech settings.",
    appearance: "Theme, UI, and setup wizard settings.",
    lobsterdex: "Every lobster palette that has visited this browser.",
    automation: "Commands, hooks, automations, and plugins.",
    mcp: "MCP servers, auth, tools, and diagnostics.",
    memory: "Memory engine, backend, search, and dreaming.",
    talk: "Realtime voice: provider, model, and speaker voice.",
    infrastructure: "Gateway, browser, node host, discovery, and ACP settings.",
    labs: "Experimental agent and tool capabilities.",
    updates: "Release channel, automatic updates, and current update status.",
    about: "Control UI and connected Gateway build identity.",
    aiAgents: "Global agent defaults: skills, tools, and session.",
    modelSetup: "Connect a verified AI model",
    modelProviders: "Default models, behavior, provider access, usage, and cost.",
    memoryImport: "Bring Codex and Claude Code memory into an agent workspace.",
    notifications: "Browser push notifications from your gateway.",
    security: "Gateway auth, exec policy, tool profile, and approvals.",
    advanced: "Every remaining config section, plus the raw file editor.",
    debug: "Snapshots, events, RPC.",
    logs: "Live gateway logs.",
    plugin: "Plugin-provided panel.",
  },
  modelSetup: {
    heading: "Connect a verified AI model",
    intro:
      "OpenClaw checks the AI access available on this Gateway and verifies the exact model before it enables conversations.",
    required: {
      title: "No AI provider configured",
      body: "We couldn't find a provider and model configured for this agent. Choose a supported connection; OpenClaw will test it before enabling chat.",
      action: "Connect an AI provider",
    },
    connectionFailure: {
      title: "Configured AI needs attention",
      body: "OpenClaw found the provider and model selected for this agent, but the live check failed. Your configuration is still intact. Review the credential, model access, or provider status, then verify again.",
      action: "Review connection",
    },
    loading: "Checking this Gateway for available AI access…",
    retry: "Retry",
    checkAgain: "Check again",
    verify: {
      title: "Selected model",
      button: "Check model",
      retry: "Try again",
      checkAgain: "Check again",
      checkingButton: "Checking…",
      checking: "Checking — asking {modelRef} for a quick reply…",
      ready: "Ready",
      readyIn: "Ready · {latencyMs} ms",
      providerUnavailable: "{provider} isn’t responding.",
    },
    access: {
      adminRequired: "Model setup requires operator.admin access.",
      gatewayTooOld: "The Gateway is running an older OpenClaw version",
    },
    candidates: {
      title: "Found on this Gateway",
      recommended: "Recommended",
      credentialsReady: "Credentials ready",
      signInNeeded: "Sign-in needed",
      detected: "Detected",
      testAndUse: "Test & use",
      retry: "Retry test",
      testingButton: "Testing…",
      testing: "Testing — asking {modelRef} for a quick reply…",
    },
    empty: {
      title: "Recommended installs",
      intro: "No existing AI access was detected. Install one of these tools, then check again.",
    },
    unavailable: {
      title: "Found, but needs attention",
      signIn: "Sign in with {provider}",
      useApiKey: "Use API key",
    },
    signIn: {
      title: "Sign in with a provider",
      signIn: "Sign in",
      pair: "Pair",
      more: "More sign-in options",
    },
    prepare: {
      title: "Run a model locally",
      intro: "Use a local model service, or run a private GGUF model directly inside this Gateway.",
      ollamaButton: "Check & set up",
      ollamaLabel: "Ollama",
      ollamaHint: "Connect to the Ollama service on this Gateway and prepare a tools-capable model",
      llamaCppLabel: "llama.cpp",
      providerNotReady:
        "{provider} did not expose a usable local model. Review the setup result, then retry.",
    },
    manual: {
      title: "Connect with an API key or token",
      provider: "Provider and access method",
      selectProvider: "Select a provider",
      selectProviderHint: "Choose where this credential comes from",
      accessValue: "API key or token",
      accessValueFor: "{provider} API key or token",
      accessValuePlaceholder: "Paste an API key or token",
      connect: "Connect",
      connectAndVerify: "Connect & verify",
      verifyHint: "OpenClaw verifies a real model reply before marking the connection ready.",
      required: "Choose a provider and enter an API key or token.",
    },
    success: {
      title: "Connection verified",
      body: "OpenClaw received a real reply from {modelRef}. You can start chatting now.",
      activeModel: "Active model",
      latency: "Verified in {latencyMs} ms",
      openChat: "Start chatting",
      continueSetup: "Continue setup",
      stayHere: "Stay in settings",
      configuredModel: "Configured model",
    },
    failure: {
      auth: "Authentication failed",
      rateLimit: "Rate limited",
      billing: "Billing problem",
      timeout: "Timed out",
      format: "Invalid response",
      unavailable: "Unavailable",
      unknown: "Connection failed",
    },
    failureGuidance: {
      auth: "Review the provider credential or sign-in, then retry.",
      rateLimit: "Wait for the provider limit to reset, then retry.",
      billing: "Restore provider billing or quota, then retry.",
      unavailable: "Make sure the provider service is running and reachable, then retry.",
      format: "Check that the endpoint exposes a compatible chat model, then retry.",
      unknown: "Review the connection details, then retry.",
    },
    errors: {
      requestFailed: "Model setup request failed.",
      activationFailed: "The model could not be activated.",
    },
    wizard: {
      dialogLabel: "Provider sign-in",
      prepareDialogLabel: "Local model setup",
      title: "Sign in with a provider",
      prepareTitle: "Set up a local model",
      starting: "Starting provider sign-in…",
      prepareStarting: "Starting local model setup…",
      checking: "Checking your model setup…",
      working: "Working…",
      continue: "Continue",
      submit: "Submit",
      openSignIn: "Open sign-in page",
      copy: "Copy",
      expires: "Expires in {count} minutes",
      cancelled: "Provider sign-in was cancelled.",
      sessionExpired:
        "This setup session expired after the Gateway restarted. Close this dialog, then start model setup again.",
      notComplete: "Sign-in finished, but model setup is not complete yet.",
    },
  },
  memoryImport: {
    unknownCollection: "Memory collection",
    claudeCode: "Claude Code",
    fileCountOne: "{count} file",
    fileCount: "{count} files",
    alreadyImported: "{count} existing",
    ready: "Ready",
    existing: "Existing",
    importComplete: "Import complete",
    importIncomplete: "Import incomplete",
    importedCount: "{count} files imported",
    importedWithIssues: "{migrated} imported · {errors} failed · {conflicts} conflicts",
    reportSaved: "report saved",
    recoveryFile: "Recovery file",
    recoveryJournal: "Recovery journal",
    itemBackup: "Item backup",
    codexDescription: "Consolidated Codex memory files.",
    claudeDescription: "Claude Code per-project auto-memory files.",
    providerFallback: "Import assistant memory into this agent workspace.",
    reviewFiles: "Review files",
    notFound: "Not found",
    noMemoryFound: "No importable memory found on this computer.",
    source: "Source",
    destination: "Destination",
    selectedCount: "{count} selected",
    selectAtLeastOne: "Select at least one file",
    importSelected: "Import selected",
    confirmTitle: "Import from {provider}?",
    confirmDescription: "Copy {count} selected memory files into this agent workspace.",
    confirmReplace:
      "Existing destination files will be backed up in the migration report before replacement.",
    confirmBackup: "OpenClaw creates a verified pre-import backup before copying memory.",
    confirmImport: "Import memory",
    disconnected: "Connect to the gateway to import memory.",
    title: "Import assistant memory",
    subtitle:
      "Review Codex consolidated memory and Claude Code auto-memory before copying it into OpenClaw.",
    agent: "Destination agent",
    replaceExisting: "Replace existing imports",
    replaceHint: "Preview conflicts again and preserve item backups before replacement.",
    backfill: {
      title: "From past sessions",
      subtitle:
        "Stage trusted memories from earlier agent sessions. Dreaming promotes the useful ones into long-term memory.",
      dateRange: "Session date range",
      dateRangeHint: "Leave either date blank to scan the full available range.",
      from: "From",
      to: "To",
      actions: "Backfill",
      preview: "Preview",
      previewing: "Previewing…",
      apply: "Apply",
      applying: "Applying…",
      rollback: "Rollback",
      previewSummary: "{candidates} candidates across {days} days",
      previewTruncated:
        "This preview shows the first bounded batch. Apply continues through the remaining candidates.",
      candidateCount: "{count} candidates",
      noCandidates: "No new trusted session candidates were found.",
      progress: "Processed {days} days · {staged} staged",
      processedCandidates: "{count} session candidates processed",
      processedDayCountOne: "{count} day processed",
      processedDayCount: "{count} days processed",
      complete: "{count} staged; promotion happens via dreaming",
      rollbackConfirmTitle: "Rollback session backfill?",
      rollbackConfirmDescription:
        "Remove diary entries and staged memories created by session backfill for this agent.",
      rollbackWarning:
        "Session backfill cursors are rewound, so the same candidates can be staged again.",
      rollbackComplete: "Session backfill rolled back",
      rollbackCounts: "{diary} diary entries and {staged} staged entries removed",
      unavailable: "Session backfill is unavailable on this Gateway.",
    },
  },
  onboarding: {
    memoryImport: {
      title: "Bring your assistant memory with you",
      body: "OpenClaw found memory from other coding assistants. Import it into your agent workspace?",
      plannedCount: "{count} ready to import",
      alreadyImported: "Already imported: {count}",
      sourceUnavailable: "Source path unavailable",
      import: "Import",
      skip: "Skip",
      reviewDetails: "Review details",
      importingProvider: "Importing…",
      providerResult: "Migrated {migrated}, skipped {skipped}",
      providerIncomplete:
        "Migrated {migrated}, skipped {skipped}, {errors} failed, {conflicts} conflicts",
      providerError: "Import failed: {error}",
      connectionChanged: "Skipped: the Gateway connection changed during the import",
      unknownError: "Request failed",
      doneTitle: "Memory import finished",
      doneBody: "Migrated {migrated}, skipped {skipped}. You can continue setting up OpenClaw.",
    },
  },
  custodian: {
    title: "OpenClaw",
    subtitle: "Your system setup guide",
    subtitleCaretaker: "System setup and care.",
    exitSetup: "Exit setup",
    newAgent: "New agent",
    hatchDraft: "Wake up, my friend!",
    placeholder: "Message OpenClaw…",
    sensitivePlaceholder: "Enter sensitive value…",
    sensitiveReply: "Sensitive reply sent",
    send: "Send",
    thinking: "OpenClaw is thinking",
    earlier: "Earlier",
    requestFailed: "OpenClaw could not reply. Try again.",
    connectionChanged: "The Gateway connection changed. Retry to continue this setup.",
    sessionRestarted:
      "{error} OpenClaw started a fresh session; earlier messages remain for context.",
    unsupportedGateway: "Update the Gateway to continue setup with OpenClaw.",
    panel: {
      title: "OpenClaw",
      toggle: "Ask OpenClaw",
      close: "Close Ask OpenClaw",
      resize: "Resize Ask OpenClaw",
      dockBottom: "Dock Ask OpenClaw at bottom",
      dockRight: "Dock Ask OpenClaw at right",
    },
    history: {
      button: "History",
      title: "Recent changes",
      description: "What changed on this system, newest first.",
      loading: "Loading recent changes…",
      empty: "No recorded changes yet.",
      requestFailed: "OpenClaw could not load change history.",
      loadMore: "Load more",
      loadingMore: "Loading more…",
      changedPaths: "Changed paths ({count})",
      invalidEdit: "This manual edit did not pass configuration validation.",
      opaqueChange: "Formatting or comments changed without visible configuration path changes.",
      sources: {
        systemAgent: "system-agent",
        doctor: "doctor",
        settings: "settings",
        cli: "CLI",
        pluginInstall: "plugin install",
        manualEdit: "manual edit",
        unknown: "unknown",
      },
    },
    nudge: {
      configReload: "Configuration reload stopped — ask me what happened",
      channelAuth: "{channel} authentication degraded — ask me what happened",
      channelDisconnected: "{channel} just disconnected — ask me what happened",
      channelDegraded: "{channel} is degraded — ask me what happened",
      channelFallback: "A channel",
      dismiss: "Dismiss this update",
      channelSetupTitle: "Reach OpenClaw outside this app",
      channelSetupBody:
        "The web app already works. Add a channel only if you want to message OpenClaw from another service.",
      channelSetupAction: "Set up a channel",
      channelSetupDismiss: "Keep using the web app",
    },
  },
  mcpServers: {
    add: "Add server",
    adding: "Adding…",
    nameLabel: "Name",
    transportLabel: "Transport",
    transportStreamableHttp: "Streamable HTTP",
    transportSse: "SSE",
    transportStdio: "Stdio",
    targetLabel: "URL or command",
    nameInvalid: "Server names use letters, numbers, dots, dashes, or underscores.",
    targetInvalid: "Enter a URL for HTTP transports or a valid command line for stdio.",
    sessionEnableFailed:
      "The server was saved disabled globally, but enabling it for this session failed: {error}",
    sessionChanged: "The active session changed before it could be enabled.",
    sessionUnavailable: "The active session is unavailable; refresh and try again.",
    nameTaken: "An MCP server named “{name}” already exists.",
    missing: "MCP server “{name}” was not found in the configuration.",
    missingTransport: "missing transport",
    addedSuccess: "Added MCP server {name}.",
    enabledSuccess: "Enabled MCP server {name}.",
    disabledSuccess: "Disabled MCP server {name}.",
    removedSuccess: "Removed MCP server {name}.",
    configUnavailable: "Configuration is unavailable; refresh and try again.",
    connectRequired: "Connect to the gateway to change MCP servers.",
    adminRequired: "MCP server changes require operator.admin access.",
    enable: "Enable",
    disable: "Disable",
    removeNamed: "Remove {name}",
    working: "Working…",
  },
  mcpPage: {
    intro: "Connect and manage MCP servers that provide tools to OpenClaw.",
    connectorsLink: "Discover one-click connectors on the Plugins page.",
    servers: "Servers",
    oauth: "OAuth",
    filtered: "Filtered",
    configuredServers: "Configured servers",
    noServers: "No MCP servers configured.",
    setUpFirstServer: "Set up your first MCP server",
    operatorCommands: "MCP operator commands",
    operatorCommandsHint: "Status, diagnostics, auth, probing, and runtime reload.",
    runtimeHint:
      "Edits save automatically; runtime changes apply after a gateway restart, and active agents rebuild MCP runtimes on next use.",
    toolFilter: "tool filter",
    parallel: "parallel",
    tlsVerifyOff: "TLS verify off",
    mtls: "mTLS",
  },
  talkPage: {
    intro: "Configure realtime voice providers, models, and speaker voices.",
    voiceSection: {
      title: "Realtime voice",
      description:
        "Continuous speech conversations with your agent. The pickers below write talk.realtime settings; the full form further down covers everything else.",
    },
    status: {
      title: "Status",
      ready: "Ready",
      notReady: "Not configured",
      unavailable: "Unavailable",
      unavailableHint: "Connect to the Gateway to check realtime voice readiness.",
      activeProvider: "Active provider: {provider}",
      noProvider: "No realtime voice provider is configured yet.",
    },
    provider: {
      title: "Provider",
      description: "Auto picks the first provider with working credentials.",
      auto: "Auto",
    },
    model: {
      title: "Model",
      description: "Realtime voice model for browser Talk sessions.",
      default: "Provider default",
      defaultNamed: "Default ({model})",
    },
    voice: {
      title: "Speaker voice",
      description: "Voice used for spoken replies. GPT-Live locks the voice once a call starts.",
      default: "Provider default",
    },
    gptLive: {
      title: "GPT-Live",
      hint: "GPT-Live works with a ChatGPT subscription: sign in once with “openclaw models auth login --provider openai”. No Platform API key needed. Browser Talk only. Delegated work can be steered while running and requires exact spoken confirmation for high-impact actions.",
      ready: "Ready",
    },
  },
  memoryPage: {
    intro: "Choose how OpenClaw stores, searches, and maintains agent memory.",
    tablistLabel: "Memory sections",
    tabs: {
      overview: "Overview",
      memories: "Memories",
      dreams: "Dreams",
      settings: "Settings",
    },
    overview: {
      hero: {
        awake: "Memory is awake",
        waking: "Waking memory…",
        hibernating: "Memory is hibernating",
        needsAttention: "Memory needs attention",
        activeDescription: "{engine} · {mode}",
        loadingDescription: "Checking this agent's memory engine and dream cycle.",
        offDescription: "Choose a memory engine in Settings to wake it up.",
        disabledDescription: "The selected memory engine is disabled. Re-enable it in Settings.",
        gatewayOffline: "The gateway is offline, so memory status is unavailable.",
        hybridSearch: "hybrid search",
        keywordSearch: "keyword search (no embeddings)",
        openSettings: "Open Settings",
        retry: "Retry",
        refresh: "Refresh status",
      },
      schedule: {
        title: "Sleep schedule",
        lightDescription:
          "Sorts fresh short-term notes and stages promising candidates without changing long-term memory.",
        remDescription:
          "Reflects on themes and recurring ideas across recent activity to strengthen ranking without changing long-term memory.",
        deepDescription:
          "Scores staged candidates, promotes the keepers into long-term memory (MEMORY.md), and writes the dream diary.",
        nextRun: "next {time}",
        lastRun: "last {time}",
        notScheduled: "Not scheduled",
        learnMore: "How dreaming works",
        openDocs: "Open dreaming guide",
      },
      activity: {
        title: "Activity",
        promotedToday: "Promoted today",
        promotedTotal: "Promoted total",
        shortTermCount: "Pending short-term entries",
        phaseHitCount: "Phase signals",
        lightPhaseHitCount: "Light-phase hits",
        remPhaseHitCount: "REM-phase hits",
      },
      health: {
        title: "Engine health",
        provider: "Provider",
        embeddings: "Embeddings",
        runtime: "Embedding runtime",
        healthy: "Ready",
        unavailable: "Unavailable",
        notChecked: "Not checked",
        notCheckedDescription: "Embedding readiness has not been checked yet.",
        checking: "Checking…",
        test: "Test",
        testing: "Testing…",
      },
      shortcuts: {
        title: "Explore memory",
        memories: "Search memories",
        diary: "Read the dream diary",
        settings: "Configure memory",
      },
    },
    memories: {
      searchLabel: "Search memories",
      searchPlaceholder: "Search this agent's memories",
      searchButton: "Search",
      idle: "Search for a person, project, decision, or anything else this agent remembers.",
      searching: "Searching memories…",
      results: "{count} results",
      empty: "No memories matched “{query}”.",
      error: "Memory search failed: {message}",
      retry: "Retry",
      gatewayUpdateRequired: "Update the gateway to search memories from the Control UI.",
      hybridSearch: "hybrid search",
      keywordSearch: "keyword search",
      lineRange: "lines {start}–{end}",
      score: "score {score}",
      sourceMemory: "memory",
      sourceSessions: "sessions",
      fileLoading: "Loading the full memory file…",
      fileError: "Could not load this memory file: {message}",
      fileUnsupported: "This memory file cannot be shown as text.",
    },
    engine: {
      title: "Engine",
      description:
        "Exactly one memory plugin owns the memory slot. Selecting an engine enables it and disables the others.",
      rowTitle: "Memory engine",
      openClawMemory: "OpenClaw Memory",
      off: "Off",
      unavailable: "Unavailable",
      autoHint: "No engine is pinned in config, so the slot falls back to its default owner.",
      explicitHint: "This engine is pinned in config under plugins.slots.memory.",
      offHint: "Memory is switched off in config: plugins.slots.memory is set to none.",
      catalogUnavailable: "Connect to the gateway to change the memory engine.",
      changeFailed: "Could not change the memory engine",
      disabledTitle: "This engine is disabled",
      disabledHint:
        "The memory slot points at this plugin, but the plugin itself is disabled, so memory is not running.",
      enable: "Enable",
    },
    backend: {
      title: "Backend",
      description: "How memory is stored and retrieved for the selected engine.",
      rowTitle: "Retrieval backend",
      builtin: "Built-in",
      qmd: "QMD",
      invalid: "Invalid configured value",
      builtinHint: "Memory files are indexed and searched by OpenClaw itself.",
      qmdHint: "Retrieval is delegated to QMD. Its settings appear below.",
      invalidHint:
        "The configured memory.backend value is invalid. Choose a backend or reset it to use the default.",
    },
    addons: {
      title: "Add-ons",
      description:
        "These plugins layer on top of the engine instead of competing for the slot, so any combination can run at once.",
      activeMemory: {
        title: "Active memory",
      },
      memoryWiki: {
        title: "Memory wiki",
      },
      toggleAriaLabel: "Enable or disable {plugin}",
      changeFailed: "Could not update {plugin}",
      stateUnknown: "Unknown",
      manage: "Enable or disable add-ons",
      manageLink: "Open Plugins",
    },
    import: {
      title: "Import",
      description: "Bring existing memory from other assistants into an agent workspace.",
      link: "Open Memory Import",
    },
    search: {
      intro: "Embedding and retrieval defaults shared by every agent that has no memory override.",
    },
    dreaming: {
      intro:
        "Dreaming runs as one managed automation across every agent workspace, so these settings are global. They are owned by the {plugin} plugin.",
      schedule: {
        title: "Schedule",
        description: "When the full sweep runs and which model narrates it.",
      },
      frequency: {
        label: "Dreaming frequency",
        help: "Cron cadence for the full dreaming sweep (light, REM, then deep). Leave empty for the plugin default.",
        placeholder: "0 3 * * *",
      },
      timezone: {
        label: "Timezone",
        help: "IANA timezone used to interpret the cron cadence.",
        placeholder: "Europe/Vienna",
        default: "Gateway local timezone",
      },
      model: {
        label: "Dreaming model",
        help: "Provider/model override for dream diary narration. Requires subagent model overrides to be allowed.",
        placeholder: "anthropic/claude-sonnet-4-6",
        default: "Agent model",
      },
      verboseLogging: {
        label: "Verbose logging",
        help: "Log each dreaming phase in detail. Useful when tuning thresholds.",
      },
      storage: {
        title: "Storage",
        description: "Where promoted memories and dreaming reports are written.",
        modeLabel: "Storage mode",
        modeHelp: "Inline writes into the memory file; separate keeps a dedicated report file.",
        modes: {
          inline: "Inline",
          separate: "Separate",
          both: "Both",
        },
        separateReportsLabel: "Separate reports",
        separateReportsHelp: "Keep dreaming reports out of the main memory file.",
      },
      phases: {
        light: {
          title: "Light phase",
          description: "Cheap recent-activity pass that stages replay candidates.",
        },
        deep: {
          title: "Deep phase",
          description: "Scored promotion pass that graduates short-term entries into memory.",
        },
        rem: {
          title: "REM phase",
          description: "Pattern pass that looks for recurring themes across the lookback window.",
        },
      },
      phaseFields: {
        enabled: "Enabled",
        enabledHelp: "Run this phase during the sweep.",
        lookbackDays: "Lookback days",
        lookbackDaysHelp: "How far back this phase reads. Leave empty for the plugin default.",
        limit: "Limit",
        limitHelp: "Maximum entries this phase processes per run.",
        dedupeSimilarity: "Dedupe similarity",
        dedupeSimilarityHelp: "Similarity above which two candidates are treated as duplicates.",
        minScore: "Minimum score",
        minScoreHelp: "Promotion score an entry must reach.",
        minRecallCount: "Minimum recalls",
        minRecallCountHelp: "How often an entry must be recalled before it can be promoted.",
        minUniqueQueries: "Minimum unique queries",
        minUniqueQueriesHelp: "How many distinct queries must have surfaced the entry.",
        recencyHalfLifeDays: "Recency half-life (days)",
        recencyHalfLifeDaysHelp: "How quickly older recall signals lose weight.",
        maxAgeDays: "Maximum age (days)",
        maxAgeDaysHelp: "Ignore short-term entries older than this.",
        maxPromotedSnippetTokens: "Max promoted snippet tokens",
        maxPromotedSnippetTokensHelp:
          "Token budget for each promoted snippet. Provenance stays attached.",
        minPatternStrength: "Minimum pattern strength",
        minPatternStrengthHelp: "Strength a recurring pattern must reach to be reported.",
      },
      agentScope: {
        rowTitle: "Agent",
      },
      unsupported: {
        title: "Dreaming settings",
        rowTitle: "Not available for this engine",
        description:
          "The {plugin} plugin owns the memory slot and its config schema has no dreaming section, so these settings cannot be stored. Switch the engine above to edit them.",
      },
    },
  },
  sessionsPage: {
    hubTablistLabel: "Session sections",
  },
  pluginsPage: {
    searchLabel: "Search plugins",
    searchPlaceholder: "Search plugins and ClawHub",
    browseClawHub: "Browse ClawHub",
    refresh: "Refresh",
    hubTablistLabel: "Plugins sections",
    installedTab: "Installed",
    discoverTab: "Discover",
    workshopTab: "Workshop",
    tryAgain: "Try again",
    loading: "Loading plugins…",
    searching: "Searching ClawHub…",
    fromClawHub: "From ClawHub",
    noClawHubResultsBody: "ClawHub has no results for “{query}”.",
    noDiscoverMatchTitle: "Nothing to discover matches",
    featuredGroup: "Featured",
    officialGroup: "Official plugins",
    connectorsGroup: "Connect your world",
    connectorsHint:
      "One-click MCP connectors and hand-picked ClawHub searches for popular services.",
    connectorGroupWork: "Work & productivity",
    connectorGroupDev: "Coding & infrastructure",
    connectorGroupHome: "Home & media",
    connectorGroupLife: "Everyday life",
    mcp: "MCP",
    oauth: "OAuth",
    connectorMcpNote: "One-click MCP server",
    connectorClawHubNote: "Community plugins on ClawHub",
    connectorAdd: "Add",
    connectorSearch: "Find on ClawHub",
    connectorAdded: "Added",
    connectorAddedOauth: "Added {name}. Authenticate with “{command}”, then restart the gateway.",
    connectorAddedEndpoint:
      "Added {name}. Update the endpoint and credentials in MCP settings before use.",
    connectorAddedReady: "Added {name}. New agent sessions can use it right away.",
    connectorDescriptions: {
      notion: "Search, create, and update pages and databases in your Notion workspace.",
      linear: "Triage issues, update cycles, and file bugs straight from chat.",
      todoist: "Read, add, and complete tasks and projects in Todoist.",
      airtable: "Query and update records, tables, and bases in Airtable.",
      jira: "Create, search, and triage Jira tickets from chat.",
      canva: "Create and edit Canva designs, manage assets, and export results.",
      stripe: "Check payments, customers, invoices, and subscriptions in your Stripe account.",
      googleCalendar: "Read, create, and get briefed on events — your agent owns your schedule.",
      email: "Mailbox triage, summaries, and drafts with send-on-approval.",
      pdf: "Extract, merge, convert, and OCR PDF documents.",
      transcription: "Turn audio and video into clean, structured transcripts.",
      github: "PR review queues, issue triage, and repo Q&A through the official GitHub MCP.",
      sentry: "Crash alerts explained and triaged the moment they fire.",
      context7: "Version-specific library docs and code examples while coding. No signup needed.",
      deepwiki: "Ask questions about any public GitHub repo. Free, no account needed.",
      huggingFace: "Search models, datasets, and papers; run Spaces as tools.",
      grafana: "Grafana know-how and community connectors for dashboards and alerts.",
      kubernetes: "Cluster operations and troubleshooting from chat.",
      homeAssistant: "Control lights, climate, and automations across your whole home.",
      spotify: "Search, queue, and soundtrack your day with mood-based playlists.",
      sonos: "Whole-home audio: play, group rooms, and queue by chat.",
      reddit: "Browse, search, and summarize subreddits and threads.",
      markets: "Live stocks and crypto with price alerts and daily digests.",
      travel: "Flight and hotel search with fare watching and trip memory.",
      news: "A personalized daily briefing: news, weather, and tasks in one message.",
      maps: "Places, routing, and travel-time answers.",
      translation: "Translate and localize text and documents.",
      notes: "Capture notes to Markdown, Obsidian, Notion, or Bear.",
    },
    noInstalledTitle: "No optional plugins installed",
    noInstalledBody: "Discover a featured plugin or search ClawHub to extend OpenClaw.",
    noInstalledMatchTitle: "No installed plugins match",
    noMatchBody: "Try a different search.",
    filterAll: "All",
    filterIssues: "Issues",
    filterLabel: "Filter installed plugins",
    categoryChannels: "Channels",
    categoryProviders: "Model providers",
    categoryMemory: "Memory",
    categoryContextEngine: "Context engines",
    categoryTools: "Tools",
    categoryOther: "Other",
    mcpServersGroup: "MCP servers",
    mcpSettingsLink: "MCP settings",
    mcpHint:
      "Connect Model Context Protocol servers to give your agent extra tools. Changes apply to new agent sessions.",
    mcpEmpty: "No MCP servers configured yet. Add one here or pick a connector from Discover.",
    remove: "Remove",
    removing: "Removing…",
    removeNamed: "Remove {name}",
    removeConfirm: "Remove this plugin?",
    cancel: "Cancel",
    removedRestart: "Removed {name}. A Gateway restart is required to apply the change.",
    verifiedSource: "Verified source",
    enableAction: "Enable",
    disableAction: "Disable",
    working: "Working…",
    detailClose: "Close",
    detailOrigin: "Source",
    detailCategory: "Category",
    detailPackage: "Package",
    detailPluginId: "Plugin ID",
    offlineTitle: "Gateway offline",
    offlineBody: "Connect to browse installed and recommended plugins.",
    optionalCapability: "Optional OpenClaw capability.",
    enabled: "Enabled",
    disabled: "Disabled",
    available: "Available",
    needsAttention: "Needs attention",
    included: "Included",
    global: "Global",
    workspace: "Workspace",
    config: "Config",
    official: "Official",
    codePlugin: "Code plugin",
    bundlePlugin: "Bundle plugin",
    unavailable: "Unavailable",
    install: "Install",
    installing: "Installing…",
    installNamed: "Install {name}",
    acknowledgeRisk: "Acknowledge risk and install",
    defaultRiskWarning: "Review the ClawHub warning before installing this plugin.",
    connectToChange: "Connect to the gateway to change plugins.",
    adminRequired: "Browsing only. Plugin changes require operator.admin access.",
    changesDisabled: "Browsing only. This gateway does not allow plugin changes.",
    configRefreshFailed: "Could not refresh Control UI configuration: {error}",
    installedSuccess: "Installed {name}.",
    installedRestart: "Installed {name}. A Gateway restart is required to apply the change.",
    enabledSuccess: "Enabled {name}.",
    enabledRestart: "Enabled {name}. A Gateway restart is required to apply the change.",
    disabledSuccess: "Disabled {name}.",
    disabledRestart: "Disabled {name}. A Gateway restart is required to apply the change.",
  },
  labsPage: {
    intro:
      "Labs contains experimental capabilities that may change, break, or disappear between releases.",
    sectionTitle: "Experimental features",
    sectionDescription: "Changes save immediately and apply to future agent runs.",
    documentation: "Documentation",
    restartRequired: "Gateway restart required.",
    saveErrorTitle: "Could not update feature",
    saveFailed: "The feature setting could not be saved.",
    codeMode: {
      title: "Code Mode",
      description:
        "Let agents combine tools in compact, sandboxed JavaScript workflows. Auto engages code mode only for models evaluated as strong code-mode performers.",
    },
    swarm: {
      title: "Swarm",
      description: "Let Code Mode orchestrate groups of subagents in parallel.",
      defaultPhase: "Unphased",
    },
    toolSearch: {
      title: "Tool Search",
      description:
        "Keep a bounded tool directory visible and defer the rest behind search, so large MCP and plugin catalogs stop crowding the prompt.",
    },
    loopDetection: {
      title: "Tool-loop detection",
      description:
        "Enable rolling-history guards that warn or block repeated tool calls when an agent stops making progress.",
    },
    localModelLean: {
      title: "Lean tools for local models",
      description:
        "Drop heavyweight default tools that smaller local models handle poorly, leaving a shorter set they can use reliably.",
    },
    auditMessages: {
      title: "Message audit metadata",
      description:
        "Record content-free metadata for direct conversations in the audit ledger. Message content is never stored.",
    },
  },
  aboutPage: {
    productName: "OpenClaw",
    tagline: "Your personal AI assistant, running on your own devices.",
    waveHello: "Wave hello to Clawd",
    linksLabel: "Community and resources",
    linkWebsite: "Website",
    linkDocs: "Docs",
    linkGitHub: "GitHub",
    linkDiscord: "Discord",
    linkX: "X (Twitter)",
    linkChangelog: "Changelog",
    license: "© 2026 OpenClaw Foundation — MIT License.",
    artifactTitle: "Control UI",
    artifactSubtitle: "Identity embedded when this browser artifact was built.",
    artifactDetails: "Control UI build details",
    version: "Version",
    commit: "Commit",
    branch: "Branch",
    built: "Built",
    unavailable: "Unavailable",
    copyCommit: "Copy full commit hash",
    copyingCommit: "Copying commit hash",
    copiedCommit: "Commit hash copied",
    copyCommitFailed: "Could not copy commit hash",
    gatewayVersion: "Connected Gateway version",
    gatewayVersionHint:
      "Reported by the active Gateway connection; separate from this Control UI build.",
  },
  appsPage: {
    heroTitle: "Take OpenClaw everywhere",
    heroTagline:
      "Companion apps for your phone, watch, desktop, and browser — plus plugins to extend what your agent can do.",
    sectionMobile: "On your phone",
    havePhone: "Already have the app?",
    pairDevice: "Pair your device",
    sectionWatch: "On your wrist",
    sectionDesktop: "On your desktop",
    sectionBrowser: "In your browser",
    sectionCommunity: "Community",
    badgeBundledIos: "Included with the iOS app",
    badgeBundledAndroid: "Included with the Android app",
    badgeStoreComingSoon: "Chrome Web Store · coming soon",
    ctaAppStore: "App Store",
    ctaPlayStore: "Google Play",
    ctaDownload: "Download",
    ctaDocs: "Docs",
    ctaSetupGuide: "Setup guide",
    ctaChromeWebStore: "Chrome Web Store",
    ctaOpenPlugins: "Open Plugins",
    ctaBrowseClawHub: "Browse ClawHub",
    linkDiscord: "Discord community",
    linkDocs: "Docs",
    cards: {
      ios: {
        title: "iPhone",
        desc: "Chat, talk, approve actions, and share into OpenClaw from iOS.",
      },
      android: {
        title: "Android",
        desc: "Your Android phone as a full OpenClaw device — chat, camera, and Canvas.",
      },
      appleWatch: {
        title: "Apple Watch",
        desc: "Glanceable chats and quick replies from your wrist.",
      },
      wearOs: {
        title: "Wear OS",
        desc: "The Android companion extends OpenClaw to your watch.",
      },
      macos: {
        title: "macOS",
        desc: "Menu bar companion for your Gateway — notifications, approvals, quick chat.",
      },
      windows: {
        title: "Windows",
        desc: "The Windows companion connects your PC as an OpenClaw device.",
      },
      linux: {
        title: "Linux",
        desc: "Native desktop app — .deb and AppImage builds.",
      },
      chrome: {
        title: "Chrome extension",
        desc: "Let OpenClaw drive your existing Chrome — tabs, pages, and forms.",
      },
      plugins: {
        title: "Plugins & ClawHub",
        desc: "Extend OpenClaw with channels, tools, and skills from the community.",
      },
    },
  },
  presence: {
    rosterLabel: "Show who's online",
    rosterTitle: "Online",
    serverRegion: "Server",
  },
  profilePage: {
    offline: "Connect to the gateway to meet your agent.",
    usageStatistics: "Usage statistics",
    usageStatisticsDescription: "View activity, costs, and usage trends.",
    identity: {
      title: "Identity",
      menuLabel: "Identity menu",
      menuButtonLabel: "Identity and app menu for {name}",
      description: "Your profile on this gateway.",
      loading: "Loading your identity…",
      profileUnavailable: "Your identity profile could not be loaded.",
      notSet: "Identity is not set.",
      setIdentity: "Set identity",
      avatar: "Avatar",
      avatarDescription: "PNG, JPEG, or WebP. Images are resized to 256 × 256 or smaller.",
      chooseAvatar: "Choose image",
      processingAvatar: "Processing…",
      displayName: "Display name",
      displayNameDescription: "Shown to other people using this gateway.",
      linkedEmails: "Linked emails",
      linkedEmailsDescription: "Email addresses connected to this profile.",
      avatarErrors: {
        invalid: "That image could not be processed.",
        sourceTooLarge: "Choose an image that is 10 MB or smaller.",
        tooLarge: "The processed avatar is larger than 512 KB.",
      },
    },
  },
  tasksPage: {
    active: "Active",
    activeSub: "Queued and running background work.",
    recent: "Recent",
    recentSub: "Latest completed, failed, and cancelled tasks.",
    loading: "Loading tasks…",
    empty: "No background tasks yet.",
    emptyActive: "No queued or running tasks.",
    emptyRecent: "No recent completed tasks.",
    disconnected: "Connect to the gateway to load and manage tasks.",
    loadFailed: "Could not load tasks.",
    cancelFailed: "Could not cancel the task.",
    recoveryFailed: "Could not update completion delivery.",
    invalidResponse: "The gateway returned an invalid task list.",
    untitled: "Background task",
    taskCount: "{count} tasks",
    taskCountOne: "1 task",
    agent: "Agent: {agent}",
    openSession: "Open session",
    cancelTask: "Cancel {title}",
    cancelling: "Cancelling…",
    retryDelivery: "Retry delivery",
    dismissDelivery: "Dismiss delivery",
    copyResult: "Copy result",
    deliveryBlocked: "Completed, but result delivery is blocked.",
    deliveryDismissed: "Completed; result delivery was dismissed.",
    duplicateRisk: "Retrying may duplicate a result after an ambiguous acknowledgement.",
    status: {
      queued: "Queued",
      running: "Running",
      completed: "Completed",
      failed: "Failed",
      cancelled: "Cancelled",
      timedOut: "Timed out",
    },
    runtime: {
      subagent: "Subagent",
      cron: "Automation",
      acp: "ACP",
      cli: "CLI",
      unknown: "Task",
    },
  },
  skillWorkshop: {
    title: "Skill Workshop",
    header: {
      useCurrentChat: "Use current chat",
      useCurrentChatAria: "Use current chat for revision requests",
      useCurrentChatTooltip:
        "Send revision requests to the current chat session instead of the proposal's workshop session.",
      selfLearning: "Self-learning",
      selfLearningAria: "Toggle autonomous self-learning",
      selfLearningTooltip:
        "Capture corrections and review substantial completed work as reusable skills. The default automatic mode applies scanner-approved captures and shows them on this board.",
      view: "Workshop view",
      board: "Board",
      today: "Today",
    },
    status: {
      all: "All",
      pending: "Pending",
      applied: "Applied",
      rejected: "Rejected",
      quarantined: "Quarantined",
      stale: "Stale",
    },
    recency: {
      today: "Today",
      yesterday: "Yesterday",
      earlier: "Earlier this week",
    },
    previewContext: "in {slug}",
    actions: {
      close: "Close",
      cancel: "Cancel",
      previous: "Previous",
      next: "Next",
      apply: "Apply",
      applying: "Applying…",
      evaluate: "Evaluate",
      evaluating: "Evaluating…",
      evaluated: "Evaluated",
      revise: "Revise",
      tweak: "Tweak",
      opening: "Opening…",
      reject: "Reject",
      rejecting: "Rejecting…",
      sending: "Sending…",
    },
    notices: {
      applied: "Applied",
      rejected: "Rejected",
      revisionRequested: "Revision requested",
    },
    revision: {
      title: "{verb} proposal",
      description:
        "Tell the agent what should change. The proposal stays pending and the workshop will create a revised version.",
      placeholder:
        "Example: Make this use Gmail labels instead of unread search, and add a safer dry-run step.",
      preparing: "Preparing revision handoff",
      send: "Send revision",
    },
    queue: {
      resize: "Resize proposal list",
      search: "Search proposals…",
      loadError: "Could not load proposals.",
      loading: "Loading proposals…",
      noStatus: "No {status} proposals.",
      noMatch: "No proposals match the current filter.",
    },
    detail: {
      edited: "Edited {time}",
      created: "Created {time}",
      supportFiles: "{count} support files",
      noSupportFiles: "0 support files",
      loading: "Loading proposal…",
      supportFilesTitle: "Support files",
      clickToPreview: "· click to preview",
    },
    evaluation: {
      title: "Evaluation",
      version: "Proposal {version}",
      completedAt: "Completed {time}",
      status: {
        completed: "Completed",
        skipped: "Skipped",
        error: "Error",
      },
      decision: {
        pass: "Pass",
        revise: "Revise",
        block: "Block",
      },
      severity: {
        info: "Info",
        warn: "Warning",
        critical: "Critical",
      },
      evaluatorVersion: "Evaluator {version}",
      mode: "Mode {mode}",
      findings: "Findings",
      metrics: "Metrics",
      fileLine: "{file}:{line}",
      errors: {
        revisionHashUnavailable: "The current proposal revision could not be identified.",
        revisionChanged: "The proposal revision changed during evaluation.",
      },
    },
    empty: {
      searchTitle: "No matching proposals",
      searchBody: "Clear the search or try a different keyword.",
      pendingTitle: "No pending proposals",
      pendingBody: "New drafts will appear here when they need review.",
      appliedTitle: "Nothing applied yet",
      appliedBody: "Use a pending proposal and it will appear here as a live skill.",
      rejectedTitle: "No rejected proposals",
      rejectedBody: "Skipped proposals will stay here for a clean review history.",
      quarantinedTitle: "Nothing quarantined",
      quarantinedBody: "Scanner-blocked or safety-held proposals will appear here.",
      staleTitle: "No stale proposals",
      staleBody: "Proposals that can no longer apply cleanly will appear here.",
      allTitle: "No proposals here",
      allBody: "Skill Workshop proposals will appear here when your agent drafts them.",
      defaultAgent: "Your agent",
      noProposalsAria: "No Skill Workshop proposals",
      noProposalsTitle: "No proposals yet",
      noProposalsBody: "{agent} hasn't drafted any skill proposals.",
      noProposalsFooter: "New proposals will appear here for review.",
    },
    selfLearning: {
      pitchTitle: "Turn on self-learning",
      pitchBody:
        "OpenClaw reviews corrections and substantial completed runs, then applies scanner-approved skills and shows them on this board. Experience review spends extra background tokens.",
      enable: "Enable self-learning",
      enabling: "Enabling…",
      updateError: "Could not update the self-learning setting.",
    },
    history: {
      eyebrow: "Past work",
      title: "Find reusable workflows",
      body: "Review substantial sessions from newest to oldest. Only strong recovery patterns or workflows that save repeated tool calls become pending proposals.",
      findIdeas: "Find skill ideas",
      scanEarlier: "Scan earlier work",
      scanNew: "Scan new work",
      scanning: "Reviewing sessions…",
      loading: "Loading history…",
      pendingOnly: "Pending proposals only · uses your configured model",
      reviewed: "{count} sessions reviewed",
      found: "{count} ideas found",
      noSessions: "No substantial sessions found in this window.",
      today: "today",
    },
    today: {
      emptyTitle: "Nothing waiting today",
      emptyBody: "Your agent hasn't drafted anything new. Switch to Board to browse history.",
      new: "NEW",
      waiting: "WAITING",
      reviewed: "REVIEWED",
      agent: "agent",
      proposalsWaiting: "{count} proposals waiting",
      browseApplied: "Browse what's already applied.",
      progress: "{current} of {total}",
      draftedBy: "Drafted by",
      supportFile: "{count} support file",
      supportFiles: "{count} support files",
      comeWithIt: "come with it.",
      useIt: "Use it",
      addToSkills: "Add to your skills",
      evaluate: "Evaluate",
      runChecks: "Run proposal checks",
      tweakIt: "Tweak it",
      askAgent: "Ask the agent to change something",
      skipping: "Skipping…",
      skip: "Skip",
      notForMe: "Not for me",
      upNext: "Up next · {count} more waiting",
      seeAll: "See all proposals →",
      collection: "Your collection · {count} in use",
      manage: "Manage →",
      workflowHeading: "How the agent will use it",
      applicabilityHeading: "When the agent should use it",
    },
  },
  activity: {
    title: "Activity",
    visibleCount: "{visible} of {total}",
    search: "Search",
    searchPlaceholder: "Filter by activity, summary, run, session",
    toolFilter: "Tool",
    allTools: "All tools",
    statusFilters: "Status filters",
    autoFollow: "Auto-follow",
    expandAll: "Expand all",
    collapseAll: "Collapse all",
    clear: "Clear",
    empty: "No activity yet.",
    emptyFiltered: "No activity matches these filters.",
    entrySummary: "{argumentSummary}",
    argumentHiddenOne: "1 argument hidden",
    argumentsHidden: "{count} arguments hidden",
    streamLabel: "Agent activity entries",
    toolCallId: "Tool call",
    runId: "Run",
    session: "Session",
    outputTruncated: "Preview redacted and truncated.",
    noOutputPreview: "No output preview.",
    answerCandidate: {
      title: "Answer candidate",
      itemId: "Item",
      candidate: "Candidate answer",
      superseded: "Superseded answer",
      selected: "Selected answer",
    },
    status: {
      running: "Running",
      done: "Done",
      error: "Error",
    },
  },
  gatewayLogs: {
    title: "Logs",
    subtitle: "Gateway file logs (JSONL).",
    exportButton: "Export {label}",
    exportLabels: {
      filtered: "filtered",
      visible: "visible",
    },
    filter: "Filter",
    searchPlaceholder: "Search logs",
    autoFollow: "Auto-follow",
    file: "File: {file}",
    truncated: "Log output truncated; showing latest chunk.",
    empty: "No log entries.",
  },
  pluginTabs: {
    unavailableTitle: "Plugin panel unavailable",
    unavailableSubtitle:
      "The plugin that owns this tab is not active on the connected gateway, or it did not provide a panel.",
  },
  logbook: {
    nav: {
      previousDay: "Previous day",
      nextDay: "Next day",
      today: "Today",
    },
    status: {
      capturing: "Capturing every {seconds}s",
      paused: "Capture paused",
      disabled: "Capture off",
      nodeHelp: "Device providing screen snapshots.",
      pending: "{count} frames queued",
      pendingHelp: "Snapshots waiting for the next analysis batch.",
      analyzing: "Analyzing…",
      captureError: "Capture error",
      batchError: "Analysis error",
      modelMissing: "No vision model",
      modelMissingHelp:
        "Set plugins.entries.logbook.config.visionModel (for example codex/gpt-5.6-sol) or configure tools.media models.",
    },
    actions: {
      pause: "Pause",
      resume: "Resume",
      analyzeNow: "Analyze now",
    },
    empty: {
      title: "Nothing on the timeline yet.",
      subtitle:
        "Logbook is collecting snapshots; cards appear after the first analysis batch completes.",
    },
    card: {
      keyframeAlt: "Screen snapshot from this activity",
      distractions: "Distractions",
    },
    stats: {
      title: "Day at a glance",
      focus: "{pct}% focus",
      tracked: "{duration} tracked",
    },
    standup: {
      title: "Daily standup",
      generate: "Generate",
      refresh: "Regenerate",
      empty: "Turn today's timeline into a ready-to-paste standup update.",
    },
    ask: {
      title: "Ask your day",
      placeholder: "When did I review the gateway PR?",
      submit: "Ask",
    },
  },
  board: {
    label: "Session dashboard",
    tabsLabel: "Dashboard tabs",
    moreTabs: "More dashboard tabs",
    gridLabel: "Dashboard widgets",
    emptyTitle: "A clear board, ready for work",
    emptyHint: "Your agent can pin widgets here — try asking for a status card.",
    actionFailed: "The dashboard change could not be saved.",
    actionInProgress: "Another dashboard change is still being saved.",
    announcement: {
      moved: "Moved {title}.",
      resized: "Resized {title}.",
      removed: "Removed {title}.",
      granted: "Widget access allowed.",
      rejected: "Widget access rejected.",
    },
    widget: {
      cellLabel:
        "Dashboard widget: {title}. Use arrow keys to navigate. Hold Alt and press an arrow key to move it.",
      moveHandle: "Move {title}",
      resizeHandle: "Resize {title}",
      menuLabel: "Widget options",
      moveToTab: "Move to tab",
      noOtherTabs: "No other tabs",
      resize: "Resize",
      autoHeight: "Auto height",
      remove: "Remove",
      needsApproval: "Needs approval",
      needsApprovalDetail: "This widget requested additional access.",
      networkAccess: "Network origins",
      hostTools: "Host tools and data",
      activeCapabilities: "Active widget capabilities",
      networkCapability: "Network: {capability}",
      toolCapability: "Tool: {capability}",
      granted: "Granted",
      allow: "Allow",
      reject: "Reject",
      rejected: "Access rejected",
      rejectedDetail: "This widget stays inactive until it is removed or replaced.",
      appLoading: "Restoring app…",
      appStaleTitle: "This pinned app is stale",
      appStaleDetail: "Its server, resource, or originating transcript is no longer available.",
      retry: "Retry",
      frameResolverMissing: "Widget content is unavailable.",
      sandboxUnavailable: "Widget sandbox host is unavailable.",
      frameAuthorizationFailed: "Widget authorization failed after repeated refresh attempts.",
      sandboxOriginRequired:
        "Widget authorization failed after repeated refresh attempts. If the gateway runs behind a reverse proxy or tunnel that does not route the widget sandbox port, set mcp.apps.sandboxOrigin to a dedicated public origin routed to the sandbox listener.",
      errorTitle: "This widget could not load",
      errorDetail: "The problem is contained to this card.",
      actionErrorTitle: "Widget change failed",
      actionErrorDetail: "The dashboard kept the previous widget state.",
      errorShow: "Show details",
      kindMcp: "MCP",
      kindHtml: "HTML",
      kindPlugin: "Plugin",
      pluginLoading: "Loading plugin widget…",
      disabledPlugin: "Widget from disabled plugin {pluginId}",
    },
  },
  workboard: {
    widget: {
      cardLabel: "Workboard card",
      summaryLabel: "Workboard summary",
      loading: "Loading Workboard…",
      cardIdRequired: "This widget needs a cardId prop.",
      cardMissing: "This Workboard card is no longer available.",
      unassigned: "Unassigned",
      openBoard: "Open board",
      statusCounts: "Cards by status",
      noActiveCards: "No ready or running cards.",
    },
    disabledHelpStart: "Workboard is disabled. Enable",
    enableConfigKey: "plugins.entries.workboard.enabled = true",
    disabledHelpEnd: ", then reload this tab.",
    status: {
      triage: "Triage",
      backlog: "Backlog",
      todo: "Todo",
      scheduled: "Scheduled",
      ready: "Ready",
      running: "Running",
      review: "Review",
      blocked: "Blocked",
      done: "Done",
    },
    noLinkedSession: "No linked session",
    stopSession: "Stop session",
    editCard: "Edit card",
    editCardHelp: "Update queue metadata and session handoff.",
    newCard: "New card",
    newCardHelp: "Queue work for an agent session.",
    archiveCard: "Archive card",
    unarchiveCard: "Restore from archive",
    archived: "Archived",
    deleteCard: "Delete card",
    viewDetails: "View details",
    detailTitle: "Card details",
    detailTask: "Gateway task",
    detailRun: "Run",
    detailUpdated: "Updated",
    detailProof: "Proof",
    detailDiagnostics: "Diagnostics",
    detailWorkerLogs: "Worker logs",
    detailWorkerProtocol: "Worker protocol",
    detailAutomation: "Automation",
    detailUpdatedValue: "Updated: {time}",
    detailAutomationTenant: "Tenant: {tenant}",
    detailAutomationBoard: "Board: {board}",
    detailAutomationSkills: "Skills: {skills}",
    detailAutomationWorkspace: "Workspace: {workspace}",
    detailAutomationSummary: "Summary: {summary}",
    detailOperatorNotes: "Operator notes",
    detailNoNotes: "No operator notes yet.",
    detailNotePlaceholder: "Add a decision, blocker, or proof note...",
    detailAddNote: "Add note",
    dashboardTitle: "Dashboard",
    dashboardEmpty: "No dashboard yet — the working agent can pin widgets.",
    openSession: "Open session",
    defaultAgent: "Default agent",
    allAgents: "All agents",
    agentFilter: "Filter by agent",
    allBoards: "All boards",
    boardFilter: "Filter by board",
    defaultBoard: "Default board",
    boardFilterSummary: "{active} active · {total} total",
    boardFilterArchivedSummary: "Archived · {active} active · {total} total",
    agentFilterUnassigned: "Unassigned (uses {agent})",
    agentFilterUnassignedHelp: "Cards without an explicit agent.",
    agentFilterConfiguredDefault: "{agent} (default)",
    agentFilterConfiguredDefaultHelp: "Cards explicitly assigned to the configured default agent.",
    agentCurrentUnconfigured: "{agent} (not configured)",
    viewPreset: "Workboard view",
    viewAll: "All cards",
    viewDefaultAgent: "Default agent",
    viewReady: "Ready",
    viewRunning: "Running",
    viewBlocked: "Blocked",
    viewReview: "Review",
    viewStale: "Stale",
    viewMissingProof: "Missing proof",
    viewRecentlyDone: "Recently done",
    viewPresetCount: "{count} cards",
    agentLinked: "Linked to {agent}",
    agentDefaultLinked: "Using default agent {agent}",
    engineOpenAI: "OpenAI",
    engineClaude: "Claude",
    engineDisabledRuntime:
      "{agent} uses the {runtime} ACP runtime. Use default start for that session.",
    runEngine: "Run {engine}",
    openEngine: "Open {engine}",
    runDefaultAgent: "Run default agent",
    run: "Run",
    open: "Open",
    start: "Start",
    dependencies: "Dependencies",
    dependenciesReady: "{count} ready",
    dependenciesReadyTitle: "{count} dependencies are done.",
    dependenciesBlocked: "{count} blocked",
    dependenciesBlockedTitle: "Waiting on dependencies: {parents}.",
    dependencyMissing: "{parent} (missing)",
    dependencyStatusMissing: "Missing",
    unknownStatus: "Unknown",
    showArchived: "Show archived cards",
    hideArchived: "Hide archived cards",
    showArchivedShort: "Archived",
    hideArchivedShort: "Hide archived",
    layout: "Card layout",
    layoutCompact: "Compact card density",
    layoutComfortable: "Comfortable card density",
    dispatch: "Dispatch ready work",
    dispatchSummary:
      "Dispatch complete: started {started}, promoted {promoted}, blocked {blocked}, reclaimed {reclaimed}, orchestrated {orchestrated}, failures {failures}.",
    dispatchSummaryEmpty: "Dispatch complete: no ready work changed.",
    lastRefreshed: "Updated {time}",
    refreshError: "Refresh failed",
    healthLabel: "Workboard health",
    healthRunning: "running",
    healthBlocked: "blocked",
    healthStale: "stale",
    healthReadyUnassigned: "ready unassigned",
    healthMissingProof: "missing proof",
    healthFailedAttempts: "failed attempts",
    live: "live",
    fieldTitle: "Title",
    fieldNotes: "Notes",
    fieldStatus: "Status",
    fieldPriority: "Priority",
    fieldAgent: "Agent",
    fieldSession: "Session",
    fieldLabels: "Labels",
    templatesLabel: "Card templates",
    template: {
      bugfix: "Bugfix",
      docs: "Docs",
      release: "Release",
      pr_review: "PR review",
      plugin: "Plugin",
    },
    templateDraft: {
      bugfixTitle: "Fix: ",
      bugfixNotes: "Symptom:\nCause:\nAcceptance:\nProof:",
      docsTitle: "Docs: ",
      docsNotes: "Page:\nChange:\nSource proof:",
      releaseTitle: "Release: ",
      releaseNotes: "Scope:\nVerification:\nCloseout:",
      prReviewTitle: "Review PR ",
      prReviewNotes: "Surface:\nRisks:\nProof:",
      pluginTitle: "Plugin: ",
      pluginNotes: "Boundary:\nConfig/docs:\nTests:",
    },
    badgeAttempts: "{count} attempts",
    badgeFailures: "{count} failed",
    badgeComments: "{count} comments",
    badgeLinks: "{count} links",
    badgeProof: "{count} proof",
    badgeHeartbeat: "heartbeat {age}",
    badgeArtifacts: "{count} artifacts",
    badgeAttachments: "{count} attachments",
    badgeDispatches: "{count} dispatches",
    badgeTaskLinked: "task linked",
    badgeClaimed: "claimed by {owner}",
    badgeStale: "stale",
    titlePlaceholder: "Card title",
    notesPlaceholder: "Notes, acceptance criteria, links",
    labelsPlaceholder: "ui, docs",
    searchPlaceholder: "Search cards",
    allPriorities: "All priorities",
    hideEmptyColumns: "Hide empty columns",
    emptyColumn: "Drop work here",
    emptyFilteredTitle: "No cards match this view",
    emptyFilteredHint: "Change the view, search, priority, agent, or archive filter.",
    lifecycleUnlinked: "No session",
    lifecycleUnlinkedDetail: "Start or link a session",
    lifecycleMissing: "Session missing",
    lifecycleMissingDetail: "Reload sessions or relink this card",
    lifecycleLinked: "Linked",
    lifecycleIdleDetail: "No active run",
    lifecycleRunning: "Running",
    lifecycleRunningDetail: "Active run in progress",
    lifecycleStale: "Stale",
    lifecycleStaleDetail: "No recent session activity",
    lifecycleDone: "Done",
    lifecycleDoneDetail: "Moved to review",
    lifecycleNeedsReview: "Needs review",
    lifecycleNeedsReviewDetail: "Run stopped or failed",
    taskStatus: {
      queued: "Task queued",
      running: "Task running",
      completed: "Task complete",
      failed: "Task failed",
      cancelled: "Task cancelled",
      timed_out: "Task timed out",
    },
    eventsLabel: "Card events",
    eventCreated: "Created",
    eventEdited: "Edited",
    eventMoved: "Moved",
    eventMovedTo: "Moved to {status}",
    eventLinked: "Linked session",
    eventSpecified: "Specified",
    eventDecomposed: "Decomposed",
    eventClaimed: "Claimed",
    eventHeartbeat: "Heartbeat",
    eventExecutionUpdated: "Agent updated",
    eventAttemptStarted: "Attempt started",
    eventAttemptUpdated: "Attempt updated",
    eventCommentAdded: "Comment added",
    eventLinkAdded: "Link added",
    eventProofAdded: "Proof added",
    eventArtifactAdded: "Artifact added",
    eventAttachmentAdded: "Attachment added",
    eventDiagnostic: "Diagnostic",
    eventNotification: "Notification",
    eventDispatch: "Dispatch",
    eventOrchestration: "Orchestration",
    eventProtocolViolation: "Protocol violation",
    eventArchived: "Archived",
    eventUnarchived: "Unarchived",
    eventStale: "Stale session",
  },
  connection: {
    queuedCount: "{count} queued",
    reconnecting: "Reconnecting…",
    retryNow: "Retry now",
    access: {
      title: "Gateway Access",
      subtitle: "Where the dashboard connects and how it authenticates.",
      wsUrl: "WebSocket URL",
      token: "Gateway Token",
      password: "Password (not stored)",
      passwordPlaceholder: "system or shared password",
      sessionKey: "Default Session Key",
      connectHint: "Click Connect to apply connection changes.",
      trustedProxy: "Authenticated via trusted proxy.",
      showToken: "Show token",
      hideToken: "Hide token",
      toggleTokenVisibility: "Toggle token visibility",
      showPassword: "Show password",
      hidePassword: "Hide password",
      togglePasswordVisibility: "Toggle password visibility",
    },
    snapshot: {
      title: "Snapshot",
      subtitle: "Latest gateway handshake information.",
      status: "Status",
      tickInterval: "Tick Interval",
      lastChannelsRefresh: "Last Channels Refresh",
      lastError: "Last error",
    },
    help: {
      title: "How to connect",
      step1: "Start the gateway on your host machine:",
      step2: "Get a tokenized dashboard URL:",
      step3: "Paste the WebSocket URL and token above, or open the tokenized URL directly.",
      docsLink: "Read the docs →",
      copyCommand: "Copy command",
      copyCommandAria: "Copy command: {command}",
    },
  },
  attention: {
    cronErrorUnknown: "Unknown error",
    cronFailed: "{count} automation(s) failed",
    cronOverdue: "{count} automation(s) overdue",
    modelAuthExpired: "Model auth expired: {providers}",
    pendingApproval: "{count} pending approval",
    pendingApprovals: "{count} pending approvals",
  },
  approvalHistory: {
    description:
      "Terminal exec, plugin, and system-agent approvals recorded by this gateway, newest first.",
    loading: "Loading approval history…",
    loadingMore: "Loading…",
    loadMore: "Load more",
    empty: "No resolved approvals in the rolling 30-day window.",
    retention: "Approval history is a rolling 30-day window.",
    offline: "Connect to the gateway to load approval history.",
    invalidResponse: "The gateway returned an invalid approval history response.",
    unknown: "Unknown",
    notApplicable: "N/A",
    columns: {
      resolved: "Resolved",
      kind: "Kind",
      request: "Request",
      decision: "Decision",
      reason: "Resolution reason",
      source: "Source agent / session",
      resolver: "Resolver",
    },
    kinds: {
      exec: "Exec",
      plugin: "Plugin",
      systemAgent: "System agent",
    },
    statuses: {
      allowed: "Allowed",
      denied: "Denied",
      expired: "Expired",
      cancelled: "Cancelled",
    },
    decisions: {
      allowOnce: "Allow once",
      allowAlways: "Always allow",
      deny: "Deny",
    },
    reasons: {
      user: "User decision",
      timeout: "Timed out",
      malformedVerdict: "Malformed verdict",
      noRoute: "No route",
      runAborted: "Run aborted",
      gatewayRestart: "Gateway restart",
      storageCorrupt: "Storage corrupt",
    },
  },
  palette: {
    placeholder: "Search chats and commands…",
    noResults: "No results",
    categories: {
      search: "Search",
      navigation: "Navigation",
      skills: "Skills",
    },
    items: {
      apps: "Apps",
      sessions: "Sessions",
      scheduled: "Automations",
      skills: "Skills",
      plugins: "Plugins",
      settings: "Settings",
      agents: "Agents",
    },
    descriptions: {
      verboseMode: "Toggle verbose mode.",
    },
    footer: {
      navigate: "navigate",
      select: "select",
      close: "close",
    },
  },
  dreaming: {
    tabs: {
      scene: "Scene",
      diary: "Diary",
      advanced: "Advanced",
    },
    header: {
      refresh: "Refresh",
      refreshing: "Refreshing…",
      on: "Dreaming On",
      off: "Dreaming Off",
      engineOff: "Memory engine is Off. Choose an engine in Settings to enable dreaming.",
    },
    toggleConfirmation: {
      subtitle: "Dreaming is a global setting; it is not scoped to this agent.",
      enableTitle: "Turn On Dreaming for All Agents",
      enableDetail:
        "The nightly dreaming sweep will run across every configured agent workspace, promoting short-term recalls into long-term memory. This applies right away.",
      enableConfirm: "Turn On Dreaming",
      disableTitle: "Turn Off Dreaming for All Agents",
      disableDetail:
        "The nightly dreaming sweep will stop for every configured agent, not just this one. Memories already written stay; nothing new gets promoted. This applies right away.",
      disableConfirm: "Turn Off Dreaming",
      saving: "Saving…",
      failed: "Could not apply change. Check your connection and try again.",
    },
    status: {
      active: "Dreaming Active",
      idle: "Dreaming Idle",
      promotedSuffix: "promoted",
      nextSweepPrefix: "next sweep",
    },
    scene: {
      backfill: "Backfill",
      dedupeDiary: "Dedupe Diary",
      reset: "Reset",
      clearGrounded: "Clear Replayed",
      repairCache: "Repair Dream Cache",
      working: "Working…",
    },
    phase: {
      light: "Light",
      deep: "Deep",
      rem: "Rem",
      off: "off",
    },
    advanced: {
      eyebrow: "Review",
      title: "Daily Log Review",
      description:
        "Review what came from the daily log, what is waiting for promotion, and what was promoted recently.",
      summaryFromDailyLog: "from daily log",
      summaryWaiting: "waiting",
      summaryPromotedToday: "promoted today",
      stagedTitle: "From the Daily Log",
      stagedDescription: "Replay candidates pulled from older daily log entries.",
      shortTermTitle: "Waiting for Promotion",
      shortTermDescription: "Current short-term candidates waiting to graduate into real memory.",
      sortRecent: "Most recent",
      sortSignals: "Strongest support",
      originDailyLog: "replayed",
      originLive: "live",
      originMixed: "mixed",
      promotedTitle: "Recent Promotions",
      promotedDescription: "Items that already made it through promotion.",
      emptyGrounded: "No staged grounded replay entries right now.",
      emptyShortTerm: "No short-term entries to inspect.",
      emptyPromoted: "No recent promotions to inspect.",
      updatedPrefix: "updated",
    },
    stats: {
      grounded: "Grounded",
      signals: "Signals",
    },
    diary: {
      title: "Dream Diary",
      noDreamsYet: "No dreams yet",
      noDreamsHint: "Dreams will appear here after the first dreaming cycle runs.",
      waitingTitle: "The diary is waiting",
      waitingHint: "Narrative entries will appear after the next dreaming cycle.",
      reload: "Reload",
      reloading: "Reloading…",
    },
    actions: {
      dedupeRemovedOneAndKept: "Removed {removed} duplicate dream entry and kept {kept}.",
      dedupeRemovedManyAndKept: "Removed {removed} duplicate dream entries and kept {kept}.",
      dedupeRemovedOne: "Removed {removed} duplicate dream entry.",
      dedupeRemovedMany: "Removed {removed} duplicate dream entries.",
      repairArchivedThreadCorpus: "archived session corpus",
      repairArchivedIngestionState: "archived ingestion state",
      repairArchivedDreamDiary: "archived dream diary",
      repairNoChanges: "Dream cache repair finished with no changes.",
      repairCompleteWithArchive: "Dream cache repair complete: {actions}. Archive: {archiveDir}",
      repairComplete: "Dream cache repair complete: {actions}.",
      backfillComplete: "Backfilled {count} dream diary entries.",
      resetDiaryComplete: "Removed {count} backfilled dream diary entries.",
      clearReplayedComplete: "Cleared {count} replayed short-term entries.",
      complete: "Dream diary action complete.",
      confirmRepairDescription:
        "This archives derived dream cache files and rebuilds them from clean inputs. Your dream diary stays untouched.",
      confirmDedupeDescription:
        "This rewrites DREAMS.md and removes only exact duplicate diary entries.",
      archivePathCopied: "Archive path copied.",
      archivePathCopyFailed: "Could not copy archive path.",
      updateFailed: "Could not update dreaming settings.",
      unsupportedPlugin: 'Selected memory plugin "{pluginId}" does not support dreaming settings.',
      configHashMissing: "Config hash missing; refresh and retry.",
    },
    wiki: {
      previewFallbackTitle: "Wiki page",
      close: "Close",
      loadingPage: "Loading wiki page…",
      dreamsTab: "Dreams",
      insightsTab: "Imported Insights",
      wikiTab: "Memory Wiki",
      dreamsExplainer:
        "This is the raw dream diary the system writes while replaying and consolidating memory; use it to inspect what the memory system is noticing, and where it still looks noisy or thin.",
      insightsExplainer:
        "These are imported insights clustered from external history; use them to review what imports surfaced before any of it graduates into durable memory.",
      wikiExplainer:
        "This is the compiled memory wiki surface the system can search and reason over; use it to inspect actual memory pages, claims, open questions, and contradictions rather than raw imported source chats.",
      copyArchivePath: "Copy archive path",
      loadingInsights: "Loading imported insights…",
      noInsights: "No imported insights yet",
      noInsightsHint:
        "Run a ChatGPT import with apply to surface clustered imported insights here.",
      candidateSignals: "Potentially useful signals",
      corrections: "Corrections or revisions",
      importDetails: "Import details",
      startedWith: "Started with:",
      endedOn: "Ended on:",
      messages: "Messages:",
      riskReasons: "Risk reasons:",
      labels: "Labels:",
      openSourcePage: "Open source page",
      loadingWiki: "Loading memory wiki…",
      emptyWiki: "Memory wiki is not populated yet",
      emptyWikiHint:
        "Right now the wiki mostly has raw source imports and operational reports. This tab becomes useful once syntheses, entities, or concepts start getting written.",
      claims: "Claims",
      openQuestions: "Open questions",
      contradictions: "Contradictions",
      pageDetails: "Page details",
      wikiPage: "Wiki page:",
      id: "Id:",
      openWikiPage: "Open wiki page",
      unavailable: "Memory Wiki is not enabled",
      unavailablePluginPrefix: "Imported Insights and Memory Wiki are provided by the bundled",
      unavailablePluginSuffix: "plugin.",
      enablePrefix: "Enable",
      enableSuffix: ", then reload this tab.",
      openConfig: "Open Config",
      howToEnable: "How to enable",
      pageTypes: {
        entity: "entity",
        concept: "concept",
        source: "source",
        synthesis: "synthesis",
        report: "report",
      },
      pageGroups: {
        sources: "Sources",
        syntheses: "Syntheses",
        reports: "Reports",
        entities: "Entities",
        concepts: "Concepts",
      },
      counts: {
        pageOne: "{count} page",
        pages: "{count} pages",
        claimRowOne: "{count} claim row",
        claimRows: "{count} claim rows",
        openQuestionOne: "{count} open question",
        openQuestions: "{count} open questions",
        contradictionOne: "{count} contradiction",
        contradictions: "{count} contradictions",
        chats: "{count} chats",
        sensitive: "{count} sensitive",
        signals: "{count} signals",
        messages: "{count} messages",
        userMessages: "{count} user",
        assistantMessages: "{count} assistant",
      },
      pageGroupSummary: "{label} · {count}",
      noPagesYet: "No pages yet",
      sectionPageSummary: "{label}: {count}",
      questionCountOnPages: "{questionCount} on {pageCount}",
      risk: {
        needsReview: "needs review",
        low: "low risk",
        medium: "medium risk",
        high: "high risk",
        unknown: "unknown risk",
      },
      pageNotFound: "No wiki page found for {lookup}.",
      previewTruncated: "Showing the first chunk of this page.",
      previewTruncatedWithTotal: "Showing the first chunk of this page ({count} total lines).",
      importedClusterSummary: "Imported chats clustered around {label}.",
      withheldDigestOne: "{count} digest was withheld pending review.",
      withheldDigests: "{count} digests were withheld pending review.",
      details: "Details",
      hideDetails: "Hide details",
      vault: "Vault",
      fullVaultBreakdown: "Full vault breakdown: {breakdown}.",
      selectedSection: "Selected section: {summary}.",
      latestUpdate: "Latest update {date}.",
      noContent: "No wiki content available.",
    },
    phrases: {
      consolidatingMemories: "consolidating memories…",
      tidyingKnowledgeGraph: "tidying the knowledge graph…",
      replayingConversations: "replaying today's conversations…",
      weavingShortTerm: "weaving short-term into long-term…",
      defragmentingMemoryLane: "defragmenting memory lane…",
      filingLooseThoughts: "filing away loose thoughts…",
      connectingDots: "connecting distant dots…",
      compostingContext: "composting old context windows…",
      alphabetizingSubconscious: "alphabetizing the subconscious…",
      promotingHunches: "promoting promising hunches…",
      forgettingNoise: "forgetting what doesn't matter…",
      dreamingEmbeddings: "dreaming in embeddings…",
      reorganizingAttic: "reorganizing the memory attic…",
      indexingDay: "softly indexing the day…",
      nurturingInsights: "nurturing fledgling insights…",
      simmeringIdeas: "simmering half-formed ideas…",
      whisperingVectorStore: "whispering to the vector store…",
    },
  },
  modelProviders: {
    title: "Configured providers",
    subtitle: "Model providers with auth, plan, quota, and cost data.",
    updated: "Updated {time}",
    refreshing: "Refreshing…",
    disconnected: "Connect to the gateway to see configured model providers.",
    emptyTitle: "No model providers configured",
    emptySubtitle: "Sign in to a provider or add an API key, then refresh.",
    status: {
      ok: "Signed in",
      ready: "Ready",
      expiring: "Expiring",
      expired: "Expired",
      missing: "Not signed in",
      apiKey: "API key",
    },
    expiresIn: "Credential expires in {time}",
    models: "{count} models",
    modelOne: "1 model",
    modelsAvailable: "{available} of {count} models available",
    globalUsage: "Global usage and cost",
    noStats: "No live usage data reported by this provider.",
    localCost: "Global session spend · {days}d",
    localCostDetail: "{tokens} tokens · {sessions} sessions",
    saving: "Saving…",
    requestFailed: "Request failed.",
    configUnavailable: "Configuration is unavailable. Refresh and try again.",
    credentials: {
      label: "Credentials for {agent}",
      oauth: "OAuth profiles: {count}",
      tokenProfiles: "Token profiles: {count}",
      configKey: "API key set in config",
      envKey: "API key from environment",
      envKeyNamed: "API key from environment ({name})",
      profileKey: "API key profiles: {count}",
      none: "Not configured",
    },
    apiKey: {
      label: "API key",
      placeholder: "Enter provider API key",
      replacePlaceholder: "Secret saved. Enter a new key to replace it.",
      set: "Set API key",
      replace: "Replace key",
      remove: "Remove key",
      saved: "Secret saved.",
      removed: "API key removed.",
      authModeBlocked: 'API key changes are unavailable while auth mode is "{mode}".',
    },
    probe: {
      test: "Test connection",
      testing: "Testing…",
      unavailable: "Connection testing requires a newer gateway.",
      latency: "{ms} ms",
      status: {
        ok: "Connected",
        auth: "Authentication failed",
        rate_limit: "Rate limited",
        billing: "Billing problem",
        timeout: "Timed out",
        format: "Invalid response",
        unknown: "Connection failed",
        no_model: "No models available",
      },
    },
    readiness: {
      title: "AI setup",
      heading: "Connect a verified AI model",
      signedInNoModels:
        "You're signed in, but this account exposes no usable models. Choose another provider or account to continue.",
      notConfigured: "Choose a provider and verify the model OpenClaw will use.",
      noModels: "No models available",
      modelRequired: "Model required",
      chooseProvider: "Choose another provider",
    },
    logout: {
      action: "Log out",
      confirm: "Log out of {provider}? Saved OAuth and token profiles will be removed.",
      loggingOut: "Logging out…",
      done: "Logged out.",
    },
    add: {
      title: "Add provider",
      subtitle: "Choose a known model provider and save its API key.",
      action: "Add provider",
      none: "All known providers are already configured.",
      provider: "Provider",
      selectProvider: "Select a provider",
      save: "Save provider",
      saved: "Provider {provider} added.",
    },
    defaults: {
      title: "Default models",
      subtitle: "Choose the primary, ordered fallbacks, and utility model.",
      primary: "Default model",
      utility: "Utility model",
      automatic: "Automatic (provider default)",
      disabled: "Disabled",
      savedSelection: "Saved selection",
      fallbacks: "Fallbacks",
      noFallbacks: "No fallback models configured.",
      unsaved: "Unsaved changes",
      addFallback: "Add fallback",
      selectModel: "Select a model",
      selectFallback: "Select a fallback model",
      noModels: "Configure a provider before selecting default models.",
      saved: "Default models saved.",
    },
    readOnly: {
      disconnected: "Connect to the gateway to change model settings.",
      adminRequired: "Browsing only. Model changes require operator.admin access.",
    },
    notes: {
      saveKey: "Save API key for {provider} from Control UI",
      removeKey: "Remove API key for {provider} from Control UI",
      addProvider: "Add model provider {provider} from Control UI",
      defaultModel: "Update default model selection from Control UI",
    },
  },
  usage: {
    common: {
      emptyValue: "—",
      unknown: "unknown",
    },
    loading: {
      title: "Usage Overview",
      badge: "Loading",
    },
    metrics: {
      tokens: "Tokens",
      cost: "Cost",
      session: "session",
      sessions: "sessions",
    },
    heatmap: {
      title: "Token Activity",
      subtitle: "Daily token intensity for the selected range, up to one year.",
      cellTokens: "{tokens} tokens",
      less: "Less",
      more: "More",
    },
    providerUsage: {
      title: "Provider plans & billing",
      subtitle: "Live plan, quota, balance, and budget data reported by configured providers.",
      balance: "Balance",
      spend: "Usage",
      budget: "Budget",
      today: "Today",
      last7Days: "7 days",
      lastDays: "{count} days",
      dailyCost: "Daily provider cost",
      requests: "{count} requests",
      inputTokens: "{count} input",
      cacheTokens: "{count} cache",
      outputTokens: "{count} output",
      topModels: "Top models",
      costCategories: "Cost categories",
      remaining: "{percent}% left",
      resets: "Resets {date}",
    },
    presets: {
      today: "Today",
      last7d: "7d",
      last30d: "30d",
      last90d: "90d",
      last1y: "1y",
      all: "All",
    },
    scope: {
      instance: "Current instance",
      instanceHint: "Show only the active session id for each logical session.",
      family: "Historical lineage",
      familyHint: "Roll up known rotated transcript-backed session ids.",
      familyIncluded: "Historical lineage includes {count} session instances.",
    },
    filters: {
      title: "Filters",
      to: "to",
      startDate: "Start date",
      endDate: "End date",
      timeZone: "Time zone",
      timeZoneLocal: "Local",
      timeZoneUtc: "UTC",
      pin: "Pin",
      pinned: "Pinned",
      selectAll: "Select All",
      clear: "Clear",
      clearAll: "Clear All",
      remove: "Remove filter",
      removeDays: "Remove days filter",
      removeHours: "Remove hours filter",
      removeSession: "Remove session filter",
      all: "All",
      days: "Days",
      hours: "Hours",
      session: "Session",
      agent: "Agent",
      channel: "Channel",
      provider: "Provider",
      model: "Model",
      tool: "Tool",
      daysCount: "{count} days",
      hoursCount: "{count} hours",
      sessionsCount: "{count} sessions",
    },
    query: {
      placeholder:
        "Filter sessions (e.g. key:agent:main:cron* model:gpt-4o has:errors minTokens:2000)",
      apply: "Filter (client-side)",
      matching: "{shown} of {total} sessions match",
      inRange: "{total} sessions in range",
      tip: "Tip: use filters or click bars to refine days.",
    },
    export: {
      label: "Export",
      sessionsCsv: "Sessions CSV",
      dailyCsv: "Daily CSV",
      json: "JSON",
    },
    cacheStatus: {
      warning: "Usage cache is rebuilding in the background. Displayed totals may be stale.",
      title: "{status}: {pending} pending, {stale} stale, {cached} cached",
      status: {
        refreshing: "refreshing",
        stale: "stale",
        partial: "partial",
      },
    },
    empty: {
      title: "Start with a date range",
      subtitle:
        "Load usage data to compare costs, inspect sessions, and drill into timelines without leaving the dashboard.",
      hint: "Select a date range and click Refresh to load usage.",
      noData: "No data",
      featureOverview: "Overview cards",
      featureSessions: "Session ranking",
      featureTimeline: "Timeline drilldown",
    },
    daily: {
      title: "Daily Usage",
      total: "Total",
      byType: "By Type",
      tokensTitle: "Daily Token Usage",
      costTitle: "Daily Cost",
      compressedScaleHint: "Square-root scale keeps low-usage days visible.",
    },
    costWindows: {
      title: "Cost Windows",
      subtitle: "Calendar windows ending {date}",
      selectedRange: "Selected Range",
      lastDays: "Last {count} days",
      perDay: "/ day",
    },
    breakdown: {
      output: "Output",
      input: "Input",
      cacheWrite: "Cache Write",
      cacheRead: "Cache Read",
      total: "Total",
      tokensByType: "Tokens by Type",
      costByType: "Cost by Type",
    },
    overview: {
      title: "Usage Overview",
      messages: "Messages",
      messagesHint: "Total user and assistant messages in range.",
      messagesAbbrev: "msgs",
      user: "user",
      assistant: "assistant",
      toolCalls: "Tool Calls",
      toolCallsHint: "Total tool call count across sessions.",
      toolsUsed: "tools used",
      errors: "Errors",
      errorsHint: "Total message and tool errors in range.",
      toolResults: "tool results",
      avgTokens: "Avg Tokens / Msg",
      avgTokensHint: "Average tokens per message in this range.",
      avgCost: "Avg Cost / Msg",
      avgCostHint: "Average cost per message when providers report costs.",
      avgCostHintMissing:
        "Average cost per message when providers report costs. Cost data is missing for some or all sessions in this range.",
      acrossMessages: "Across {count} messages",
      sessions: "Sessions",
      sessionsHint: "Distinct sessions in the range.",
      sessionsInRange: "of {count} in range",
      throughput: "Throughput",
      throughputHint: "Throughput shows tokens per minute over active time. Higher is better.",
      tokensPerMinute: "tok/min",
      perMinute: "/ min",
      errorRate: "Error Rate",
      errorHint: "Error rate = errors / total messages. Lower is better.",
      avgSession: "avg session",
      cacheHitRate: "Cache Hit Rate",
      cacheHint:
        "Cache hit rate = cache read / (input + cache read + cache write). Higher is better.",
      cached: "cached",
      prompt: "prompt",
      calls: "calls",
      costShare: "{percent}% of cost",
      topModels: "Top Models",
      topProviders: "Top Providers",
      topTools: "Top Tools",
      topAgents: "Top Agents",
      topChannels: "Top Channels",
      peakErrorDays: "Peak Error Days",
      peakErrorHours: "Peak Error Hours",
      noModelData: "No model data",
      noProviderData: "No provider data",
      noToolCalls: "No tool calls",
      noAgentData: "No agent data",
      noChannelData: "No channel data",
      noErrorData: "No error data",
    },
    sessions: {
      title: "Sessions",
      shown: "{count} shown",
      total: "{count} total",
      avg: "avg",
      all: "All",
      recent: "Recently viewed",
      recentShort: "Recent",
      sort: "Sort",
      ascending: "Ascending",
      descending: "Descending",
      clearSelection: "Clear Selection",
      noRecent: "No recent sessions",
      noneInRange: "No sessions in range",
      more: "+{count} more",
      selected: "Selected ({count})",
      copy: "Copy",
      limitReached: "Showing first 1,000 sessions. Narrow date range for complete results.",
    },
    details: {
      noUsageData: "No usage data for this session.",
      duration: "Duration",
      modelMix: "Model Mix",
      filtered: "(filtered)",
      close: "Close session details",
      noTimeline: "No timeline data",
      loadFailed: "Could not load {detail}: {error}",
      noDataInRange: "No data in range",
      usageOverTime: "Usage Over Time",
      reset: "Reset",
      perTurn: "Per Turn",
      cumulative: "Cumulative",
      turnRange: "Turns {start}–{end} of {total}",
      assistantOutputTokens: "Assistant output tokens",
      userToolInputTokens: "User + tool input tokens",
      tokensWrittenToCache: "Tokens written to cache",
      tokensReadFromCache: "Tokens read from cache",
      noContextData: "No context data",
      systemPromptBreakdown: "System Prompt Breakdown",
      collapse: "Collapse",
      collapseAll: "Collapse All",
      expandAll: "Expand All",
      baseContextPerMessage: "Base context per message",
      system: "System",
      systemShort: "Sys",
      skills: "Skills",
      tools: "Tools",
      files: "Files",
      ofInput: "of input",
      of: "of",
      timelineFiltered: "timeline filtered",
      conversation: "Conversation",
      noMessages: "No messages",
      tool: "Tool",
      toolResult: "Tool result",
      hasTools: "Has tools",
      searchConversation: "Search conversation",
      filterByRole: "Filter by role",
      filterByTool: "Filter by tool",
      you: "You",
      noMessagesMatch: "No messages match the filters.",
    },
    mosaic: {
      title: "Activity by Time",
      subtitleEmpty: "Estimates require session timestamps.",
      subtitle: "Estimated from session spans (first/last activity). Time zone: {zone}.",
      noTimelineData: "No timeline data yet.",
      dayOfWeek: "Day of Week",
      midnight: "Midnight",
      fourAm: "4am",
      eightAm: "8am",
      noon: "Noon",
      fourPm: "4pm",
      eightPm: "8pm",
      legend: "Low → High token density",
      sun: "Sun",
      mon: "Mon",
      tue: "Tue",
      wed: "Wed",
      thu: "Thu",
      fri: "Fri",
      sat: "Sat",
    },
  },
  login: {
    subtitle: "Gateway Dashboard",
    deviceAuthMigration: {
      banner: "This browser still needs one-time device approval after the update.",
      action: "Secure this browser",
      secureContextRequired:
        "This legacy browser remains available temporarily. Reopen it over HTTPS or localhost to secure it with device identity.",
      pendingUnavailable: "The browser pairing request is not available yet. Retry in a moment.",
      loadFailed: "Could not load this browser's pairing request: {error}",
      approvalFailed: "Could not secure this browser: {error}",
    },
    passwordPlaceholder: "optional",
    showToken: "Show token",
    hideToken: "Hide token",
    toggleTokenVisibility: "Toggle token visibility",
    showPassword: "Show password",
    hidePassword: "Hide password",
    togglePasswordVisibility: "Toggle password visibility",
    failure: {
      rawError: "Raw error",
      docsAuth: "Control UI auth docs",
      docsPairing: "Device pairing docs",
      docsInsecure: "Insecure HTTP docs",
      authRequired: {
        title: "Auth required",
        summary:
          "The Gateway is reachable, but it needs a matching token or password before this browser can connect.",
        stepPaste:
          "Paste the token from openclaw gateway auth-token --show or enter the configured password.",
        stepGenerate:
          "If no token is configured, run openclaw doctor --generate-gateway-token on the gateway host.",
        stepConnect: "Click Connect again after updating the credential.",
      },
      authFailed: {
        title: "Auth did not match",
        summary:
          "The supplied credential was rejected. The most common cause is a stale token or a token copied from another Gateway URL.",
        stepDashboard:
          "Run openclaw dashboard --no-open for a fresh URL, or openclaw gateway auth-token --show to recover the token.",
        stepReplace:
          "Replace stale token/password values; do not reuse a token from another Gateway URL.",
        stepMode:
          "Use one matching auth mode at a time: gateway token for token mode, password for password mode.",
      },
      rateLimited: {
        title: "Too many failed attempts",
        summary: "The Gateway is temporarily limiting authentication attempts for this client.",
        stepStop: "Stop retrying from this tab for a moment.",
        stepWait:
          "Wait for the auth limiter to cool down, then reconnect with the corrected credential.",
        stepCheckClients: "If this is a shared host, check other clients for repeated bad retries.",
      },
      pairing: {
        title: "Device pairing required",
        scopeTitle: "Scope upgrade pending",
        roleTitle: "Role upgrade pending",
        metadataTitle: "Device refresh pending",
        summary:
          "This browser needs one-time approval from the Gateway host before it can use the Control UI.",
        upgradeSummary:
          "This browser is already known, but the requested access changed and needs a fresh approval.",
        stepDashboard:
          "On the Gateway host, run openclaw dashboard to open a secure one-time pairing link.",
        stepList: "Run openclaw devices list on the Gateway host.",
        stepApproveId: "Approve this request: openclaw devices approve {requestId}.",
        stepApprove: "Approve the pending browser/device request from that list.",
        stepReconnect: "Reconnect after the approval completes.",
      },
      insecure: {
        title: "Secure browser context required",
        summary:
          "This page is running over plain HTTP, so the browser cannot create the device identity the Gateway expects.",
        stepHttps: "Use HTTPS/Tailscale Serve, or open http://127.0.0.1:18789 on the Gateway host.",
        stepAvoidDisable:
          "Do not use a remote plain-HTTP URL; a token or password cannot replace browser device identity.",
      },
      origin: {
        title: "Browser origin not allowed",
        summary:
          "The Gateway rejected this page origin before accepting the Control UI connection.",
        stepAllowedOrigins: "Add this browser origin to gateway.controlUi.allowedOrigins.",
        stepFullOrigin: "Use full origins such as http://localhost:5173, not wildcard patterns.",
        stepRestart: "Restart or reload the Gateway after changing allowed origins.",
      },
      protocol: {
        title: "Protocol mismatch",
        summary:
          "The served Control UI and the running Gateway do not agree on the supported connection protocol.",
        refresh: "Refresh page",
        stepDashboard:
          "Reopen the served dashboard with openclaw dashboard so the UI and Gateway come from the same install.",
        stepDevUi:
          "If using pnpm ui:dev, rebuild or restart the dev UI against the current checkout.",
        stepRestart:
          "Restart the Gateway after updating OpenClaw so it serves the current protocol.",
      },
      network: {
        title: "Could not connect",
        summary:
          "The browser could not complete the Gateway connection. Check the target and transport before retrying credentials.",
        stepGateway: "Confirm the Gateway is running with openclaw status or openclaw gateway run.",
        stepUrl:
          "Check the WebSocket URL and use wss:// when the Gateway is behind HTTPS/Tailscale Serve.",
        stepDashboard:
          "Reopen the dashboard with openclaw dashboard --no-open to recopy the current URL and auth details.",
      },
    },
  },
  chat: {
    sendErrors: {
      activeLeafChanged: "The session switched branches — review and resend.",
      steerRunNoLongerActive:
        "This steer still targets the previous run, but that run is no longer active.",
    },
    waitingForApproval: "Waiting for approval…",
    startupStatus: {
      preparingWorkspace: "Preparing workspace…",
      provisioningEnvironment: "Provisioning environment…",
      preparingContext: "Preparing this turn…",
      startingModel: "Waiting for a response…",
    },
    archivedSessionDisabled: "This session is archived. Unarchive it to continue the conversation.",
    sessionRoute: {
      chooseTitle: "Choose a session",
      multipleMatches: "More than one session matches {shortId}.",
      additionalMatches: "Search results remain. Use a longer id prefix.",
    },
    commandResults: {
      startingNewThread: "Starting new session...",
      resettingThread: "Resetting session...",
      stoppingCurrentRun: "Stopping current run...",
      chatHistoryCleared: "Chat history cleared.",
      exportingThread: "Exporting session...",
      unknownCommand: "Unknown command: `{command}`",
      options: "Options: {options}.",
      sessionUnavailable: "Session capability is unavailable",
      help: {
        availableCommands: "Available Commands",
        agentCommand: "agent",
        openMenu: "Type `/` to open the command menu.",
      },
      compaction: {
        failed: "Compaction failed.",
        failedWithReason: "Compaction failed: {reason}",
        tokenSummary: " ({before} -> {after} tokens)",
        succeeded: "Context compacted successfully",
        skipped: "Compaction skipped.",
        skippedWithReason: "Compaction skipped: {reason}",
      },
      model: {
        current: "**Current model:** {model}",
        available: "**Available:** {models}",
        more: " +{count} more",
        getFailed: "Failed to get model info: {error}",
        set: "Model set to {model}.",
        setFailed: "Failed to set model: {error}",
      },
      thinking: {
        current: "Current thinking level: {level}.",
        getFailed: "Failed to get thinking level: {error}",
        reset: "Thinking level reset to default.",
        resetFailed: "Failed to reset thinking level: {error}",
        unrecognized: 'Unrecognized thinking level "{level}". Valid levels: {options}.',
        unsupported:
          'Unsupported thinking level "{level}" for this model. Valid levels: {options}.',
        set: "Thinking level set to {level}.",
        setFailed: "Failed to set thinking level: {error}",
      },
      verbose: {
        current: "Current verbose level: {level}.",
        getFailed: "Failed to get verbose level: {error}",
        unrecognized: 'Unrecognized verbose level "{level}". Valid levels: off, on, full.',
        set: "Verbose mode set to {level}.",
        setFailed: "Failed to set verbose mode: {error}",
      },
      fast: {
        autoValue: "auto ({seconds} sec)",
        on: "on",
        off: "off",
        sourceSession: " (session)",
        sourceAgent: " (default: agent)",
        sourceModel: " (default: model)",
        sourceDefault: " (default)",
        current: "Current fast mode: {value}",
        options: "on, off, auto ({seconds} sec), default, status",
        getFailed: "Failed to get fast mode: {error}",
        reset: "Fast mode reset to default.",
        resetFailed: "Failed to reset fast mode: {error}",
        unrecognized:
          'Unrecognized fast mode "{mode}". Valid levels: on, off, auto, default, status.',
        setAuto: "Fast mode set to auto.",
        enabled: "Fast mode enabled.",
        disabled: "Fast mode disabled.",
        setFailed: "Failed to set fast mode: {error}",
      },
      usage: {
        noActiveThread: "No active session.",
        notAvailable: "n/a",
        title: "Session Usage",
        inputTokens: "Input: {count} tokens",
        outputTokens: "Output: {count} tokens",
        totalTokens: "Total: {count} tokens",
        context: "Context: {percent} of {total}",
        model: "Model: {model}",
        failed: "Failed to get usage: {error}",
      },
      agents: {
        none: "No agents configured.",
        title: "**Agents** ({count})",
        default: "default",
        runtime: " · runtime {runtime}",
        failed: "Failed to list agents: {error}",
      },
      steer: {
        timeout: "The active run ended before the steer message was accepted.",
        failed: "Steer failed before it reached the run; try again.",
        usage: "Usage: `/steer <message>`",
        noActiveRun: "No active run. Use the chat input or `/redirect` instead.",
        succeeded: "Steered.",
        requestFailed: "Failed to steer: {error}",
      },
      redirect: {
        timeout: "The active run ended before the redirect message was accepted.",
        failed: "Redirect failed before it reached the run; try again.",
        usage: "Usage: `/redirect <message>`",
        succeeded: "Redirected.",
        requestFailed: "Failed to redirect: {error}",
      },
    },
    sessionSharing: {
      menu: "Session sharing",
      current: "Session visibility: {visibility}",
      visibility: "Visibility",
      shared: "Shared",
      readOnly: "Read-only",
      suggest: "Suggest",
      draft: "Draft",
      publishDraft: "Publish draft",
      members: "Members",
      selected: "Member",
      noPeople: "No paired people found.",
      readOnlyNotice: "Only the session owner and members can act in this session.",
    },
    sessionSuggestions: {
      suggest: "Suggest",
      suggestMessage: "Suggest message",
      attachmentsUnsupported: "Remove attachments before submitting a text suggestion.",
      sendNow: "Send {author}'s suggestion now",
      queue: "Queue {author}'s suggestion",
      edit: "Edit {author}'s suggestion",
      dismiss: "Dismiss {author}'s suggestion",
      typing: "{name} is typing…",
      typingMany: "{names} are typing…",
      state: {
        pending: "Pending",
        accepted: "Accepted",
        dismissed: "Dismissed",
      },
    },
    sessionHeader: {
      renameTooltip: "Rename session",
      renameAria: "Rename session {title}",
      renameInputAria: "Session title",
      renameInputPlaceholder: "Session title",
      workspaceAria: "Workspace actions for {workspace}",
      revealFinder: "Reveal in Finder",
      revealFileExplorer: "Reveal in File Explorer",
      revealFileManager: "Open in file manager",
      copyPath: "Copy path",
      copyBranch: "Copy branch name",
      copied: "Copied",
      incognito: "Incognito session",
      branches: "Session branches",
      branchSwitchUnavailable: "Branch switch is unavailable while the agent is working.",
      untitledBranch: "Untitled branch",
      oneMessage: "{count} message",
      messages: "{count} messages",
      activeBranch: "Active branch",
      gatewayPicker: {
        menuLabel: "Gateway: {gateway}",
        primaryTag: "primary",
        setPrimary: "Set as primary…",
        openSettings: "Gateway settings…",
        connected: "Connected",
        unreachable: "Unreachable",
        unknown: "Unknown status",
      },
    },
    board: {
      faceLabel: "Session face",
      chatFace: "Chat",
      splitFace: "Split",
      dashboardFace: "Dashboard",
      dockMenu: "Chat dock: {dock}",
      dockLeft: "Dock chat left",
      dockRight: "Dock chat right",
      dockBottom: "Dock chat bottom",
      resizeDock: "Resize chat dock",
      workboardCard: "Workboard card: {title}, {status}",
      defaultTab: "Main",
      mockPlaceholder: "Board view seam · {tabs} tabs · {widgets} widgets",
      mockOverview: "Overview",
      mockResearch: "Research",
      mockSessionStatus: "Session status",
      mockRecentFindings: "Recent findings",
      mockSourceMap: "Source map",
      resetTitle: "Reset conversation?",
      resetDescription: "The conversation context resets. Your dashboard stays.",
    },
    catalog: {
      remoteViewOnly: "This session is on a paired device and is view-only.",
      unsupportedViewOnly: "This external session source is view-only.",
      sessionMenu: "External session actions",
      openInOpenClaw: "Open in OpenClaw",
      openInTerminal: "Open in terminal",
      terminalUnavailable: "Terminal opening is unavailable for this session.",
    },
    taskSuggestions: {
      eyebrow: "Suggested follow-up",
      start: "Start in worktree",
      starting: "Starting…",
      dismiss: "Dismiss {title}",
      project: "Project",
      instructions: "Instructions",
      adminRequired: "Administrator access is required to create a worktree from this project.",
    },
    pullRequests: {
      linkLabel: "Pull request #{number}: {title}",
      createPr: "Create PR",
      createPrLabel: "Create a pull request for {branch}",
      dismiss: "Dismiss pull request #{number}",
      open: "Open",
      draft: "Draft",
      merged: "Merged",
      closed: "Closed",
      checks: "CI",
      checksPassing: "CI checks passing",
      checksFailing: "CI checks failing",
      checksPending: "CI checks running",
      ciMonitoring: "CI monitoring",
      openChecks: "Open checks on GitHub",
      checksPassed: "Passed",
      checksFailed: "Failed",
      checksRunning: "Running",
      checksSkipped: "Skipped",
      showMore: "Show {count} more",
      rateLimited:
        "GitHub API rate limit reached. Pull request status may be out of date until the limit resets.",
    },
    usageRemaining: "Usage Remaining",
    view: {
      menu: "View",
      reasoning: "Reasoning",
      toolCalls: "Tool calls",
      commentary: "Keep commentary",
    },
    sendShortcut: "Send shortcut",
    sendShortcutEnter: "Enter",
    sendShortcutModifierEnter: "⌘/Ctrl+Enter",
    followUpMode: "Follow-ups while the agent is working",
    followUpModeQueue: "Queue until the run ends",
    followUpModeServer: "Server default ({mode})",
    followUpModeLoading: "loading…",
    followUpModeSteer: "Steer into the active run",
    followUpModeUsingServer: "Using server default ({mode})",
    followUpModeOverriding: "Overriding server default ({mode})",
    followUpModeReset: "Reset to server default",
    catalogOpenTarget: "Open external sessions in",
    catalogOpenTargetViewer: "OpenClaw viewer",
    catalogOpenTargetTerminal: "Terminal",
    onboardingDisabled: "Disabled during setup",
    commandPaletteTitle: "Search or jump to… (⌘K)",
    openCommandPalette: "Open command palette",
    updating: "Updating…",
    dismissUpdateBanner: "Dismiss update banner",
    actions: {
      copyAsMarkdown: "Copy as markdown",
      dismissError: "Dismiss error",
      exitFocusMode: "Exit focus mode",
      scrollToLatest: "Scroll to latest",
    },
    markdown: {
      truncated: "… truncated ({total} chars, showing first {shown}).",
    },
    codeBlock: {
      jsonLines: "JSON · {count} lines",
    },
    workspaceConflict: {
      titleOne: "1 cloud workspace conflict",
      titleMany: "{count} cloud workspace conflicts",
      description:
        "OpenClaw kept your local versions and applied the other cloud changes. Inspect the staged result or take its version for a conflicted path.",
      morePaths: "+{count} more paths",
      stagedResult: "Staged cloud result",
      copyStagedResult: "Copy staged result ref",
      inspectCloud: "Inspect the first cloud version",
      takeCloud: "Take the first cloud version",
      copyInspectCommand: "Copy cloud inspect command",
      copyTakeCommand: "Copy take-cloud command",
      commandHelp:
        "Run these in Bash or zsh (Git Bash on Windows). If inspect says the path does not exist, the cloud deleted it; verify and remove the local path manually. If checkout reports a file/directory conflict, move or remove the blocking local path, then retry. If the staged ref is missing, the notice is stale; do not change the local path.",
      commandsUnavailable:
        "This filename contains terminal control characters, so OpenClaw will not build a copyable shell command for it. Inspect the staged ref directly and enter the path manually with care.",
      dismiss: "Dismiss workspace conflict notice",
      eventSender: "Cloud workspace",
      eventTitleOne: "Cloud result applied with 1 conflict",
      eventTitleMany: "Cloud result applied with {count} conflicts",
      eventDescription:
        "Local versions were kept for these paths; other cloud changes were applied.",
    },
    compaction: {
      label: "Compacted history",
      savedTokens: "saved {count} tokens",
      description:
        "The compacted transcript is preserved as a checkpoint. Open session checkpoints to branch or restore from that compacted view.",
      openCheckpoints: "Open checkpoints",
    },
    progressLabels: {
      shelling: "Shelling",
      scuttling: "Scuttling",
      clawing: "Clawing",
      pinching: "Pinching",
      molting: "Molting",
      bubbling: "Bubbling",
      tiding: "Tiding",
      reefing: "Reefing",
      cracking: "Cracking",
      sifting: "Sifting",
      brining: "Brining",
      nautiling: "Nautiling",
      krilling: "Krilling",
      barnacling: "Barnacling",
      lobstering: "Lobstering",
      tidepooling: "Tidepooling",
      pearling: "Pearling",
      snapping: "Snapping",
      surfacing: "Surfacing",
    },
    turnRecap: {
      doneIn: "Done in {duration}",
      tokens: "{count} tokens",
      tokensOne: "1 token",
    },
    commands: {
      arguments: "Command arguments",
      menu: "Slash commands",
      instant: "instant",
      optionCount: "{count} options",
      showMoreOne: "Show 1 more command",
      showMoreMany: "Show {count} more commands",
      navigate: "navigate",
      fill: "fill",
      run: "run",
      select: "select",
      close: "close",
      clearDescription: "Clear chat history",
      redirectDescription: "Abort and restart with a new message",
      steerDescription: "Inject a message into the active run",
      categories: {
        session: "Session",
        model: "Model",
        agents: "Agents",
        tools: "Tools",
      },
    },
    skills: {
      menu: "Skill references",
      label: "Skills",
      loading: "Loading skills…",
    },
    splitView: {
      open: "Open split view",
      splitRight: "Split right",
      splitDown: "Split down",
      closePane: "Close pane",
      dropSplit: "Split",
      dropOpenHere: "Open here",
    },
    sidebar: {
      updateMacAndGateway: "Update Mac app + Gateway",
      updateGateway: "Update Gateway",
      serverUpdatedTitle: "Server updated",
      serverUpdatedRefresh: "Refresh for full capabilities",
      threads: "Sessions",
      groups: "Groups",
      coding: "Coding",
      noSessionsForAgent: "No sessions found for this agent",
      catalogViewOptions: "View options",
      hideFromSidebar: "Hide from sidebar",
      hiddenSessionSections: "Hidden session sections",
      showSessionSection: "Show",
      catalogGroupByProject: "Project",
      catalogGroupByPerson: "Person",
      openSessionMenu: "Open session menu",
      sortBy: "Sort by",
      sortCreated: "Created",
      sortSessions: "Sort sessions",
      sortUpdated: "Last updated",
      sessionMenu: "Actions for {session}",
      sessionMenuMany: "Actions for {count} sessions",
      toolActivity: "Using {tool}",
      catalogDiscoveryHelp:
        "{error}. Configure native session discovery in Settings > Automation > Plugins.",
    },
    welcome: {
      hintBeforeShortcut: "Type a message below ·",
      hintAfterShortcut: "for commands",
      recentSessions: "Recent chats",
      suggestions: {
        whatCanYouDo: "What can you do?",
        summarizeRecentSessions: "Summarize my recent sessions",
        configureChannel: "Help me configure a channel",
        checkSystemHealth: "Check system health",
      },
    },
    runControls: {
      newSession: "New session",
      newSessionWorktree: "New session in worktree",
      export: "Export",
      exportChat: "Export chat",
      queue: "Queue",
      queueMessage: "Queue message",
      stop: "Stop",
      stopGenerating: "Stop generating",
      send: "Send",
      sendMessage: "Send message",
    },
    queue: {
      retry: "Retry",
      retryQueuedMessage: "Retry queued message",
      steer: "Steer",
      steerQueuedMessage: "Steer queued message",
      removeQueuedMessage: "Remove queued message",
      states: {
        steering: "Steering",
        applyingSettings: "Applying chat settings",
        waitingForRun: "Waiting for current run",
        runningCommand: "Running command",
        waitingForReconnect: "Waiting for reconnect",
        needsReview: "Delivery uncertain",
      },
      imageCount: "Image ({count})",
    },
    goals: {
      edit: "Edit goal",
      pause: "Pause goal",
      resume: "Resume goal",
      clear: "Clear goal",
      showDetails: "Show goal details",
      hideDetails: "Hide goal details",
    },
    questions: {
      other: "Type your own answer here",
      submit: "Submit",
      next: "Next",
      back: "Back",
      skip: "Skip",
      skipping: "Skipping…",
      skipped: "Skipped",
      collapse: "Collapse question",
      expand: "Expand question",
      eyebrow: "Question",
      summaryLabel: "Question outcome",
      timeRemaining: "Time remaining",
      ownAnswer: "Your own answer…",
      ownAnswerFor: "Your own answer for {header}",
      submitting: "Submitting…",
      submitFailed: "Could not submit: {error}",
      answered: "Answered",
      answeredElsewhere: "Answered elsewhere",
      unavailable: "Unavailable",
      expired: "Expired",
      disconnected: "Not connected. Try again after reconnecting.",
    },
    imageLightbox: {
      label: "Image preview: {title}",
      open: "Open image {title}",
      openOriginal: "Open original",
      close: "Close image preview",
      untitled: "Image",
    },
    messages: {
      activity: "Activity",
      copySelection: "Copy",
      forkFromHere: "Fork from here",
      fullContentLoadFailed: "Could not load the full message.",
      reply: "Reply",
      replyToMessage: "Reply to message",
      replyingTo: "Replying to {name}",
      message: "message",
      currentMessage: "current message",
      actions: "Message actions",
      selectionActions: "Selection actions",
      moreDetails: "More details",
      askInSideChat: "Ask in side chat",
      rewind: "Rewind",
      rewindConfirm: "Rewind to before this message?",
      dontAskAgain: "Don't ask again",
      rewindToHere: "Rewind to here",
      rewindUnavailable: "Rewind is unavailable while the agent is working",
      forkUnavailable: "Fork is unavailable while the agent is working",
      showLess: "Show less",
      showMore: "Show more",
      unknownDate: "Unknown date",
      voiceNote: "Voice note",
      duplicatesCollapsed: "{count} consecutive identical messages collapsed",
      contextFor: "Message context for {timestamp}",
    },
    mediaPlayer: {
      play: "Play",
      pause: "Pause",
      seek: "Seek media",
      download: "Download {filename}",
      preparing: "Preparing playback…",
      videoUnavailable: "Can't play this format — download instead.",
    },
    modelControls: {
      current: "Current",
      default: "Default",
      reasoning: "Reasoning",
      effort: "Effort",
      faster: "Faster",
      smarter: "Smarter",
      speed: "Speed",
      fastMode: "Fast mode",
      searchModels: "Search models",
      noMatchingModels: "No models match your search",
      sessionOverride: "Session override",
      useDefault: "Use default",
      usingDefault: "Using agent default",
      resetToDefault: "Reset to default ({model})",
      defaultWithModel: "Default ({model})",
      defaultWithLevel: "Default ({level})",
      fastHelp: "Fast responses finish sooner and can use more of your usage limits.",
      speedUnsupported: "Speed control is not supported for this model.",
      contextWindow: "{count} context",
      chatOnly: "Chat only",
      chatOnlyHelp:
        "This model can chat, but it cannot use tools. Choose another model for files, commands, web, or media tasks.",
      loadingModels: "Loading models…",
      refreshingModels: "Refreshing models…",
      modelsUnavailable: "Models unavailable",
      modelsRefreshFailed: "Couldn’t refresh models",
      noModelsAvailable: "No models available",
      providerModels: "{provider} models",
      resetReasoning: "Reset to default ({level})",
      useDefaultReasoning: "Use default reasoning ({level})",
      fastResponsesAria: "Fast responses: {state}",
    },
    rail: {
      title: "Session companion",
      subtitle: "Ask about this session or its project",
      expand: "Expand session rail",
      collapse: "Collapse session rail",
      hide: "Hide session rail",
      show: "Show session rail",
      clear: "Clear companion session",
      plan: "Plan",
      progress: "{completed} of {total}",
      pullRequests: "Pull requests",
      checksPassing: "{count} passed",
      checksFailing: "{count} failed",
      checksPending: "{count} running",
      finished: "Finished {time}",
      empty: "Ask a read-only question about this session or its project.",
      askLabel: "Ask the session companion",
      askPlaceholder: "What should I know?",
      askSubmit: "Ask",
      askPending: "Checking the session…",
      askBusy: "The companion is already answering a question.",
      askUnavailable: "The companion cannot answer right now.",
      asOf: "as of {time}",
      health: {
        "on-track": "On track",
        grinding: "Working",
        stuck: "Stuck",
        "waiting-on-user": "Waiting on you",
        "wrapping-up": "Wrapping up",
        done: "Done",
        failed: "Failed",
      },
    },
    observer: {
      title: "Session observer",
      plan: "Plan",
      progress: "{completed} of {total}",
      boardCurrentStatus: "Current status",
      boardTimeline: "Health timeline",
      boardCurrentRun: "Current run",
      boardPreviousRun: "Previous run",
      boardUnknownRun: "Unidentified run",
      boardSinceYouLeft: "Since you left",
      health: {
        "on-track": "On track",
        grinding: "Working",
        stuck: "Stuck",
        "waiting-on-user": "Waiting on you",
        "wrapping-up": "Wrapping up",
        done: "Done",
        failed: "Failed",
      },
    },
    detailPanel: {
      close: "Close sidebar",
      copyPath: "Copy path",
      discard: "Discard",
      editFile: "Edit file",
      searchInFile: "Search in file",
      showInFiles: "Show in Files",
      previousMatch: "Previous match",
      nextMatch: "Next match",
      overwrite: "Overwrite",
      viewRawText: "View Raw Text",
      renderedMarkdown: "Rendered Markdown",
      renderedMarkdownHint: "Sanitized rich-text preview for quick reading.",
      noPreviewableMarkdown: "No previewable markdown content.",
      noContent: "No content available",
      fullContentOversized:
        "Full content is unavailable because the stored transcript entry is too large to return safely.",
      fullContentNotVisible:
        "Full content is unavailable because this transcript entry does not have a visible WebChat projection.",
      fullContentUnavailable: "Full content is no longer available for this transcript entry.",
      copyContents: "Copy file contents",
      fileChanged: "File changed on disk since it was loaded.",
      renderPreview: "Render preview",
      imagePreview: "Image preview",
      file: "File",
      markdownPreview: "Markdown preview",
      toolDetails: "Tool details",
      reloadFailed: "Failed to reload the latest file.",
      overwriteLoadFailed: "Failed to load the latest file before overwriting.",
      fullContentLoadFailed: "Failed to load full content: {error}",
    },
    sidebarColumns: {
      chat: "Chat",
      discussion: "Discussion",
      detail: "Details",
      close: "Close {panel}",
      drag: "Drag {panel}",
      dropOnEmptyLeft: "Move {panel} to the empty left sidebar",
      dropOnEmptyRight: "Move {panel} to the empty right sidebar",
      resize: "Resize {panel}",
    },
    thread: {
      search: "Search messages",
      searchPlaceholder: "Search messages...",
      closeSearch: "Close search",
      unpin: "Unpin",
      loading: "Loading chat",
      noMatches: "No matching messages",
      pinnedCount: "{count} pinned",
    },
    pairingQrExpired: {
      title: "Pairing QR expired",
      reason: "Run /pair qr again to generate a fresh setup code.",
      badge: "Expired",
    },
    composer: {
      placeholder: "Message {name}",
      placeholderWithAttachments: "Add a message or paste more images...",
      offlineHint: "Offline — messages will be queued and sent when the connection returns.",
      offlineQueuedHint: "Offline — {count} queued; messages send when the connection returns.",
      preparingModel: "Preparing model...",
      responding: "{name} is responding...",
      sendingMessage: "Sending message...",
      working: "{name} is working...",
      runDone: "Done",
      runInterrupted: "Interrupted",
      runStatus: "Run status: {status}",
      compactingContext: "Compacting context...",
      compacting: "Compacting",
      compact: "Compact",
      contextCompacted: "Context compacted",
      fallbackActive: "Fallback active: {model}",
      fallbackCleared: "Fallback cleared: {model}",
      fallbackSelected: "Selected: {model}",
      fallbackCurrent: "Active: {model}",
      fallbackPrevious: "Previous fallback: {model}",
      fallbackReason: "Reason: {reason}",
      fallbackAttempts: "Attempts: {attempts}",
      cancelReply: "Cancel reply",
      attachmentPreview: "Attachment preview",
      compactRecommendedContext: "Compact recommended session context",
      removeAttachment: "Remove attachment",
      addAttachment: "Add attachment",
      attachPhoto: "Photo",
      attachFile: "Attach file",
      attachFileOption: "File",
      menu: {
        back: "Back",
        skills: "Skills",
        connectors: "Connectors",
        webSearch: "Web search",
        managePlugins: "Manage plugins",
        manageSkills: "Manage skills",
        browseConnectors: "Browse connectors",
        addMcpServer: "Add MCP server…",
        addMcpServerTitle: "Add MCP server",
        addMcpServerDescription: "Configure the server and choose where it is enabled.",
        scopeLabel: "Availability",
        scopeSession: "This session",
        scopeEverywhere: "Everywhere",
        scopeSessionHint:
          "The server is saved globally disabled and enabled only for this session.",
        scopeEverywhereHint: "The server is saved and enabled for every session.",
        toolAccess: {
          label: "Tool access",
          loading: "Loading tools…",
          loadFailed: "Couldn’t load tools.",
          noTools: "No tools available for this connector.",
          summary: "{enabled} of {total} tools on",
          summaryOne: "{enabled} of {total} tool on",
        },
        enabledCount: "{count} on",
        loadingSkills: "Loading skills…",
        skillsLoadFailed: "Couldn’t load skills.",
        noSkills: "No skills available.",
        noConnectors: "No MCP servers configured.",
        depsMissing: "deps missing",
        skillBlocked: "not available for this agent",
        sessionTag: "session",
        offlineBlocked: "Connect to the gateway to change session capabilities.",
        adminBlocked: "Admin access is required to manage connectors.",
        savingBlocked: "Wait for the current session capability change to finish.",
      },
      overrides: {
        count: "{count} session overrides",
        countOne: "{count} session override",
        clear: "Clear session overrides",
      },
      contextUsage: {
        title: "Context usage details",
        open: "Open context usage details",
        summary: "Session context usage: {used} of {limit} ({pct}%)",
        contextWindow: "Context window",
        latestRunTokens: "Latest run tokens",
        estimatedCost: "Est. cost",
        planUsage: "Plan usage",
        openUsage: "Open usage dashboard",
        resets: "Resets {time}",
        usageCredits: "Usage credits",
        budgetValue: "{used} of {limit}",
        limitFiveHour: "5-hour limit",
        limitWeekly: "Weekly · all models",
        limitDaily: "Daily limit",
        limitHours: "{hours}-hour limit",
      },
      takePhoto: "Take photo",
      cameraAccessFailed: "Unable to access the camera.",
      cameraBusy: "The camera is busy or unavailable to the browser.",
      cameraFallback: "Camera {number}",
      cameraInput: "Camera",
      cameraListUnsupported: "This browser cannot list cameras.",
      cameraNoneFound: "No camera was found.",
      cameraPageInactive: "Cameras are unavailable while this page is inactive.",
      cameraPermissionBlocked:
        "Camera access is blocked. Allow camera access in browser site settings.",
      cameraPreview: "Camera preview",
      dismissVoiceInputError: "Dismiss voice input error",
      microphoneAccessFailed: "Unable to access microphone inputs.",
      microphoneBusy: "Microphone inputs are busy or unavailable to the browser.",
      microphoneFallback: "Microphone {number}",
      microphoneInput: "Microphone input",
      microphoneAppliesNextSession: "Changes apply when you start your next Talk session.",
      microphoneListUnsupported: "This browser cannot list microphone inputs.",
      noCameras: "No additional cameras found",
      noMicrophones: "No additional microphones found",
      microphoneNoneFound: "No microphone inputs were found.",
      microphonePageInactive: "Microphone inputs are unavailable while this page is inactive.",
      microphonePermissionBlocked:
        "Microphone access is blocked. Allow it in browser site settings to list inputs.",
      holdToRecordSetting: "Hold microphone button to dictate",
      holdToRecordSettingDescription:
        "Hold the composer microphone button, speak, then release to insert text without sending.",
      dictationAudioUnsupported: "The Gateway returned an unsupported dictation audio format.",
      dictationBrowserAudioUnsupported: "This browser cannot capture dictation audio at 8 kHz.",
      dictationConnecting: "Starting dictation…",
      dictationDisconnected: "Dictation stopped because the Gateway disconnected.",
      dictationFailed: "Dictation failed.",
      dictationFinalizing: "Finishing dictation…",
      dictationFinalizationTimedOut:
        "Dictation stopped before the last partial transcript could be finalized.",
      dictationProviderUnavailable: "No transcription provider is configured for dictation.",
      dictationRecording: "Recording {elapsed}",
      dictationReleaseToInsert: "Release to insert dictation",
      realtimeTalkRequiresMicrophone: "Realtime voice input requires browser microphone access.",
      selectedMicrophoneUnavailable:
        "The selected microphone is unavailable. Choose another input or System default.",
      selectedCameraUnavailable:
        "The selected camera is unavailable. Choose another camera or System default.",
      startVoiceInput: "Start voice input",
      stillListening: "Still listening",
      stopVoiceInput: "Stop voice input",
      systemDefaultMicrophone: "System default",
      systemDefaultCamera: "System default",
      switchCamera: "Switch camera",
      turnCameraOff: "Turn camera off",
      turnCameraOn: "Turn camera on",
      voiceTranscript: "Voice transcript",
    },
    attachments: {
      attachedFile: "Attached file",
      outsideAllowedFolders: "Outside allowed folders",
      unavailable: "Unavailable",
      checking: "Checking...",
    },
    voice: {
      asking: "Asking OpenClaw...",
      connecting: "Connecting voice input...",
      listening: "Listening...",
    },
    selectors: {
      loadMoreSessions: "Show more",
      model: "Chat model",
      modelSection: "Model",
      modelLocked: "Locked",
      modelLockedLabel: "Model selection controlled for this session",
      lockedSessionModel: "Session model",
      nativeCodexModel: "Codex-controlled model",
      thinkingLevel: "Chat thinking level",
    },
    toolCards: {
      tool: "Tool",
      canvas: "Canvas",
      summary: "Summary",
      toolInput: "Tool input",
      toolOutput: "Tool output",
      toolError: "Tool error",
      rawDetails: "Raw details",
      openFile: "Open file",
      openDetails: "Open tool details in side panel",
      widgetActions: "Widget actions",
      copyToClipboard: "Copy to clipboard",
      downloadFile: "Download file",
      widgetExportRerender: "This widget needs to be re-rendered to export as an image.",
      widgetExportFailed: "Widget export failed. Try again.",
      pinToDashboard: "Pin to dashboard",
      pinToDashboardPending: "Pinning…",
      pinnedToDashboard: "Pinned",
      fileChanges: "File changes",
      attemptedChanges: "Attempted changes",
      failed: "failed",
      running: "Running",
      noOutputFailed: "No output — tool failed.",
      noOutputSucceeded: "No output — tool completed successfully.",
      noResult: "No result available.",
      verbs: {
        read: "Read",
        edit: "Edit",
        editing: "Editing",
        edited: "Edited",
        write: "Write",
        writing: "Writing",
        wrote: "Wrote",
        searched: "Searched",
        fetched: "Fetched",
      },
      group: {
        commandsOne: "ran a command",
        commandsMany: "ran {count} commands",
        readsOne: "read a file",
        readsMany: "read {count} files",
        editsOne: "edited a file",
        editsMany: "edited {count} files",
        writesOne: "created a file",
        writesMany: "created {count} files",
        searchesOne: "ran a search",
        searchesMany: "ran {count} searches",
        fetchesOne: "fetched a page",
        fetchesMany: "fetched {count} pages",
        namedTool: "used {names}",
        namedToolRepeated: "used {names} ×{count}",
        otherOne: "used a tool",
        otherMany: "used {count} tools",
        emptyOne: "Ran a tool call",
        emptyMany: "Ran {count} tool calls",
        failedOne: "{count} failed",
        failedMany: "{count} failed",
        activityErrorOne: "Activity: {count} tool, includes errors.",
        activityErrorMany: "Activity: {count} tools, includes errors.",
      },
    },
    workRun: {
      workedFor: "Worked for {duration}",
      worked: "Worked",
      workedForError: "Worked for {duration}, includes errors.",
      workedError: "Worked, includes errors.",
    },
    backgroundTasks: {
      label: "Background tasks",
      title: "Background tasks",
      show: "Show background tasks",
      collapse: "Collapse background tasks",
      refresh: "Refresh background tasks",
      loading: "Loading background tasks…",
      empty: "No background tasks for this agent yet.",
      running: "Running ({count})",
      finished: "Finished ({count})",
      statusRunningOne: "1 running task",
      statusRunningMany: "{count} running tasks",
      statusPreviewMore: "+{count} more",
      stopTask: "Stop {title}",
      viewTranscript: "View transcript",
      toolUseOne: "1 tool use",
      toolUseMany: "{count} tool uses",
      expandTask: "Show details for {title}",
      detailTitle: "Task details",
      transcriptTitle: "Task transcript",
      backToTasks: "Back to background tasks",
      backToDetail: "Back to task details",
      detailLoading: "Loading task details…",
      detailFailed: "Could not load task details.",
      transcriptLoading: "Loading task transcript…",
      transcriptEmpty: "No transcript messages yet.",
      transcriptFailed: "Could not load task transcript.",
      prompt: "Prompt",
      output: "Output",
      promptUnavailable: "Prompt unavailable.",
      outputPending: "No output yet.",
    },
    sessionDiff: {
      title: "Changes",
      show: "Show session changes",
      refresh: "Refresh changes",
      loading: "Loading changes…",
      empty: "No changes in this session's checkout.",
      notGit: "This session's workspace is not a git checkout.",
      unknownSession: "No workspace is associated with this session.",
      disconnected: "Gateway is disconnected.",
      unmodifiedLines: "{count} unmodified lines",
      binaryFile: "Binary file",
      untracked: "untracked",
      tooLarge: "Diff too large to display.",
      truncatedFile: "Diff truncated.",
      truncatedResult: "Some changes were omitted because the diff is very large.",
      statusAdded: "Added",
      statusDeleted: "Deleted",
      statusRenamed: "Renamed",
      statusModified: "Modified",
    },
    sessionDiscussion: {
      show: "Show discussion",
      hide: "Hide discussion",
      disconnected: "Gateway is disconnected.",
      loading: "Loading discussion…",
      opening: "Opening discussion…",
      requiresWriteAccess: "Operator write access is required to open this discussion.",
      openExternal: "Open discussion in a new tab",
      frameTitle: "Session discussion",
      unavailable: "This discussion cannot be embedded.",
    },
    workspaceFiles: {
      label: "Session workspace",
      collapse: "Collapse session workspace",
      showFiles: "Show session files",
      dockRight: "Dock to the right",
      dockBottom: "Dock to the bottom",
      dragToDock: "Drag to dock right or bottom",
      workspace: "Session",
      files: "Workspace",
      refresh: "Refresh session workspace",
      loading: "Loading session workspace…",
      empty: "No files touched in this session yet",
      changed: "Changed",
      read: "Read",
      artifacts: "Artifacts",
      browser: "Project files",
      root: "Root",
      search: "Search files",
      searchResults: "Search results",
      parentFolder: "Parent folder",
      noBrowserFiles: "No files in this folder.",
      noSearchResults: "No matching files.",
      truncated: "Showing the first matching files. Refine the search to narrow results.",
      session: "Session",
      missing: "Missing",
      summary: "Session workspace summary",
      changedCount: "{count} changed",
      readCount: "{count} read",
      artifactCount: "{count} artifacts",
      browserCount: "{count} shown",
      actions: "Workspace file actions",
      preview: "Preview",
      copyPath: "Copy path",
    },
  },
  languages: {
    en: "English",
    zhCN: "简体中文 (Simplified Chinese)",
    zhTW: "繁體中文 (Traditional Chinese)",
    ptBR: "Português (Brazilian Portuguese)",
    de: "Deutsch (German)",
    es: "Español (Spanish)",
    jaJP: "日本語 (Japanese)",
    ko: "한국어 (Korean)",
    fr: "Français (French)",
    hi: "हिन्दी (Hindi)",
    ar: "العربية (Arabic)",
    it: "Italiano (Italian)",
    tr: "Türkçe (Turkish)",
    uk: "Українська (Ukrainian)",
    id: "Bahasa Indonesia (Indonesian)",
    pl: "Polski (Polish)",
    th: "ไทย (Thai)",
    vi: "Tiếng Việt (Vietnamese)",
    nl: "Nederlands (Dutch)",
    fa: "فارسی (Persian)",
    ru: "Русский (Russian)",
  },
  cron: {
    adminRequired: "Browsing only. Automation changes require operator.admin access.",
    tabs: {
      filterLabel: "Automation status",
      all: "All",
      active: "Active",
      paused: "Paused",
    },
    list: {
      viewLabel: "Automation views",
      searchPlaceholder: "Search automations",
      newTask: "New automation",
      filters: "Filters",
      shownOf: "{shown} of {total}",
      emptyTitle: "No automations yet",
      emptyHint: "Describe what OpenClaw should do and when — it runs on schedule.",
      noMatching: "No automations match the current filters.",
      loadMore: "Load more",
      loading: "Loading...",
      schedulerOff: "Scheduler disabled",
      refresh: "Refresh",
      refreshing: "Refreshing...",
      paused: "Paused",
      tasksTab: "Automations",
      activityTab: "Run history",
    },
    suggestions: {
      title: "Starter automations",
      schedules: {
        weekdayMornings: "Weekdays at 9:00 AM",
        everyMorning: "Daily at 8:00 AM",
        weekly: "Mondays at 9:00 AM",
        hourly: "Every hour",
      },
      ideas: {
        repoPulse: {
          name: "Repo pulse",
          tagline: "Overnight issues, PRs, and CI failures, ranked by urgency.",
          prompt:
            "Review overnight activity in my repositories: new issues, pull requests, and CI failures. Summarize the three things that most need my attention today, each with a link and a one-line reason.",
        },
        standupGhostwriter: {
          name: "Standup ghostwriter",
          tagline: "Your standup update, drafted from yesterday's work.",
          prompt:
            "Draft my standup update from yesterday's commits, merged pull requests, and open review threads. Three bullets max: done, doing, blocked.",
        },
        hackerNewsScout: {
          name: "Hacker News scout",
          tagline: "Three links worth your coffee, with hot takes.",
          prompt:
            "Scan today's Hacker News front page for posts about AI agents, developer tooling, and TypeScript. Send me the three most interesting links, each with a one-line hot take.",
        },
        dependencyRadar: {
          name: "Dependency radar",
          tagline: "Outdated or vulnerable dependencies, with upgrade notes.",
          prompt:
            "Check my main project for outdated or vulnerable dependencies. List the notable updates with a one-line risk note each, and draft the upgrade command.",
        },
        watchdog: {
          name: "Night watch",
          tagline: "Hourly health check with a one-line verdict.",
          prompt:
            "Check that my services and gateway are healthy: scan recent logs for new errors, restarts, or unusual load. Reply with a single short all-clear line when everything is fine; if something looks broken, report what failed and where to start looking.",
        },
        polyglotMinute: {
          name: "Polyglot minute",
          tagline: "One useful foreign phrase with your morning coffee.",
          prompt:
            "Teach me one useful phrase in Japanese: the phrase, how to pronounce it, its literal meaning, and when to use it. Keep it under five lines.",
        },
      },
    },
    stats: {
      tasks: "Automations",
      failing: "Failing",
      nextWake: "Next wake",
    },
    detail: {
      tabsLabel: "Automation details",
      newTitle: "New automation",
      newSubtitle: "Describe what OpenClaw should do, then pick when it runs.",
      back: "All automations",
      settingsTab: "Settings",
      historyTitle: "Run history",
      generalSection: "General",
      scheduleSection: "Schedule",
      deliverySection: "Delivery",
      active: "Active",
      paused: "Paused",
    },
    actions: {
      runNow: "Run now",
      runIfDue: "Run if due",
      pause: "Pause",
      resume: "Resume",
      clone: "Clone",
      remove: "Remove",
      more: "More actions",
    },
    runNotStarted: {
      notDue: "This automation is not due yet.",
      alreadyRunning: "This automation is already running.",
      recoveryPending: "Scheduler recovery is still in progress.",
      invalidSpec: "This automation has an invalid schedule or payload.",
      stopped: "The scheduler is stopped.",
      unknown: "This automation could not be started.",
    },
    jobs: {
      schedule: "Schedule",
      lastRun: "Last run",
      all: "All",
      sort: "Sort",
      nextRun: "Next run",
      recentlyUpdated: "Recently updated",
      name: "Name",
      direction: "Direction",
      ascending: "Ascending",
      descending: "Descending",
      reset: "Reset",
    },
    runs: {
      searchRuns: "Search runs",
      searchPlaceholder: "Summary, error, or task",
      newestFirst: "Newest first",
      oldestFirst: "Oldest first",
      status: "Status",
      delivery: "Delivery",
      clear: "Clear",
      allStatuses: "All statuses",
      allDelivery: "All delivery",
      noMatching: "No matching runs.",
      emptyTitle: "No runs yet",
      emptyHint: "Runs show up here once an automation fires.",
      loadMore: "Load more runs",
      runStatusOk: "OK",
      runStatusError: "Error",
      runStatusSkipped: "Skipped",
      runStatusUnknown: "Unknown",
      deliveryDelivered: "Delivered",
      deliveryNotDelivered: "Not delivered",
      deliveryUnknown: "Unknown",
      deliveryNotRequested: "Not requested",
    },
    form: {
      required: "Required",
      requiredSr: "required",
      fieldName: "Name",
      description: "Description",
      agentId: "Agent",
      namePlaceholder: "Morning brief",
      descriptionPlaceholder: "Optional context for this task",
      agentPlaceholder: "main or ops",
      agentHelp: "Start typing to pick a known agent, or enter a custom one.",
      promptPlaceholder: "Describe what OpenClaw should do...",
      repeat: "Repeat",
      repeatInterval: "Interval",
      repeatOnce: "Once",
      repeatOnExit: "On exit",
      repeatStream: "Stream source",
      onExitHelp: "Runs when a watched command exits. The schedule cannot be edited here.",
      every: "Every",
      at: "At",
      cronOption: "Cron",
      runAt: "Run at",
      unit: "Unit",
      minutes: "Minutes",
      hours: "Hours",
      days: "Days",
      expression: "Expression",
      expressionPlaceholder: "0 7 * * *",
      everyAmountPlaceholder: "30",
      summaryEverySecondOne: "Runs every second",
      summaryEveryMinuteOne: "Runs every minute",
      summaryEveryHourOne: "Runs every hour",
      summaryEveryDayOne: "Runs every day",
      summaryEverySeconds: "Runs every {amount} seconds",
      summaryEveryMinutes: "Runs every {amount} minutes",
      summaryEveryHours: "Runs every {amount} hours",
      summaryEveryDays: "Runs every {amount} days",
      summaryOnce: "Runs once at {at}",
      summaryCron: "Cron schedule {expr}",
      summaryCronTz: "Cron schedule {expr} ({tz})",
      timezoneOptional: "Timezone",
      timezonePlaceholder: "America/Los_Angeles",
      timezoneHelp:
        "Optional. Leave blank to use the Gateway host timezone, or enter any valid IANA timezone.",
      runsIn: "Runs in",
      mainSession: "Main session",
      isolatedSession: "Isolated session",
      sessionHelp: "Main posts into the agent's timeline. Isolated runs a dedicated agent turn.",
      wakeMode: "Wake mode",
      now: "Now",
      nextHeartbeat: "Next heartbeat",
      wakeModeHelp: "Now triggers immediately. Next heartbeat waits for the next cycle.",
      action: "Action",
      command: "Command",
      script: "Script",
      readOnlyPayloadHelp:
        "This payload was created outside Control UI. Its contents stay read-only and are preserved when you save other changes.",
      systemEvent: "Post to main timeline",
      agentTurn: "Run agent task",
      systemEventHelp: "Sends your text to the gateway main timeline (good for reminders).",
      agentTurnHelp: "Starts an agent run in its own session using your prompt.",
      timeoutSeconds: "Timeout (seconds)",
      timeoutPlaceholder: "Optional, e.g. 90",
      timeoutHelp: "Optional. Leave blank to use the gateway default timeout for this run.",
      mainTimelineMessage: "Main timeline message",
      assistantTaskPrompt: "Prompt",
      deliveryModeLabel: "Mode",
      announceDefault: "Announce summary",
      webhookPost: "Webhook POST",
      noneInternal: "None (internal)",
      deliveryHelp: "Announce posts a summary to chat. None keeps execution internal.",
      webhookUrl: "Webhook URL",
      channel: "Channel",
      webhookPlaceholder: "https://example.com/cron",
      channelHelp: "Choose which connected channel receives the summary.",
      webhookHelp: "Send run summaries to a webhook endpoint.",
      to: "To",
      toPlaceholder: "+1555... or chat id",
      toHelp: "Optional recipient override (chat id, phone, or user id).",
      advanced: "Advanced",
      advancedHelp:
        "Optional overrides for delivery guarantees, schedule jitter, and model controls.",
      startEnabled: "Start enabled",
      deleteAfterRun: "Delete after run",
      deleteAfterRunHelp: "Best for one-shot reminders that should auto-clean up.",
      clearAgentOverride: "Clear agent override",
      clearAgentHelp: "Force this task to use the gateway default agent.",
      sessionKey: "Session key",
      sessionKeyHelp: "Optional routing key for job delivery and wake routing.",
      exactTiming: "Exact timing (no stagger)",
      exactTimingHelp: "Run on exact cron boundaries with no spread.",
      staggerWindow: "Stagger window",
      staggerUnit: "Stagger unit",
      staggerPlaceholder: "30",
      seconds: "Seconds",
      accountId: "Account ID",
      accountIdHelp: "Optional channel account ID for multi-account setups.",
      lightContext: "Light context",
      lightContextHelp: "Use lightweight bootstrap context for this agent job.",
      model: "Model",
      modelPlaceholder: "openai/gpt-5.2",
      modelHelp: "Start typing to pick a known model, or enter a custom one.",
      thinking: "Reasoning",
      thinkingPlaceholder: "low",
      thinkingHelp: "Use a suggested level or enter a provider-specific value.",
      failureAlerts: "Failure alerts",
      failureAlertInherit: "Inherit global setting",
      failureAlertDisabled: "Disable for this job",
      failureAlertCustom: "Custom per-job settings",
      failureAlertsHelp: "Control when this job sends repeated-failure alerts.",
      failureAlertAfter: "Alert after",
      failureAlertAfterHelp: "Consecutive errors before alerting.",
      failureAlertCooldown: "Cooldown (seconds)",
      failureAlertCooldownHelp: "Minimum seconds between alerts.",
      failureAlertChannel: "Alert channel",
      failureAlertTo: "Alert to",
      failureAlertToPlaceholder: "+1555... or chat id",
      failureAlertToHelp: "Optional recipient override for failure alerts.",
      failureAlertMode: "Alert mode",
      failureAlertAnnounce: "Announce (via channel)",
      failureAlertWebhook: "Webhook (HTTP POST)",
      failureAlertAccountId: "Alert account ID",
      failureAlertAccountPlaceholder: "Account ID for multi-account setups",
      bestEffortDelivery: "Best effort delivery",
      bestEffortHelp: "Do not fail the task if delivery itself fails.",
      cantAddYet: "Can't save yet",
      fillRequired: "Fill the required fields below to enable submit.",
      fixFields: "Fix {count} field to continue.",
      fixFieldsPlural: "Fix {count} fields to continue.",
      saving: "Saving...",
      saveChanges: "Save changes",
      createTask: "Create automation",
      createAndRun: "Create & run now",
      cancel: "Cancel",
    },
    jobState: {
      next: "Next",
    },
    runEntry: {
      noSummary: "No summary.",
      runAt: "Run at",
      openRunChat: "Open run chat",
      next: "Next {rel}",
      due: "Due {rel}",
    },
    errors: {
      nameRequired: "Name is required.",
      scheduleAtInvalid: "Enter a valid date/time.",
      everyAmountInvalid: "Interval must be greater than 0.",
      cronExprRequired: "Cron expression is required.",
      staggerAmountInvalid: "Stagger must be greater than 0.",
      systemTextRequired: "System text is required.",
      agentMessageRequired: "Agent message is required.",
      timeoutInvalid: "If set, timeout must be greater than 0 seconds.",
      webhookUrlRequired: "Webhook URL is required.",
      webhookUrlInvalid: "Webhook URL must start with http:// or https://.",
      invalidRunTime: "Invalid run time.",
      invalidIntervalAmount: "Invalid interval amount.",
      cronExprRequiredShort: "Cron expression required.",
      invalidStaggerAmount: "Invalid stagger amount.",
      systemEventTextRequired: "System event text required.",
      agentMessageRequiredShort: "Agent message required.",
      nameRequiredShort: "Name required.",
    },
  },
};
